/**
 * OracleHome — PSI (Precision Secured Intelligence). Formerly POM.
 * Full-screen compliance command center. No sidebar — PSI IS the interface.
 * Internal slug remains "overmind" for audit-trail continuity; user-facing
 * label is PSI everywhere.
 * Theme toggle (light default / dark). App launcher modal for navigation.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import AgentDashboard from './AgentDashboard';
import HubCalendars from './components/HubCalendars';
import { canSeeApp as sharedCanSeeApp } from '../../lib/canSeeApp';
import useAgentProgress from '../../lib/useAgentProgress';
import AgentThinkingPanel from '../../components/AgentThinkingPanel';
import PhiFriendlyNote from '../../components/PhiFriendlyNote.jsx';
import ReclassifyChip from '../../components/ReclassifyChip';
import AgentCard from '../Agent/AgentCard.jsx';
import { useAgentProposals, objectKeyOfItem, ProposalStrip, AlsoNoticed } from '../Agent/Proposals.jsx';
import {
  InboxIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentListIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  FlagIcon,
  MagnifyingGlassIcon,
  GlobeAltIcon,
  BookOpenIcon,
  PaperAirplaneIcon,
  BuildingOffice2Icon,
  ScaleIcon,
  ChartBarSquareIcon,
  ArrowsRightLeftIcon,
  ShieldExclamationIcon,
  CpuChipIcon,
  PresentationChartBarIcon,
} from '@heroicons/react/24/outline';

const API = process.env.REACT_APP_API_URL;

// ---------------------------------------------------------------------------
// Theme helper: t(light, dark) returns the right class based on current theme
// ---------------------------------------------------------------------------
function useTheme() {
  const [theme, setTheme] = useState(() => localStorage.getItem('pom-theme') || 'light');
  const toggle = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('pom-theme', next);
  };
  const t = (light, dark) => theme === 'dark' ? dark : light;
  return { theme, toggle, t };
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const URGENCY_CONFIG = {
  critical: { label: 'Critical', dot: 'bg-red-500' },
  high:     { label: 'High',     dot: 'bg-amber-400' },
  normal:   { label: 'Normal',   dot: 'bg-blue-400' },
  low:      { label: 'Low',      dot: 'bg-slate-400' },
};

const APP_LABELS = {
  network_adequacy: 'Network Adequacy',
  calendar:         'Compliance Hub',
  coa:              'COA Work Plan',
  cil:              'Compliance Issues',
  risk:             'Risk',
  privacy:          'Privacy',
  memo:             'Mandate',
  cap:              'CAP',
  ppm:              'Policies',
  sentinel:         'Sentinel',
  fdr:              'FDR',
  priq:             'RuleIQ',
  ai_command:       'Precision Responsible AI',
  materials_review: 'Materials Review',
  readiness: 'Readiness',
};

const APP_ICONS = {
  network_adequacy: '🌐', calendar: '🏛️', coa: '📋', cil: '📝',
  risk: '⚠️', privacy: '🛡️', memo: '📄', cap: '🎯',
  ppm: '📘', sentinel: '🛰️', fdr: '🏢', priq: '⚖️',
  ai_command: '🤖',
  materials_review: '📄',
  readiness: '✅',
};

// Shared per-app accent colors — the launcher tiles and the queue cards speak
// the same icon language (colored squircle, white glyph).
const APP_COLORS = {
  calendar: '#0ea5e9', risk: '#f59e0b', coa: '#8b5cf6', cil: '#f97316',
  privacy: '#6366f1', memo: '#10b981', cap: '#eab308', scrubber: '#14b8a6', scrubber_2029: '#0f766e',
  network_adequacy: '#06b6d4', ppm: '#818cf8', fdr: '#f43f5e',
  priq: '#f59e0b',
  workstream: '#06b6d4', ratify: '#6366f1',
  reporter: '#c8323f', universe_requests: '#c8323f',
  sentinel: '#dc2626', deck_studio: '#6366f1',
  ai_command: '#E5601F',
  materials_review: '#E5601F',
  readiness: '#E5601F',
  agent: '#E5601F',
};

// ── Live chart preview ──────────────────────────────────────────────────────
// Dependency-free SVG that mirrors the server's board-deck chart rules (same
// palette, same type picker, same colors/stack/combo/target), so the author sees
// what the PowerPoint will produce. Renders nothing to the network — pure client.
const PSI_CHART_PALETTE = ['F58220', '156082', '196B24', '0F9ED5', 'A02B93', '4EA72E'];
const PSI_TARGET_COLOR = '9AA7B2';
function psiSafeHex(c) { if (typeof c !== 'string') return null; const h = c.trim().replace(/^#/, '').toUpperCase(); return /^[0-9A-F]{6}$/.test(h) ? h : null; }
function psiColorAt(colors, i) { return psiSafeHex((colors || [])[i]) || PSI_CHART_PALETTE[i % PSI_CHART_PALETTE.length]; }
function psiNum(v) { const n = Number(v); return (v === '' || v == null || isNaN(n)) ? null : n; }

export function ChartPreview({ block, effType, dark }) {
  const W = 340, H = 190, padL = 10, padR = 10, padT = 10, padB = 26;
  const labels = (block.labels || []).map(String);
  const series = (block.series || [])
    .map(se => ({ name: (se.name || '').trim() || 'Series', values: (se.values || []).map(psiNum) }))
    .filter(se => se.values.some(v => v != null));
  const ink = dark ? '#cbd5e1' : '#334155';
  const sub = dark ? '#64748b' : '#94a3b8';
  const grid = dark ? '#ffffff14' : '#0f172a12';
  if (!labels.length || !series.length) {
    return <div className="flex items-center justify-center h-[190px] text-[11px]" style={{ color: sub }}>Enter categories and values to preview</div>;
  }
  const type = effType;
  const multi = series.length > 1;
  const plotW = W - padL - padR, plotH = H - padT - padB;

  // Pie / doughnut
  if (type === 'pie' || type === 'doughnut') {
    const vals = series[0].values.map(v => v == null ? 0 : Math.max(0, v));
    const total = vals.reduce((a, b) => a + b, 0) || 1;
    const cx = padL + plotW * 0.36, cy = padT + plotH / 2, r = Math.min(plotW * 0.36, plotH / 2) - 2;
    let ang = -Math.PI / 2;
    const arcs = vals.map((v, i) => {
      const frac = v / total, a2 = ang + frac * Math.PI * 2;
      const x1 = cx + r * Math.cos(ang), y1 = cy + r * Math.sin(ang), x2 = cx + r * Math.cos(a2), y2 = cy + r * Math.sin(a2);
      const large = frac > 0.5 ? 1 : 0;
      const d = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
      ang = a2;
      return <path key={i} d={d} fill={`#${psiColorAt(block.colors, i)}`} stroke={dark ? '#0d0f16' : '#fff'} strokeWidth="1" />;
    });
    return (
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Chart preview">
        {arcs}
        {type === 'doughnut' && <circle cx={cx} cy={cy} r={r * 0.6} fill={dark ? '#0d0f16' : '#fff'} />}
        {labels.map((l, i) => (
          <g key={i} transform={`translate(${padL + plotW * 0.72}, ${padT + 6 + i * 15})`}>
            <rect width="9" height="9" rx="2" fill={`#${psiColorAt(block.colors, i)}`} />
            <text x="13" y="8" fontSize="9" fill={ink}>{l.slice(0, 16) || `#${i + 1}`}</text>
          </g>
        ))}
      </svg>
    );
  }

  // Cartesian (bar/line/area/combo). Compute scale.
  const stack = block.stack === 'stacked' || block.stack === 'percent' ? block.stack : 'none';
  const percent = stack === 'percent';
  const combo = block.combo === true && multi;
  const lineIdx = new Set((Array.isArray(block.lineSeries) && block.lineSeries.length ? block.lineSeries : [series.length - 1]).filter(i => i >= 0 && i < series.length));
  const target = psiNum(block.targetLine);

  let maxV;
  if (percent) maxV = 100;
  else if (stack === 'stacked') maxV = Math.max(...labels.map((_, ci) => series.reduce((a, se) => a + (se.values[ci] || 0), 0)), target ?? 0, 1);
  else maxV = Math.max(...series.flatMap(se => se.values.map(v => v ?? 0)), target ?? 0, 1);
  const yOf = v => padT + plotH - (v / maxV) * plotH;
  const n = labels.length, band = plotW / n;

  const nodes = [];
  // Bars
  const barSeries = combo ? series.map((se, i) => ({ se, i })).filter(o => !lineIdx.has(o.i)) : series.map((se, i) => ({ se, i }));
  const lineSeriesArr = combo ? series.map((se, i) => ({ se, i })).filter(o => lineIdx.has(o.i)) : [];
  if (type === 'bar' || combo) {
    const stacking = stack !== 'none';
    const groupN = stacking ? 1 : barSeries.length;
    const bw = (band * 0.62) / Math.max(1, groupN);
    labels.forEach((_, ci) => {
      let acc = 0;
      const colTotal = percent ? series.reduce((a, se) => a + (se.values[ci] || 0), 0) || 1 : 1;
      barSeries.forEach((o, bi) => {
        const raw = o.se.values[ci] || 0;
        const v = percent ? (raw / colTotal) * 100 : raw;
        if (stacking) {
          const y1 = yOf(acc), y2 = yOf(acc + v);
          nodes.push(<rect key={`b${ci}-${bi}`} x={padL + ci * band + band * 0.19} y={y2} width={band * 0.62} height={Math.max(0, y1 - y2)} fill={`#${psiColorAt(block.colors, o.i)}`} rx="1.5" />);
          acc += v;
        } else {
          const x = padL + ci * band + band * 0.19 + bi * bw;
          const y = yOf(v);
          nodes.push(<rect key={`b${ci}-${bi}`} x={x} y={y} width={Math.max(1, bw - 1.5)} height={Math.max(0, padT + plotH - y)} fill={`#${psiColorAt(block.colors, o.i)}`} rx="1.5" />);
        }
      });
    });
  }
  // Lines / area
  const drawLine = (o, area) => {
    const pts = o.se.values.map((v, ci) => v == null ? null : [padL + ci * band + band / 2, yOf(v)]).filter(Boolean);
    if (pts.length < 1) return null;
    const dl = pts.map((p, i) => `${i ? 'L' : 'M'} ${p[0]} ${p[1]}`).join(' ');
    const col = `#${psiColorAt(block.colors, o.i)}`;
    return (
      <g key={`l${o.i}`}>
        {area && <path d={`${dl} L ${pts[pts.length - 1][0]} ${padT + plotH} L ${pts[0][0]} ${padT + plotH} Z`} fill={col} opacity="0.16" />}
        <path d={dl} fill="none" stroke={col} strokeWidth="2" strokeLinejoin="round" />
        {pts.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r="2.5" fill={col} />)}
      </g>
    );
  };
  if (type === 'line') series.forEach((se, i) => nodes.push(drawLine({ se, i }, false)));
  else if (type === 'area') series.forEach((se, i) => nodes.push(drawLine({ se, i }, true)));
  else if (combo) lineSeriesArr.forEach(o => nodes.push(drawLine(o, false)));

  // Target line
  const targetNode = (target != null && !percent) ? (
    <g>
      <line x1={padL} y1={yOf(target)} x2={padL + plotW} y2={yOf(target)} stroke={`#${PSI_TARGET_COLOR}`} strokeWidth="1.5" strokeDasharray="4 3" />
      <text x={padL + plotW} y={yOf(target) - 3} fontSize="8" textAnchor="end" fill={sub}>Target {target}</text>
    </g>
  ) : null;

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Chart preview">
      {[0.25, 0.5, 0.75].map(f => <line key={f} x1={padL} y1={padT + plotH * f} x2={padL + plotW} y2={padT + plotH * f} stroke={grid} strokeWidth="1" />)}
      <line x1={padL} y1={padT + plotH} x2={padL + plotW} y2={padT + plotH} stroke={grid} strokeWidth="1" />
      {nodes}
      {targetNode}
      {labels.map((l, ci) => <text key={ci} x={padL + ci * band + band / 2} y={H - 12} fontSize="8" textAnchor="middle" fill={sub}>{String(l).slice(0, 8)}</text>)}
      {(multi || target != null) && block.legendPos !== 'none' && (
        <g>{series.map((se, i) => (
          <g key={i} transform={`translate(${padL + i * 82}, ${H - 4})`}>
            <rect width="8" height="8" rx="2" y="-7" fill={`#${psiColorAt(block.colors, i)}`} />
            <text x="11" y="0" fontSize="8" fill={ink}>{(se.name || `S${i + 1}`).slice(0, 10)}</text>
          </g>
        ))}</g>
      )}
    </svg>
  );
}

/** Launchpad-language app glyph: colored squircle with a white heroicon. */
function AppGlyph({ appKey, size = 40, className = '' }) {
  const color = APP_COLORS[appKey] || '#64748b';
  const AppIcon = LAUNCHER_APPS[appKey]?.Icon || InboxIcon;
  return (
    <span className={`inline-flex items-center justify-center rounded-[30%] flex-shrink-0 ${className}`}
      style={{ width: size, height: size, background: `linear-gradient(160deg, ${color}f0 0%, ${color} 60%, ${color}e6 100%)`, boxShadow: `0 8px 18px -8px ${color}90, inset 0 1px 0 rgba(255,255,255,0.3)` }}>
      <AppIcon className="text-white" style={{ width: size * 0.5, height: size * 0.5 }} strokeWidth={1.8} />
    </span>
  );
}

const APP_ACCENT_L = {
  network_adequacy: 'border-l-teal-500', calendar: 'border-l-blue-500',
  coa: 'border-l-violet-500', cil: 'border-l-orange-400',
  risk: 'border-l-red-500', privacy: 'border-l-orange-500',
  memo: 'border-l-emerald-500', cap: 'border-l-amber-500',
  ppm: 'border-l-indigo-500', sentinel: 'border-l-red-600',
  fdr: 'border-l-teal-600', priq: 'border-l-yellow-500',
};

const ARTIFACT_TYPE_LABELS = {
  memo: 'Regulatory Memo', audit_report: 'Audit Report',
  na_file: 'NA Provider File', universe: 'Universe File', protocol_file: 'Protocol File',
};

const ARTIFACT_TYPE_ICONS = {
  memo: '📄', audit_report: '🔍', na_file: '🌐', universe: '📊', protocol_file: '📋',
};

// App launcher — module-based structure. Icons mirror Core's AppSidebar
// (src/components/AppSidebar.jsx) so the launcher and the left rail show the
// same glyph for each app.
const LAUNCHER_APPS = {
  workstream:       { Icon: InboxIcon,                    label: 'Workstream',         desc: 'Cross-module AI follow-ups' },
  ratify:           { Icon: PaperAirplaneIcon,            label: 'SignoffHub',         desc: 'Approvals & ratification' },
  calendar:         { Icon: CalendarDaysIcon,             label: 'Compliance Hub',     desc: 'Obligations & calendar' },
  risk:             { Icon: ExclamationTriangleIcon,      label: 'Risk Assessment',    desc: 'Risk assessment & approval' },
  audit:            { Icon: ClipboardDocumentListIcon,    label: 'Audit',              desc: 'CMS and internal audits, end to end' },
  coa:              { Icon: ClipboardDocumentListIcon,    label: 'COA Work Plan',      desc: 'Audit monitoring & CAP' },
  cil:              { Icon: PencilSquareIcon,             label: 'Issues Log',         desc: 'Compliance issues tracking' },
  privacy:          { Icon: ShieldCheckIcon,              label: 'Privacy',            desc: 'Incident management' },
  memo:             { Icon: DocumentTextIcon,             label: 'Mandate',            desc: 'Regulatory memo analysis' },
  mandate_native:   { Icon: DocumentTextIcon,             label: 'Precision Mandate',  desc: 'Regulatory command center' },
  cap:              { Icon: FlagIcon,                     label: 'CAP',                desc: 'Corrective action plans' },
  scrubber:         { Icon: MagnifyingGlassIcon,          label: 'Scrubber',           desc: 'Universe validation, current and 2027 audit year protocols' },
  scrubber_2029:    { Icon: MagnifyingGlassIcon,          label: 'Scrubber',           desc: 'Universe validation, 2027 audit year protocols' },
  network_adequacy: { Icon: GlobeAltIcon,                 label: 'Network Adequacy',   desc: 'Provider network analysis' },
  materials_review: { Icon: DocumentTextIcon,             label: 'Materials Review',   desc: 'Marketing and communications materials' },
  readiness:        { Icon: ClipboardDocumentListIcon,    label: 'Readiness',          desc: 'CMS annual readiness checklist' },
  agent:            { Icon: CpuChipIcon,                  label: 'Agent',              desc: 'The Compliance Agent, what it watches' },
  ppm:              { Icon: BookOpenIcon,                 label: 'Policy Management',  desc: 'Enterprise policy system' },
  fdr:              { Icon: BuildingOffice2Icon,          label: 'FDR Oversight',      desc: 'Vendor & delegate oversight' },
  priq:             { Icon: ScaleIcon,                    label: 'RuleIQ',             desc: 'CMS Final Rule intelligence' },
  reporter:         { Icon: ChartBarSquareIcon,           label: 'Reporter',           desc: 'Part C/D Reporting validator' },
  universe_requests:{ Icon: ArrowsRightLeftIcon,          label: 'Universe Requests',  desc: 'Request universes from FDRs' },
  sentinel:         { Icon: ShieldExclamationIcon,        label: 'Sentinel',           desc: 'Fraud (FWA) investigations' },
  ai_command:       { Icon: CpuChipIcon,                  label: 'Precision Responsible AI', desc: 'Responsible AI governance' },
  deck_studio:      { Icon: PresentationChartBarIcon,     label: 'Deck Studio',        desc: 'Org-branded PowerPoint builder' },
};

// NOTE: launcher categories live INSIDE the component as `modules` (around
// line ~200) because the icons are JSX SVGs. The earlier LAUNCHER_MODULES
// constant was dead code that drifted out of sync with the real one — kept
// removed to prevent future edits from missing the change.

// Visibility gate for PSI's app launcher comes from src/lib/canSeeApp.js —
// shared with src/components/AppSidebar.jsx so the two launchers cannot
// drift. Local re-export keeps the call sites in this file untouched.
const canSeeApp = sharedCanSeeApp;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDueDate(date) {
  if (!date) return null;
  const d    = new Date(date);
  const now  = new Date();
  const diff = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
  if (diff < 0)   return { text: `Overdue by ${Math.abs(diff)}d`, color: 'text-red-600 dark:text-red-400 font-semibold' };
  if (diff === 0) return { text: 'Due today',                      color: 'text-red-600 dark:text-red-400 font-semibold' };
  if (diff <= 3)  return { text: `Due in ${diff}d`,                color: 'text-amber-600 dark:text-amber-400 font-semibold' };
  if (diff <= 7)  return { text: `Due in ${diff}d`,                color: 'text-amber-500 dark:text-amber-300' };
  if (diff <= 30) return { text: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), color: 'text-slate-500' };
  // Beyond a month there is nothing to do yet, so it reads as a date, quietly.
  return { text: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), color: 'text-slate-400 dark:text-slate-500' };
}

/**
 * Priority items historically stored machine payloads (raw JSON) in the
 * context column — engineering must never reach the user's eyes. If the
 * context parses as JSON, rebuild it as plain language from the fields we
 * recognize; if nothing readable survives, show nothing at all.
 */
function humanizeContext(raw) {
  const s = String(raw || '').trim();
  if (!s) return null;
  if (!s.startsWith('{') && !s.startsWith('[')) return s;
  try {
    const o = JSON.parse(s);
    if (o && typeof o === 'object' && !Array.isArray(o)) {
      const parts = [];
      if (o.cfr_section) parts.push(String(o.cfr_section));
      if (Array.isArray(o.departments) && o.departments.length) parts.push(o.departments.join(', '));
      return parts.length ? parts.join(' — ') : null;
    }
    return null;
  } catch { return null; }
}

function timeAgo(date) {
  if (!date) return 'never';
  const diffMs  = Date.now() - new Date(date).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1)  return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24)  return `${diffHr}h ago`;
  return `${Math.floor(diffHr / 24)}d ago`;
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024)        return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function Spinner({ size = 4, className = '' }) {
  return (
    <svg className={`w-${size} h-${size} animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// AppLauncher Modal — module-based layout
// ---------------------------------------------------------------------------
const RECENT_APPS_KEY = 'psi-recent-apps';
function readRecentApps() {
  try { const v = JSON.parse(localStorage.getItem(RECENT_APPS_KEY) || '[]'); return Array.isArray(v) ? v.filter(k => typeof k === 'string') : []; } catch { return []; }
}
function pushRecentApp(key) {
  try { localStorage.setItem(RECENT_APPS_KEY, JSON.stringify([key, ...readRecentApps().filter(k => k !== key)].slice(0, 6))); } catch { /* private mode */ }
}

// Exported so the department-user Workspace can render the exact same
// Launchpad (operator decision 2026-07-20: one launcher for everyone).
// `hint` (optional): a one-line "N things need you" notice under the search
// pill; clicking it closes the launcher and reveals the page beneath, where
// the work cards live. Absent by default — PSI passes nothing.
// Apps a conversation can be opened over from the launcher (the Regulation
// desk). Ticking one or more of these and pressing Ask opens the desk instead
// of the app; the server decides again which of them the person may read.
export function AppLauncher({ open, onClose, onSelectApp, user, t, hint = null }) {
  const searchRef = useRef(null);
  const [query, setQuery] = useState('');
  const [selIdx, setSelIdx] = useState(0);
  const [recents, setRecents] = useState(readRecentApps);
  // Ask mode: tiles become tick boxes for the apps a conversation can read.
  // The server says which apps this person may ask across and which tile
  // each is ticked from (the Medicaid contract rides on the Compliance Hub
  // tile it lives in); nothing here is enabled unless the server lists it.
  const [askMode, setAskMode] = useState(false);
  const [asked, setAsked] = useState([]);
  const [askApps, setAskApps] = useState([]);
  const askEnabled = askApps.length > 0;
  const askableTiles = [...new Set(askApps.map((a) => a.tile))];
  useEffect(() => {
    if (!open || typeof fetch === 'undefined') return;
    let dead = false;
    fetch(`${API}/api/lane/apps`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : null)).then((d) => { if (!dead) setAskApps(Array.isArray(d?.apps) ? d.apps : []); }).catch(() => {});
    return () => { dead = true; };
  }, [open]);
  // Agentic mode (operator order 2026-10-23): the switch reads Open and
  // Agent. The server says whether the Compliance Agent is on for this
  // person (container flag, organization switch, audience). With it on, the
  // Agent view shows the agent's panel; the question bar keeps the
  // Regulation desk behavior wherever the desk is enabled.
  const [agentStatus, setAgentStatus] = useState(null);
  const agentEnabled = agentStatus?.enabled === true;
  useEffect(() => {
    if (!open || typeof fetch === 'undefined') return;
    let dead = false;
    fetch(`${API}/api/agent/status`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : null)).then((d) => { if (!dead) setAgentStatus(d?.success ? d : null); }).catch(() => {});
    return () => { dead = true; };
  }, [open]);
  // Phase B: with the agent on and the Agent view chosen, the person's own
  // cards, the badges per tile (only apps with a signal carry one), the
  // headline for the pill, and the apps the question bar pre-ticks.
  const [agentMe, setAgentMe] = useState(null);
  const [agentMeKey, setAgentMeKey] = useState(0);
  useEffect(() => {
    if (!open || !askMode || !agentEnabled || typeof fetch === 'undefined') return;
    let dead = false;
    fetch(`${API}/api/agent/me`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : null)).then((d) => { if (!dead) setAgentMe(d?.success ? d : null); }).catch(() => {});
    return () => { dead = true; };
  }, [open, askMode, agentEnabled, agentMeKey]);
  useEffect(() => {
    if (!askMode || !agentMe?.apps?.length) return;
    setAsked((a) => (a.length ? a : askApps.filter((x) => agentMe.apps.includes(x.tile)).map((x) => x.tile).filter((v, i, arr) => arr.indexOf(v) === i)));
  }, [askMode, agentMe, askApps]);
  const startAsk = () => {
    if (!asked.length) return;
    const chosen = askApps.filter((a) => asked.includes(a.tile));
    try {
      sessionStorage.setItem('lane-apps', JSON.stringify(chosen.map((a) => a.key)));
      sessionStorage.setItem('lane-app-labels', JSON.stringify(Object.fromEntries(chosen.map((a) => [a.key, a.label]))));
    } catch { /* ignore */ }
    pushRecentApp('lane');
    onSelectApp('lane'); onClose();
  };

  // Org identity for the top-left of the Launchpad — same resolution as the
  // Core top bar in src/App.js: the session's organization_logo_url wins,
  // otherwise probe the Deck Studio branding store (the logo compliance
  // uploaded for their decks), otherwise fall back to the org's name as
  // text. Department users land directly on this overlay (Workspace opens
  // it on arrival), so this is their first sight of "their" branded product.
  const [orgLogoUrl, setOrgLogoUrl] = useState(null);
  useEffect(() => {
    if (!user) { setOrgLogoUrl(null); return; }
    if (user.organization_logo_url) { setOrgLogoUrl(user.organization_logo_url); return; }
    let dead = false;
    fetch(`${API}/api/deck-studio/branding`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!dead && d?.has_logo) setOrgLogoUrl(`${API}/api/deck-studio/branding/logo`); })
      .catch(() => {});
    return () => { dead = true; };
  }, [user]);

  // The client's colors (operator order 2026-10-22): the Deck Studio
  // branding tokens compliance set for their decks color the launcher's own
  // chrome too (the Open/Ask switch, the attention pill, the selected row,
  // the Start button, a soft bloom on the glass). App tiles keep their own
  // colors so apps stay recognizable. Only an organization that has set its
  // branding is tinted; everyone else sees the neutral launcher as before.
  const [brandTokens, setBrandTokens] = useState(null);
  useEffect(() => {
    if (!open || typeof fetch === 'undefined') return;
    let dead = false;
    fetch(`${API}/api/deck-studio/branding`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!dead) setBrandTokens(d?.updated_at && d?.tokens ? d.tokens : null); })
      .catch(() => {});
    return () => { dead = true; };
  }, [open]);
  // Fit the screen (operator order 2026-10-22): on a laptop the Launchpad
  // scales down rather than spilling under the dock. Below 780px of viewport
  // height the tiles shrink, the islands pack tighter, Tools runs six per
  // row instead of five, and the dock joins the flow under the islands
  // instead of floating over them, so every icon stays visible without
  // scrolling. On a big display nothing changes.
  const [viewportH, setViewportH] = useState(() => (typeof window !== 'undefined' ? window.innerHeight : 900));
  useEffect(() => {
    if (!open || typeof window === 'undefined') return;
    const onResize = () => setViewportH(window.innerHeight);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open]);
  const compact = viewportH < 780;
  const tileSize = compact ? (viewportH < 680 ? 42 : 46) : 58;
  const perRow = compact ? 6 : 5;

  const isDark = t(false, true) === true;
  const hex6 = (v) => (/^[0-9A-Fa-f]{6}$/.test(String(v || '')) ? `#${String(v).toUpperCase()}` : null);
  const brand = hex6(brandTokens?.brand);
  const brandInk = isDark ? '#FFFFFF' : (hex6(brandTokens?.ink) || '#0F172A');
  const brandFill = brand ? { background: brand, color: '#FFFFFF' } : undefined;
  const brandTint = brand ? { background: `${brand}${isDark ? '33' : '14'}`, color: brandInk, boxShadow: `inset 0 0 0 1px ${brand}66` } : undefined;

  // Active work across the apps — the same per-user, server-scoped counts
  // that power the Workspace page (each source fail-soft). Feeds the red
  // badges on the app tiles and the "N things need you" pill. A failed
  // fetch leaves counts null and the launcher renders exactly as before.
  const [appCounts, setAppCounts] = useState(null);
  useEffect(() => {
    if (!open || typeof fetch === 'undefined') return;
    let dead = false;
    fetch(`${API}/api/workspace/summary`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (dead || !d?.success) return;
        const map = { ...(d.counts || {}) };
        for (const a of (d.apps || [])) if (a.open_count != null && map[a.key] == null) map[a.key] = a.open_count;
        setAppCounts(map);
      })
      .catch(() => {});
    return () => { dead = true; };
  }, [open]);

  // Reset + focus ONLY when the launcher transitions to open. onClose is read through a
  // ref so parent re-renders (background polls create a fresh inline onClose every render)
  // can never re-trigger the reset and wipe the user's search text mid-typing.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  useEffect(() => {
    if (!open) return;
    setQuery(''); setSelIdx(0); setRecents(readRecentApps()); setAskMode(false); setAsked([]);
    const focusTimer = setTimeout(() => searchRef.current?.focus(), 30);
    const handleKey = (e) => { if (e.key === 'Escape') onCloseRef.current(); };
    document.addEventListener('keydown', handleKey);
    return () => { clearTimeout(focusTimer); document.removeEventListener('keydown', handleKey); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  // Mirrors Core's PANEL_CATEGORIES in src/components/AppSidebar.jsx so the
  // top-right launcher and the left-rail show the exact same app set.
  const modules = [
    { id: 'risk_module', label: 'Risk Module',    icon: (<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"/></svg>), apps: ['risk', 'cil'] },
    { id: 'audit_monitoring', label: 'Audit & Monitoring', icon: (<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0118 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM10.5 14.25l1.5 1.5 3-3"/></svg>), apps: ['audit', 'coa'] },
    { id: 'privacy',    label: 'Privacy',         icon: (<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z"/></svg>), apps: ['privacy'] },
    { id: 'fdr',        label: 'FDR',             icon: (<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.75A.75.75 0 0 1 9.75 16.5h4.5a.75.75 0 0 1 .75.75V21"/></svg>), apps: ['fdr'] },
    { id: 'fwa',        label: 'FWA',             icon: (<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/></svg>), apps: ['sentinel'] },
    { id: 'ai_governance', label: 'Responsible AI', icon: (<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 002.25-2.25V6.75a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 6.75v10.5a2.25 2.25 0 002.25 2.25zm.75-12h9v9h-9v-9z"/></svg>), apps: ['ai_command'] },
    { id: 'policies',   label: 'Policies',        icon: (<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/></svg>), apps: ['ppm'] },
    { id: 'materials',  label: 'Materials',       icon: (<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25M8.25 15h7.5M8.25 18h4.5M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/></svg>), apps: ['materials_review'] },
    { id: 'tools',      label: 'Tools',           icon: (<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75a4.5 4.5 0 01-4.884 4.484c-1.076-.091-2.264.071-2.95.904l-7.152 8.684a2.548 2.548 0 11-3.586-3.586l8.684-7.152c.833-.686.995-1.874.904-2.95a4.5 4.5 0 016.336-4.486l-3.276 3.276a3.004 3.004 0 002.25 2.25l3.276-3.276c.256.565.398 1.192.398 1.852z"/></svg>), apps: ['agent', 'readiness', 'deck_studio', 'workstream', 'calendar', 'ratify', 'mandate_native', 'cap', 'network_adequacy', 'scrubber', 'priq', 'reporter', 'universe_requests'] },
  ];

  const visibleModules = modules.map(m => ({ ...m, visibleApps: m.apps.filter(k => canSeeApp(k, user)) })).filter(m => m.visibleApps.length);

  // Every app the user can see, deduped and searchable by label/description.
  const allVisibleKeys = [...new Set(visibleModules.flatMap(m => m.visibleApps))];
  const q = query.trim().toLowerCase();
  const matches = q
    ? allVisibleKeys.filter(k => {
        const app = LAUNCHER_APPS[k];
        return app && (`${app.label} ${app.desc}`.toLowerCase().includes(q));
      })
    : [];
  const visibleRecents = recents.filter(k => allVisibleKeys.includes(k) && LAUNCHER_APPS[k]).slice(0, 6);

  // External apps live on their own subdomains; everything else routes through onSelectApp.
  const externalHrefFor = (key) => {
    const map = { memo: 'mandate', knowledge: 'knowledge' };
    const sub = map[key];
    if (!sub) return null;
    const isAzure = typeof window !== 'undefined' && window.location.hostname.startsWith('az-');
    return `https://${isAzure ? `az-${sub}` : sub}.precisioncompliancegroup.com`;
  };
  const openApp = (key) => {
    pushRecentApp(key);
    const href = externalHrefFor(key);
    if (href) { onClose(); window.location.assign(href); return; }
    onSelectApp(key); onClose();
  };
  const onSearchKeyDown = (e) => {
    if (!matches.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelIdx(i => Math.min(i + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); const key = matches[Math.min(selIdx, matches.length - 1)]; if (key) openApp(key); }
  };

  // One Launchpad icon: iOS-style colored squircle, white glyph, label below.
  const AppTile = ({ appKey, size = tileSize }) => {
    const app = LAUNCHER_APPS[appKey];
    if (!app) return null;
    const color = APP_COLORS[appKey] || '#64748b';
    const AppIcon = app.Icon;
    const href = externalHrefFor(appKey);
    const badgeCount = (askMode && agentEnabled) ? (agentMe?.badges?.[appKey] || 0) : (appCounts && appCounts[appKey] > 0 ? appCounts[appKey] : 0);
    const icon = (
      <span className="relative flex items-center justify-center rounded-[30%] transition-all duration-150 group-hover:scale-[1.09] group-hover:-translate-y-1 group-active:scale-100"
        style={{ width: size, height: size, background: `linear-gradient(160deg, ${color}f0 0%, ${color} 60%, ${color}e6 100%)`, boxShadow: `0 12px 26px -10px ${color}90, inset 0 1px 0 rgba(255,255,255,0.3)` }}>
        {AppIcon ? <AppIcon className="text-white" style={{ width: size * 0.46, height: size * 0.46 }} strokeWidth={1.8} /> : null}
        {badgeCount > 0 && (
          <span aria-label={`${badgeCount} open item${badgeCount === 1 ? '' : 's'}`}
            className="absolute -top-1.5 -right-1.5 min-w-[19px] h-[19px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 shadow-[0_2px_6px_rgba(239,68,68,0.5)] ring-2 ring-white/90">
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
      </span>
    );
    const label = (
      <span className={`${compact ? 'text-[10px] max-w-[74px]' : 'text-[11px] max-w-[86px]'} font-medium leading-tight text-center ${t('text-slate-700','text-slate-300')}`}>{app.label}</span>
    );
    const cls = 'group flex flex-col items-center gap-2';
    if (askMode && askEnabled) {
      const askable = askableTiles.includes(appKey);
      const ticked = asked.includes(appKey);
      return (
        <button key={appKey} type="button" disabled={!askable} title={askable ? (ticked ? `Leave ${app.label} out` : `Ask across ${app.label}`) : 'Not part of a conversation yet'}
          onClick={() => setAsked((a) => (a.includes(appKey) ? a.filter((k) => k !== appKey) : [...a, appKey]))}
          className={`${cls} relative ${askable ? '' : 'opacity-30 cursor-default'}`}>
          <span className="relative">
            {icon}
            {ticked && (
              <span className="absolute -top-1.5 -right-1.5 w-[22px] h-[22px] rounded-full bg-emerald-500 ring-2 ring-white flex items-center justify-center" style={brand ? { background: brand } : undefined} aria-label={`${app.label} selected`}>
                <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
              </span>
            )}
          </span>
          {label}
        </button>
      );
    }
    if (href) return <a key={appKey} href={href} className={cls} title={app.desc} onClick={() => { pushRecentApp(appKey); onClose(); }}>{icon}{label}</a>;
    return <button key={appKey} onClick={() => openApp(appKey)} className={cls} title={app.desc}>{icon}{label}</button>;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-label="App launcher" style={{ animation: 'psiFade .2s ease-out' }}>
      {/* Launchpad field — the whole product recedes behind heavy glass */}
      <div className={`fixed inset-0 ${t('bg-slate-100/70','bg-[#05060a]/78')}`} onClick={onClose}
        style={{ backdropFilter: 'blur(44px) saturate(150%)', WebkitBackdropFilter: 'blur(44px) saturate(150%)' }} />
      {/* The client's color as a soft bloom on the glass, never over the tiles' own colors. */}
      {brand && (
        <div aria-hidden="true" className="fixed inset-0 pointer-events-none"
          style={{ background: `radial-gradient(900px 600px at 15% 0%, ${brand}${isDark ? '40' : '26'}, transparent 70%), radial-gradient(800px 600px at 90% 100%, ${brand}${isDark ? '2E' : '1A'}, transparent 70%)` }} />
      )}
      <style>{`
        @keyframes launchIn { from { opacity:0; transform:scale(.97) translateY(12px) } to { opacity:1; transform:none } }
        @keyframes psiFade { from { opacity:0 } to { opacity:1 } }
        @media (prefers-reduced-motion: reduce) { [style*="launchIn"], [style*="psiFade"] { animation: none !important } }
      `}</style>

      {/* Client identity — top-left, exactly like the Core top bar: the logo
          (wordmark) REPLACES the name when one exists, text name otherwise.
          pointer-events-none so it never intercepts the click-to-close
          backdrop. */}
      <div className="fixed top-6 left-7 z-[1] pointer-events-none" style={{ animation: 'launchIn .24s cubic-bezier(.2,1.1,.3,1)' }}>
        {orgLogoUrl ? (
          <span className="inline-flex items-center bg-white rounded-xl px-3.5 py-2 shadow-[0_10px_30px_-10px_rgba(15,23,42,0.35)]" title={user?.organization_name || ''}>
            <img
              src={orgLogoUrl}
              alt={user?.organization_name || 'Organization logo'}
              onError={() => setOrgLogoUrl(null)}
              className="object-contain object-left"
              style={{ height: '34px', maxWidth: '200px', width: 'auto' }}
            />
          </span>
        ) : user?.organization_name ? (
          <span className={`text-[17px] font-semibold tracking-tight ${t('text-slate-800', 'text-slate-100')}`}>
            {user.organization_name}
          </span>
        ) : null}
      </div>

      <div className={`relative flex flex-col items-center px-6 ${compact ? 'min-h-0 pt-[3vh] pb-8' : 'min-h-full pt-[6vh] pb-36'}`} onClick={onClose}>
        {/* Floating search pill */}
        <div onClick={e => e.stopPropagation()}
          className={`w-full max-w-[420px] flex items-center gap-2.5 px-4 h-11 rounded-full ring-1 ${compact ? 'mb-5' : 'mb-8'} flex-shrink-0
            ${t('bg-white/80 ring-black/[0.07] shadow-[0_10px_30px_-10px_rgba(15,23,42,0.3)]','bg-white/[0.08] ring-white/[0.12] shadow-[0_10px_30px_-10px_rgba(0,0,0,0.7)]')}`}
          style={{ animation: 'launchIn .24s cubic-bezier(.2,1.1,.3,1)', backdropFilter: 'blur(22px)', WebkitBackdropFilter: 'blur(22px)' }}>
          <svg className={`w-[16px] h-[16px] flex-shrink-0 ${t('text-slate-400','text-slate-400')}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 10.5a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z"/></svg>
          <input ref={searchRef} type="text" value={query}
            onChange={(e) => { setQuery(e.target.value); setSelIdx(0); }}
            onKeyDown={onSearchKeyDown}
            placeholder="Search apps…"
            aria-label="Search apps"
            className={`flex-1 bg-transparent outline-none text-[14px] ${t('text-slate-800 placeholder-slate-400','text-white placeholder-slate-400')}`} />
          <kbd className={`hidden sm:inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${t('text-slate-400 bg-slate-100','text-slate-400 bg-white/[0.08]')}`}>⌘K</kbd>
        </div>

        {/* Open an app, or work with the agent (and ask across apps where the desk is on) */}
        {((askEnabled && askableTiles.some((k) => allVisibleKeys.includes(k))) || agentEnabled) && (
          <div onClick={(e) => e.stopPropagation()} className={`${compact ? '-mt-2 mb-4' : '-mt-4 mb-6'} inline-flex items-center rounded-full p-0.5 ring-1 text-[12px] font-medium ${t('bg-white/70 ring-black/[0.07]','bg-white/[0.08] ring-white/[0.12]')}`} style={{ animation: 'launchIn .24s cubic-bezier(.2,1.1,.3,1)' }}>
            <button type="button" onClick={() => { setAskMode(false); setAsked([]); }} style={!askMode ? brandFill : undefined} className={`px-3.5 py-1 rounded-full transition ${!askMode ? t('bg-slate-900 text-white','bg-white text-slate-900') : t('text-slate-500','text-slate-400')}`}>Open</button>
            <button type="button" onClick={() => setAskMode(true)} style={askMode ? brandFill : undefined} className={`px-3.5 py-1 rounded-full transition ${askMode ? t('bg-slate-900 text-white','bg-white text-slate-900') : t('text-slate-500','text-slate-400')}`}>Agent</button>
          </div>
        )}

        {(() => {
          // The alert pill: in Agent mode it says so; otherwise an explicit
          // hint from the parent wins, else it is derived from the live
          // counts — total open work and how many apps carry it. Absent
          // entirely when there is nothing.
          const activeTotal = appCounts ? Object.values(appCounts).reduce((n, v) => n + (v > 0 ? v : 0), 0) : 0;
          const activeApps = appCounts ? Object.values(appCounts).filter((v) => v > 0).length : 0;
          const pillText = askMode ? (agentEnabled && agentMe?.headline ? `Agentic mode on · ${agentMe.headline}` : 'Agentic mode on') : (hint || (activeTotal > 0
            ? `${activeTotal} ${activeTotal === 1 ? 'item needs' : 'items need'} your attention across ${activeApps} app${activeApps === 1 ? '' : 's'}`
            : null));
          if (!pillText) return null;
          return (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              className={`${compact ? 'mb-4 -mt-2 py-1.5 text-[12px]' : 'mb-6 -mt-3 py-2 text-[13px]'} flex items-center gap-2 px-4 rounded-full font-medium transition
                ${brand ? '' : `ring-1 ${t('bg-orange-50 text-orange-800 ring-orange-200 hover:bg-orange-100','bg-orange-500/15 text-orange-200 ring-orange-500/30 hover:bg-orange-500/25')}`}`}
              style={{ animation: 'launchIn .3s cubic-bezier(.2,1.1,.3,1)', ...(brandTint || {}) }}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${brand ? '' : t('bg-orange-500','bg-orange-400')} animate-pulse`} style={brand ? { background: brand } : undefined} />
              {pillText}
            </button>
          );
        })()}

        {/* The agent's panel (phase B): the person's cards, or one quiet line when nothing needs them */}
        {askMode && !q && agentEnabled && (
          <div onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-[620px] rounded-[20px] ring-1 px-6 py-5 ${compact ? 'mb-5' : 'mb-8'} text-left
              ${t('bg-white/85 ring-black/[0.06] shadow-[0_24px_60px_-16px_rgba(15,23,42,0.3)]','bg-[#0d0f16]/90 ring-white/[0.08] shadow-[0_24px_60px_-16px_rgba(0,0,0,0.8)]')}`}
            style={{ animation: 'launchIn .2s cubic-bezier(.2,1.1,.3,1)', backdropFilter: 'blur(26px) saturate(150%)', WebkitBackdropFilter: 'blur(26px) saturate(150%)' }}>
            {!agentMe ? (
              <p className={`text-[13px] ${t('text-slate-500','text-slate-400')}`}>Reading your items…</p>
            ) : agentMe.cards?.length ? (
              <div className="space-y-3 max-h-[46vh] overflow-auto pr-1">
                {agentMe.cards.map((c) => <AgentCard key={c.id} card={c} compact brand={agentMe.brand || null} onDone={() => setAgentMeKey((k) => k + 1)} />)}
              </div>
            ) : (
              <div>
                <p className={`text-[14px] font-semibold ${t('text-slate-800','text-slate-100')}`}>Nothing needs you right now.</p>
                <p className={`text-[13px] mt-1.5 leading-relaxed ${t('text-slate-600','text-slate-300')}`}>
                  The Compliance Agent watches the apps for what is due, late or stalled and speaks here only when something concerns you, one short note at a time.
                </p>
              </div>
            )}
            {agentStatus?.compliance && (
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <button type="button" onClick={() => openApp('agent')} style={brandFill}
                  className={`text-[13px] font-medium px-4 py-1.5 rounded-full transition ${t('bg-slate-900 text-white hover:bg-slate-800','bg-white text-slate-900 hover:bg-slate-100')}`}>
                  Open the Agent
                </button>
                {agentMe?.brief?.headline && <span className={`text-[12.5px] ${t('text-slate-600','text-slate-300')}`}>{agentMe.brief.questions_open ? `${agentMe.brief.questions_open} ${agentMe.brief.questions_open === 1 ? 'question waits' : 'questions wait'} for compliance. ` : ''}{agentMe.brief.headline}</span>}
              </div>
            )}
          </div>
        )}

        {q ? (
          /* ── Search results panel ── */
          <div onClick={e => e.stopPropagation()}
            className={`w-full max-w-[520px] rounded-[20px] ring-1 p-2
              ${t('bg-white/85 ring-black/[0.06] shadow-[0_24px_60px_-16px_rgba(15,23,42,0.3)]','bg-[#0d0f16]/90 ring-white/[0.08] shadow-[0_24px_60px_-16px_rgba(0,0,0,0.8)]')}`}
            style={{ animation: 'launchIn .2s cubic-bezier(.2,1.1,.3,1)', backdropFilter: 'blur(26px) saturate(150%)', WebkitBackdropFilter: 'blur(26px) saturate(150%)' }}>
            {matches.length === 0 ? (
              <div className={`flex flex-col items-center justify-center py-12 gap-2 ${t('text-slate-400','text-slate-500')}`}>
                <span className="text-2xl" aria-hidden>◌</span>
                <p className="text-[13px]">No app matches “{query}”</p>
              </div>
            ) : matches.map((key, i) => {
              const app = LAUNCHER_APPS[key];
              const color = APP_COLORS[key] || '#64748b';
              const AppIcon = app.Icon;
              const selected = i === selIdx;
              return (
                <button key={key} onClick={() => openApp(key)} onMouseEnter={() => setSelIdx(i)}
                  style={selected && brand ? { background: `${brand}${isDark ? '2E' : '14'}` } : undefined}
                  className={`w-full flex items-center gap-3.5 px-3 py-2.5 rounded-xl text-left transition-colors duration-100
                    ${selected ? t('bg-slate-900/[0.05]','bg-white/[0.07]') : ''}`}>
                  <span className="w-9 h-9 rounded-[30%] flex items-center justify-center flex-shrink-0"
                    style={{ background: `linear-gradient(160deg, ${color}f0, ${color})`, boxShadow: `inset 0 1px 0 rgba(255,255,255,0.28)` }}>
                    {AppIcon ? <AppIcon className="w-[17px] h-[17px] text-white" strokeWidth={1.8} /> : null}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className={`block text-[13px] font-semibold leading-tight ${t('text-slate-800','text-slate-100')}`}>{app.label}</span>
                    <span className={`block text-[11px] mt-0.5 truncate ${t('text-slate-400','text-slate-500')}`}>{app.desc}</span>
                  </span>
                  {selected && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${t('text-slate-400 bg-slate-100','text-slate-400 bg-white/[0.06]')}`}>↵</span>}
                </button>
              );
            })}
          </div>
        ) : (
          /* ── Launchpad — horizontal islands, everything on one screen ── */
          <div className={`w-full max-w-[1140px] flex flex-wrap justify-center items-start ${compact ? 'gap-x-8 gap-y-5' : 'gap-x-12 gap-y-9'}`} style={{ animation: 'launchIn .26s cubic-bezier(.2,1.1,.3,1)' }}>
            {visibleModules.map(mod => (
              <section key={mod.id} onClick={e => e.stopPropagation()} className="flex flex-col items-center">
                <h3 className={`text-[10px] font-bold uppercase tracking-[0.1em] ${compact ? 'mb-2' : 'mb-3.5'} text-center ${t('text-slate-400','text-slate-500')}`}>{mod.label}</h3>
                <div className={`grid justify-items-center ${compact ? 'gap-x-4 gap-y-3' : 'gap-x-5 gap-y-5'}`} style={{ gridTemplateColumns: `repeat(${Math.min(mod.visibleApps.length, perRow)}, minmax(0, 1fr))` }}>
                  {mod.visibleApps.map(key => <AppTile key={key} appKey={key} />)}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>

      {/* The question bar — the ticked apps and the one button that opens the desk (unchanged behavior) */}
      {askMode && !q && askEnabled && (
        <div className={compact ? 'relative z-10 flex justify-center -mt-2 pb-7' : 'fixed bottom-8 left-1/2 -translate-x-1/2 z-10'} onClick={(e) => e.stopPropagation()}>
          <div className={`flex items-center gap-3 pl-5 pr-2 py-2 rounded-full ring-1 ${t('bg-white/80 ring-black/[0.08] shadow-[0_20px_50px_-15px_rgba(15,23,42,0.35)]','bg-white/[0.09] ring-white/[0.12] shadow-[0_20px_50px_-15px_rgba(0,0,0,0.8)]')}`}
            style={{ animation: 'launchIn .3s cubic-bezier(.2,1.1,.3,1)', backdropFilter: 'blur(32px) saturate(170%)', WebkitBackdropFilter: 'blur(32px) saturate(170%)' }}>
            <span className={`text-[13px] ${t('text-slate-600','text-slate-300')}`}>
              {asked.length ? `Ask across ${asked.map((k) => LAUNCHER_APPS[k]?.label || k).join(', ')}` : 'Tick the apps to ask across'}
            </span>
            <button type="button" onClick={startAsk} disabled={!asked.length} style={brandFill}
              className={`text-[13px] font-medium px-4 py-1.5 rounded-full transition disabled:opacity-40 ${t('bg-slate-900 text-white hover:bg-slate-800','bg-white text-slate-900 hover:bg-slate-100')}`}>
              Start
            </button>
          </div>
        </div>
      )}

      {/* Dock — recent apps, always within thumb's reach */}
      {!q && !askMode && visibleRecents.length > 0 && (
        <div className={compact ? 'relative z-10 flex justify-center -mt-2 pb-7' : 'fixed bottom-8 left-1/2 -translate-x-1/2 z-10'} onClick={e => e.stopPropagation()}>
          <div className={`flex items-end ${compact ? 'gap-2.5 px-3 py-2 rounded-[20px]' : 'gap-3 px-4 py-3 rounded-[26px]'} ring-1
              ${t('bg-white/60 ring-black/[0.08] shadow-[0_20px_50px_-15px_rgba(15,23,42,0.35)]','bg-white/[0.09] ring-white/[0.12] shadow-[0_20px_50px_-15px_rgba(0,0,0,0.8)]')}`}
            style={{ animation: 'launchIn .3s cubic-bezier(.2,1.1,.3,1)', backdropFilter: 'blur(32px) saturate(170%)', WebkitBackdropFilter: 'blur(32px) saturate(170%)' }}>
            {visibleRecents.map(key => {
              const app = LAUNCHER_APPS[key];
              const color = APP_COLORS[key] || '#64748b';
              const AppIcon = app.Icon;
              return (
                <button key={key} onClick={() => openApp(key)} title={app.label}
                  className="group flex items-center justify-center rounded-[30%] transition-all duration-150 hover:scale-[1.12] hover:-translate-y-1.5 active:scale-100"
                  style={{ width: compact ? 38 : 46, height: compact ? 38 : 46, background: `linear-gradient(160deg, ${color}f0, ${color})`, boxShadow: `0 10px 22px -8px ${color}90, inset 0 1px 0 rgba(255,255,255,0.3)` }}>
                  {AppIcon ? <AppIcon className={compact ? 'w-[18px] h-[18px] text-white' : 'w-[21px] h-[21px] text-white'} strokeWidth={1.8} /> : null}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Footer hint */}
      <div className={`fixed bottom-2 inset-x-0 text-center text-[10px] font-medium ${t('text-slate-400','text-slate-500')}`}>
        {q ? `${matches.length} result${matches.length !== 1 ? 's' : ''} · ↑↓ navigate · ↵ open · esc close` : askMode ? 'tick one or more apps, then Start · esc close' : 'click anywhere or press esc to close'}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PriorityCard
// ---------------------------------------------------------------------------
function PriorityCard({ item, onAction, onDraft, onApproveAndAct, onOpenApp, onReclassified, t }) {
  const [expanded, setExpanded]         = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [draft, setDraft]               = useState(item.pom_draft || null);
  const [actioning, setActioning]       = useState(false);
  const [reclassifying, setReclassifying] = useState(false);
  const [reclassType, setReclassType]     = useState(null);
  const [reclassSaving, setReclassSaving] = useState(false);

  // Reclassify — only for items from ingested documents
  const isFromIntake = item.source_entity_type === 'ingested_document';
  const handleReclassify = async () => {
    if (!reclassType || !item.source_entity_id) return;
    setReclassSaving(true);
    try {
      await fetch(`${API}/api/overmind/intake/${item.source_entity_id}/reclassify`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ artifact_type: reclassType }),
      });
      setReclassifying(false);
      setReclassType(null);
      onReclassified?.();
    } catch {}
    setReclassSaving(false);
  };

  const cfg       = URGENCY_CONFIG[item.urgency_level] || URGENCY_CONFIG.normal;
  const dueInfo   = formatDueDate(item.due_date);
  const contextText = humanizeContext(item.context);

  // "Why" clamps to a couple of lines — this is a triage screen, the full
  // reasoning is one click away, never a wall of text.
  const [whyOpen, setWhyOpen] = useState(false);
  const whyText = String(item.why || '');
  const whyLong = whyText.length > 200;
  const whyShown = whyOpen || !whyLong ? whyText : `${whyText.slice(0, 200).replace(/\s+\S*$/, '')}…`;

  const handleDraft = async () => {
    if (draft) { setExpanded(true); return; }
    setLoadingDraft(true);
    const result = await onDraft(item.id);
    if (result) setDraft(result);
    setLoadingDraft(false);
    setExpanded(true);
  };

  const handleAction = async (action) => { setActioning(true); await onAction(item.id, action); setActioning(false); };
  const handleApproveAct = async () => { setActioning(true); await onApproveAndAct(item.id); setActioning(false); };

  const badgeCfg = {
    critical: t('bg-red-100 text-red-700 border-red-200', 'bg-red-500/10 text-red-400 border-red-500/20'),
    high:     t('bg-amber-50 text-amber-700 border-amber-200', 'bg-amber-400/10 text-amber-400 border-amber-400/20'),
    normal:   t('bg-blue-50 text-blue-700 border-blue-200', 'bg-blue-500/10 text-blue-400 border-blue-500/20'),
    low:      t('bg-slate-100 text-slate-500 border-slate-200', 'bg-slate-700/50 text-slate-400 border-slate-600/30'),
  };

  return (
    <div className={`group relative rounded-[20px] overflow-hidden transition-all duration-200
      ${t(
        'bg-white/75 backdrop-blur-xl ring-1 ring-slate-200/70 shadow-[0_2px_10px_-4px_rgba(15,23,42,0.08)] hover:shadow-[0_14px_34px_-14px_rgba(15,23,42,0.22)] hover:ring-slate-300/70 hover:-translate-y-px',
        'bg-white/[0.035] backdrop-blur-xl ring-1 ring-white/[0.07] hover:ring-white/[0.14] hover:-translate-y-px'
      )}`}
    >
      <div className="pl-5 pr-5 py-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3.5 min-w-0 flex-1">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${badgeCfg[item.urgency_level] || badgeCfg.normal}`}>
                  {cfg.label}
                </span>
                <span className={`text-xs font-medium ${t('text-slate-400', 'text-slate-500')}`}>
                  {APP_LABELS[item.source_app] || item.source_app}
                </span>
                {dueInfo && <span className={`text-xs ${dueInfo.color}`}>{dueInfo.text}</span>}
              </div>
              <h3 className={`text-sm font-semibold leading-snug ${t('text-slate-800', 'text-slate-100')}`}>{item.title}</h3>
              {contextText && <p className={`text-xs mt-0.5 leading-relaxed ${t('text-slate-500', 'text-slate-500')}`}>{contextText}</p>}
            </div>
          </div>
          <div className="flex-shrink-0 text-right">
            <div className={`text-lg font-bold tabular-nums leading-none ${t('text-slate-700', 'text-white')}`}>{Math.round(item.priority_score)}</div>
            <div className={`text-xs leading-none mt-0.5 ${t('text-slate-400', 'text-slate-600')}`}>score</div>
          </div>
        </div>

        {item.why && (
          <div className="mt-2 flex items-start gap-1.5">
            <span className={`text-xs flex-shrink-0 mt-px ${t('text-slate-400', 'text-slate-600')}`}>Why:</span>
            <span className={`text-xs leading-relaxed ${t('text-slate-600', 'text-slate-400')}`}>
              {whyShown}
              {whyLong && (
                <button onClick={() => setWhyOpen(o => !o)}
                  className={`ml-1.5 font-semibold ${t('text-orange-600 hover:text-orange-700', 'text-orange-400 hover:text-orange-300')}`}>
                  {whyOpen ? 'Less' : 'More'}
                </button>
              )}
            </span>
          </div>
        )}

        {item.suggested_action && !expanded && (
          <div className="mt-2 flex items-start gap-1.5">
            <span className={`text-xs flex-shrink-0 mt-px ${t('text-slate-400', 'text-slate-600')}`}>Action:</span>
            <span className={`text-xs leading-relaxed ${t('text-slate-600', 'text-slate-400')}`}>{item.suggested_action}</span>
          </div>
        )}

        {/* Actions */}
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {item.action_url && onOpenApp && (
            <button onClick={() => openPriorityItem(item, onOpenApp)}
              className="px-3 py-1.5 text-xs text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-all">
              Open in {APP_LABELS[item.source_app] || 'App'} →
            </button>
          )}
          <button onClick={() => handleAction('snooze')} disabled={actioning}
            className={`px-3 py-1.5 text-xs transition-colors disabled:opacity-50 ${t('text-slate-400 hover:text-slate-600','text-slate-600 hover:text-slate-400')}`}>Snooze</button>
          <button onClick={() => handleAction('dismiss')} disabled={actioning}
            className={`px-3 py-1.5 text-xs transition-colors disabled:opacity-50 ${t('text-slate-400 hover:text-slate-600','text-slate-600 hover:text-slate-400')}`}>Dismiss</button>
          {isFromIntake && (
            <button onClick={() => setReclassifying(!reclassifying)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                reclassifying
                  ? t('bg-amber-50 text-amber-700 border-amber-200','bg-amber-500/10 text-amber-400 border-amber-500/20')
                  : t('text-amber-600 border-amber-200 hover:bg-amber-50','text-amber-400 border-amber-500/20 hover:bg-amber-500/10')
              }`}>
              {reclassifying ? 'Cancel' : 'Wrong type? Reclassify'}
            </button>
          )}
        </div>

        {/* Reclassify panel */}
        {reclassifying && isFromIntake && (
          <div className={`mt-3 rounded-xl border p-4 ${t('bg-amber-50/50 border-amber-200','bg-amber-500/5 border-amber-500/20')}`}>
            <p className={`text-xs font-semibold mb-2 ${t('text-amber-800','text-amber-300')}`}>PSI classified this wrong. Select the correct type:</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mb-3">
              {ARTIFACT_OPTIONS.map(opt => (
                <button key={opt.value} onClick={() => setReclassType(opt.value)}
                  className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg border text-xs transition-all ${
                    reclassType === opt.value
                      ? t('bg-amber-100 border-amber-400 text-amber-900 font-bold','bg-amber-500/20 border-amber-500/40 text-amber-200 font-bold')
                      : t('bg-white border-slate-200 text-slate-600 hover:border-amber-300','bg-black/20 border-white/[0.06] text-slate-400 hover:border-amber-500/30')
                  }`}>
                  {opt.label}
                </button>
              ))}
            </div>
            <button onClick={handleReclassify} disabled={!reclassType || reclassSaving}
              className="w-full py-2 text-xs font-bold rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors disabled:opacity-40">
              {reclassSaving ? 'Reclassifying…' : !reclassType ? 'Select a type above' : `Reclassify as ${ARTIFACT_FRIENDLY[reclassType]?.label || reclassType}`}
            </button>
            <p className={`text-xs mt-2 ${t('text-amber-600','text-amber-500')}`}>PSI and all agents will learn from this correction.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// StatCard
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// PriorityRow + PriorityDrawer — the queue scans as rows, one item acts in a
// drawer. Behavior is the old card's exactly: same handlers, same endpoints
// (open-in-app, snooze, dismiss, reclassify). Only the presentation is new.
// ---------------------------------------------------------------------------
// Soft app tags — Asana project-pill treatment: a tinted chip per module.
const APP_TAG_COLORS = {
  network_adequacy: '#3b82f6', calendar: '#8b5cf6', coa: '#10b981', cil: '#64748b',
  risk: '#eab308', privacy: '#06b6d4', memo: '#22c55e', cap: '#0ea5e9',
  ppm: '#a855f7', sentinel: '#ef4444', fdr: '#14b8a6', priq: '#f59e0b',
};
const URGENCY_EDGE = {
  critical: { bar: 'bg-red-500',   glowL: 'shadow-[inset_3px_0_0_#ef4444]', pill: 'bg-red-500/10 text-red-500 ring-1 ring-red-500/30' },
  high:     { bar: 'bg-amber-500', glowL: 'shadow-[inset_3px_0_0_#f59e0b]', pill: 'bg-amber-500/10 text-amber-500 ring-1 ring-amber-500/30' },
  normal:   { bar: 'bg-blue-400',  glowL: 'shadow-[inset_3px_0_0_#60a5fa]', pill: 'bg-blue-500/10 text-blue-500 ring-1 ring-blue-500/30' },
  low:      { bar: 'bg-slate-400', glowL: 'shadow-[inset_3px_0_0_#94a3b8]', pill: 'bg-slate-500/10 text-slate-500 ring-1 ring-slate-400/30' },
};

function PriorityRow({ item, onSelect, t, hero = false }) {
  const cfg  = URGENCY_CONFIG[item.urgency_level] || URGENCY_CONFIG.normal;
  const edge = URGENCY_EDGE[item.urgency_level] || URGENCY_EDGE.normal;
  const dueInfo = formatDueDate(item.due_date);
  const contextText = humanizeContext(item.context);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect?.(item)}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect?.(item)}
      className={`group relative flex items-center gap-3 ${hero ? 'px-5 py-4' : 'px-5 py-3.5'} cursor-pointer transition-all duration-150 border-b last:border-b-0 ${t('border-slate-100 hover:bg-slate-50/80', 'border-white/[0.05] hover:bg-white/[0.05]')}`}
    >
      {/* Severity as a quiet dot — color is an accent, never a shout. */}
      <span
        className={`flex-shrink-0 w-2 h-2 rounded-full ${cfg.dot}`}
        title={`${cfg.label} priority`}
        aria-label={`${cfg.label} priority`}
      />

      <div className="flex-1 min-w-0">
        <div className={`${hero ? 'text-sm' : 'text-[13px]'} font-medium leading-snug truncate ${t('text-slate-800', 'text-slate-100')}`}>
          {item.title}
        </div>
        {(contextText || (hero && item.why)) && (
          <div className={`text-xs truncate mt-0.5 ${t('text-slate-500', 'text-slate-500')}`}>
            {contextText}
            {hero && item.why ? <span>{contextText ? ' — ' : ''}{String(item.why).slice(0, 110)}…</span> : null}
          </div>
        )}
      </div>

      {/* App tag — the Asana project pill. */}
      <span
        className="hidden md:inline-flex flex-shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-md"
        style={{
          backgroundColor: `${APP_TAG_COLORS[item.source_app] || '#64748b'}1a`,
          color: APP_TAG_COLORS[item.source_app] || '#64748b',
        }}
      >
        {APP_LABELS[item.source_app] || item.source_app}
      </span>

      {/* Due date: a fixed right-aligned slot so every row lines up. */}
      <span className={`hidden sm:inline-block flex-shrink-0 w-[76px] text-right text-[12px] font-medium ${dueInfo ? dueInfo.color : t('text-slate-300', 'text-slate-600')}`}>
        {dueInfo ? dueInfo.text : ''}
      </span>

      {/* No score on display. The best queue tools show none: the internal
          ranking decides the ORDER of the list; the user sees severity, due
          date, and words — meaning, not machinery. */}
      <svg className={`w-4 h-4 flex-shrink-0 transition-transform group-hover:translate-x-0.5 ${t('text-slate-300 group-hover:text-slate-500', 'text-slate-600 group-hover:text-slate-300')}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </div>
  );
}

function PriorityDrawer({ item, onClose, onAction, onOpenApp, onReclassified, t }) {
  const [actioning, setActioning] = useState(false);
  const [reclassifying, setReclassifying] = useState(false);
  const [reclassType, setReclassType] = useState(null);
  const [reclassSaving, setReclassSaving] = useState(false);

  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);
  useEffect(() => { setReclassifying(false); setReclassType(null); }, [item?.id]);

  if (!item) return null;
  const cfg  = URGENCY_CONFIG[item.urgency_level] || URGENCY_CONFIG.normal;
  const edge = URGENCY_EDGE[item.urgency_level] || URGENCY_EDGE.normal;
  const dueInfo = formatDueDate(item.due_date);
  const contextText = humanizeContext(item.context);
  const isFromIntake = item.source_entity_type === 'ingested_document';

  // The same endpoint the old card called — reclassify teaches the agents.
  const handleReclassify = async () => {
    if (!reclassType || !item.source_entity_id) return;
    setReclassSaving(true);
    try {
      await fetch(`${API}/api/overmind/intake/${item.source_entity_id}/reclassify`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ artifact_type: reclassType }),
      });
      setReclassifying(false); setReclassType(null);
      onReclassified?.();
      onClose();
    } catch {}
    setReclassSaving(false);
  };
  const act = async (action) => { setActioning(true); await onAction(item.id, action); setActioning(false); };

  return (
    <>
      <div className="fixed inset-0 bg-slate-950/50 z-40 backdrop-blur-[2px]" onClick={onClose} role="button" tabIndex={0} aria-label="Close" />
      <aside className={`fixed right-0 top-0 h-full w-full max-w-lg z-50 flex flex-col shadow-2xl border-l ${t('bg-white border-slate-200', 'bg-[#0b0d12] border-white/[0.07]')}`} role="dialog">
        {/* Urgency lives in the frame itself. */}
        <div className={`h-1 flex-shrink-0 ${edge.bar}`} />

        <div className="flex-1 overflow-y-auto">
          <div className="px-6 pt-5 pb-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-md ${edge.pill}`}>{cfg.label}</span>
                <span className={`text-xs font-medium ${t('text-slate-500', 'text-slate-400')}`}>{APP_LABELS[item.source_app] || item.source_app}</span>
                {dueInfo && <span className={`text-xs font-medium ${dueInfo.color}`}>{dueInfo.text}</span>}
              </div>
              <button type="button" onClick={onClose} aria-label="Close"
                className={`p-1.5 -mr-1.5 rounded-lg transition-colors ${t('text-slate-400 hover:bg-slate-100 hover:text-slate-600', 'text-slate-500 hover:bg-white/[0.06] hover:text-slate-300')}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <h2 className={`mt-3 text-lg font-semibold leading-snug ${t('text-slate-900', 'text-white')}`}>{item.title}</h2>
            {contextText && <p className={`mt-1 text-[13px] ${t('text-slate-500', 'text-slate-400')}`}>{contextText}</p>}
            {/* No score on display — severity, dates and words carry the
                meaning; the internal score only orders the list. */}
          </div>

          {item.why && (
            <div className="px-6 py-4">
              <h3 className={`text-[11px] font-bold uppercase tracking-[0.08em] mb-1.5 ${t('text-slate-400', 'text-slate-500')}`}>Why this needs you</h3>
              <p className={`text-[13.5px] leading-relaxed whitespace-pre-wrap ${t('text-slate-700', 'text-slate-300')}`}>{item.why}</p>
            </div>
          )}

          {item.suggested_action && (
            <div className="px-6 py-4">
              <h3 className={`text-[11px] font-bold uppercase tracking-[0.08em] mb-1.5 ${t('text-slate-400', 'text-slate-500')}`}>Suggested next step</h3>
              <div className={`rounded-xl px-4 py-3 text-[13.5px] leading-relaxed border-l-2 ${t('bg-orange-50/60 border-orange-400 text-slate-700', 'bg-orange-500/[0.06] border-orange-500/60 text-slate-300')}`}>
                {item.suggested_action}
              </div>
            </div>
          )}

          {reclassifying && isFromIntake && (
            <div className="px-6 py-4">
              <div className={`rounded-xl border p-4 ${t('bg-amber-50/50 border-amber-200', 'bg-amber-500/5 border-amber-500/20')}`}>
                <p className={`text-xs font-semibold mb-2 ${t('text-amber-800', 'text-amber-300')}`}>PSI classified this wrong. Select the correct type:</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mb-3">
                  {ARTIFACT_OPTIONS.map(opt => (
                    <button key={opt.value} onClick={() => setReclassType(opt.value)}
                      className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg border text-xs transition-all ${
                        reclassType === opt.value
                          ? t('bg-amber-100 border-amber-400 text-amber-900 font-bold', 'bg-amber-500/20 border-amber-500/40 text-amber-200 font-bold')
                          : t('bg-white border-slate-200 text-slate-600 hover:border-amber-300', 'bg-black/20 border-white/[0.06] text-slate-400 hover:border-amber-500/30')
                      }`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
                <button onClick={handleReclassify} disabled={!reclassType || reclassSaving}
                  className="w-full py-2 text-xs font-bold rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors disabled:opacity-40">
                  {reclassSaving ? 'Reclassifying…' : !reclassType ? 'Select a type above' : `Reclassify as ${ARTIFACT_FRIENDLY[reclassType]?.label || reclassType}`}
                </button>
                <p className={`text-xs mt-2 ${t('text-amber-600', 'text-amber-500')}`}>PSI and all agents will learn from this correction.</p>
              </div>
            </div>
          )}
        </div>

        {/* Actions, pinned where thumbs expect them. Same handlers as always. */}
        <div className={`flex-shrink-0 px-6 py-4 border-t backdrop-blur-xl ${t('bg-white/90 border-slate-200', 'bg-[#0b0d12]/90 border-white/[0.07]')}`}>
          <div className="flex items-center gap-2">
            {item.action_url && onOpenApp && (
              <button onClick={() => { openPriorityItem(item, onOpenApp); onClose(); }}
                className="flex-1 px-4 py-2.5 text-[13px] font-semibold rounded-xl bg-orange-500 text-white hover:bg-orange-600 transition-colors">
                Open in {APP_LABELS[item.source_app] || 'app'} →
              </button>
            )}
            <button onClick={() => act('snooze')} disabled={actioning}
              className={`px-4 py-2.5 text-[13px] font-medium rounded-xl transition-colors disabled:opacity-50 ${t('text-slate-600 bg-slate-100 hover:bg-slate-200', 'text-slate-300 bg-white/[0.06] hover:bg-white/[0.1]')}`}>
              Snooze
            </button>
            <button onClick={() => act('dismiss')} disabled={actioning}
              className={`px-4 py-2.5 text-[13px] font-medium rounded-xl transition-colors disabled:opacity-50 ${t('text-slate-500 hover:bg-slate-100', 'text-slate-400 hover:bg-white/[0.06]')}`}>
              Dismiss
            </button>
          </div>
          {isFromIntake && !reclassifying && (
            <button onClick={() => setReclassifying(true)}
              className={`mt-2 text-xs font-medium ${t('text-amber-600 hover:text-amber-700', 'text-amber-400 hover:text-amber-300')}`}>
              Wrong type? Reclassify — PSI learns from it
            </button>
          )}
        </div>
      </aside>
    </>
  );
}

function StatCard({ label, value, color, active, onClick, icon, t }) {
  return (
    <button onClick={onClick}
      className={`flex flex-col items-start p-4 rounded-2xl border transition-all duration-200 text-left w-full
        ${active
          ? `${color.activeBg} ${color.activeBorder} shadow-md`
          : t('bg-white/75 backdrop-blur-xl ring-1 ring-slate-200/60 border-transparent hover:border-slate-300 hover:shadow-sm', 'bg-white/[0.03] backdrop-blur-xl ring-1 ring-white/[0.06] border-transparent hover:border-white/[0.12]')
        }`}
    >
      <div className={`text-2xl font-bold tabular-nums mb-1 ${active ? color.activeText : t('text-slate-800', 'text-white')}`}>{value}</div>
      <div className={`text-xs font-medium flex items-center gap-1.5 ${active ? color.activeText : t('text-slate-500', 'text-slate-500')}`}>
        <span>{icon}</span><span>{label}</span>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// ComingDueTab
// ---------------------------------------------------------------------------
function ComingDueTab({ items, onOpenApp, t }) {
  const now = new Date();
  const withDue = items.filter(i => i.due_date).map(i => {
    const d = new Date(i.due_date);
    return { ...i, daysLeft: Math.ceil((d - now) / 86400000) };
  }).sort((a, b) => a.daysLeft - b.daysLeft);

  const groups = [
    { label: 'Overdue',      items: withDue.filter(i => i.daysLeft < 0),                      color: 'text-red-600',   bg: t('bg-red-50 border-red-200','bg-red-500/10 border-red-500/20') },
    { label: 'Today',        items: withDue.filter(i => i.daysLeft === 0),                     color: 'text-red-600',   bg: t('bg-red-50 border-red-200','bg-red-500/10 border-red-500/20') },
    { label: 'This Week',    items: withDue.filter(i => i.daysLeft >= 1 && i.daysLeft <= 7),   color: 'text-amber-600', bg: t('bg-amber-50 border-amber-200','bg-amber-400/10 border-amber-400/20') },
    { label: 'Next 2 Weeks', items: withDue.filter(i => i.daysLeft >= 8 && i.daysLeft <= 14),  color: 'text-blue-600',  bg: t('bg-blue-50 border-blue-200','bg-blue-500/10 border-blue-500/20') },
    { label: 'Next 30 Days', items: withDue.filter(i => i.daysLeft >= 15 && i.daysLeft <= 30), color: 'text-slate-600', bg: t('bg-slate-50 border-slate-200','bg-white/[0.04] border-white/[0.08]') },
  ].filter(g => g.items.length > 0);

  if (!groups.length) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 text-2xl ${t('bg-gradient-to-br from-green-100 to-teal-100','bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/20')}`}>✅</div>
        <p className={`font-semibold ${t('text-slate-600','text-slate-300')}`}>Nothing due in the next 30 days.</p>
        <p className={`text-sm mt-1 ${t('text-slate-400','text-slate-600')}`}>Keep up the great work.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {groups.map(group => (
        <div key={group.label}>
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold border mb-3 ${group.bg} ${group.color}`}>
            {group.label} · {group.items.length} item{group.items.length !== 1 ? 's' : ''}
          </div>
          <div className="space-y-2">
            {group.items.map(item => {
              const cfg = URGENCY_CONFIG[item.urgency_level] || URGENCY_CONFIG.normal;
              const dueInfo = formatDueDate(item.due_date);
              return (
                <div key={item.id} className={`flex items-center gap-3 rounded-xl px-4 py-3 transition-all ${t('bg-white border border-slate-200/80 hover:shadow-sm','bg-[#0d1117] border border-white/[0.06] hover:border-white/[0.12]')}`}>
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${t('text-slate-800','text-slate-200')}`}>{item.title}</p>
                    <p className={`text-xs mt-0.5 ${t('text-slate-400','text-slate-500')}`}>{APP_LABELS[item.source_app] || item.source_app}</p>
                  </div>
                  {dueInfo && <span className={`text-xs font-semibold flex-shrink-0 ${dueInfo.color}`}>{dueInfo.text}</span>}
                  {onOpenApp && <button onClick={() => openPriorityItem(item, onOpenApp)} className="flex-shrink-0 text-xs text-blue-600 hover:text-blue-700 font-medium">Open →</button>}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Intake Inbox
// ---------------------------------------------------------------------------

const ARTIFACT_FRIENDLY = {
  memo:           { label: 'Regulatory Memo',  icon: '📄' },
  audit_report:   { label: 'Audit Report',     icon: '🔍' },
  na_file:        { label: 'NA Provider File', icon: '🌐' },
  universe:       { label: 'Universe File',    icon: '📊' },
  protocol_file:  { label: 'Protocol File',    icon: '📋' },
  fdr_document:   { label: 'FDR Document',     icon: '🤝' },
};

// External app URLs are host-aware: when the user is on az-core, they need
// az-mandate / az-scrubber / etc. The plain `<host>.precisioncompliancegroup.com`
// names belong to the legacy Render deploy and don't resolve in DNS anymore,
// so a hardcoded href to those leaves the new tab on a "site can't be reached"
// page — looks like "Mandate doesn't open" from the user's perspective.
// eslint-disable-next-line no-unused-vars
function externalAppHref(subBase) {
  const isAzure = typeof window !== 'undefined' && window.location.hostname.startsWith('az-');
  const sub = isAzure ? `az-${subBase}` : subBase;
  return `https://${sub}.precisioncompliancegroup.com`;
}

// Audit reports now flow into Core's in-Core CAP module rather than the
// decommissioned cap.precisioncompliancegroup.com app, so memo + audit_report
// route via appKey through the launcher selector.
const ROUTE_TARGET = {
  // Memos land in the native Precision Mandate module inside Core; the
  // standalone app is no longer the destination.
  memo:           { label: 'Precision Mandate', href: null,                        appKey: 'mandate_native' },
  audit_report:   { label: 'CAP',              href: null,                         appKey: 'cap' },
  na_file:        { label: 'Network Adequacy', href: null,                         appKey: 'network_adequacy' },
  universe:       { label: 'COA',              href: null,                         appKey: 'coa' },
  protocol_file:  { label: 'Scrubber',         href: null,                         appKey: 'scrubber' },
  fdr_document:   { label: 'FDR Oversight',    href: null,                         appKey: 'fdr' },
};

const ARTIFACT_OPTIONS = [
  { value: 'memo',          label: 'Regulatory Memo' },
  { value: 'audit_report',  label: 'Audit Report' },
  { value: 'na_file',       label: 'NA Provider File' },
  { value: 'universe',      label: 'Universe File' },
  { value: 'protocol_file', label: 'Protocol File' },
  { value: 'fdr_document',  label: 'FDR Document' },
];

function IntakeDocCard({ doc, onConfirm, onOpenApp, t }) {
  const [confirming, setConfirming] = useState(false);
  const [overriding, setOverriding] = useState(false);
  const [chosenType, setChosenType] = useState(doc.artifact_type);
  const [done, setDone]             = useState(false);

  const type   = ARTIFACT_FRIENDLY[doc.artifact_type] || { label: doc.artifact_type, icon: '📄' };
  const target = ROUTE_TARGET[doc.artifact_type];

  const handleConfirm = async (typeToUse) => {
    setConfirming(true);
    await onConfirm(doc.id, typeToUse || doc.artifact_type);
    setDone(true); setConfirming(false); setOverriding(false);
  };

  if (done) {
    return (
      <div className={`flex items-center gap-3 rounded-xl px-4 py-3 ${t('bg-emerald-50 border border-emerald-200','bg-emerald-500/10 border border-emerald-500/20')}`}>
        <div>
          <p className={`text-sm font-medium ${t('text-emerald-800','text-emerald-300')}`}>{doc.file_name}</p>
          <p className={`text-xs mt-0.5 ${t('text-emerald-600','text-emerald-500')}`}>Confirmed — routed to your Action Queue</p>
        </div>
      </div>
    );
  }

  const typeBadge = t(
    'bg-blue-50 text-blue-700 border-blue-200',
    'bg-blue-500/10 text-blue-400 border-blue-500/20'
  );
  const confBadge = {
    high:   t('bg-emerald-100 text-emerald-700 border-emerald-200', 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'),
    medium: t('bg-amber-100 text-amber-700 border-amber-200', 'bg-amber-400/10 text-amber-400 border-amber-400/20'),
    low:    t('bg-slate-100 text-slate-500 border-slate-200', 'bg-slate-700/50 text-slate-400 border-slate-600/30'),
  }[doc.routing_confidence] || t('bg-slate-100 text-slate-500 border-slate-200', 'bg-slate-700/50 text-slate-400 border-slate-600/30');

  return (
    <div className={`rounded-2xl p-4 space-y-3 transition-all ${t('bg-white border border-slate-200/80 hover:shadow-sm','bg-[#0d1117] border border-white/[0.08] hover:border-white/[0.14]')}`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0">
          <p className={`text-sm font-semibold truncate ${t('text-slate-800','text-slate-100')}`}>{doc.file_name}</p>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded-full border ${typeBadge}`}>{type.label}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full border ${confBadge}`}>{doc.routing_confidence} confidence</span>
            <span className={`text-xs ${t('text-slate-400','text-slate-600')}`}>{timeAgo(doc.created_at)}</span>
          </div>
        </div>
      </div>

      {doc.ai_analysis?.summary && (
        <p className={`text-xs leading-relaxed rounded-xl px-3 py-2 ${t('bg-slate-50 text-slate-600','bg-black/30 text-slate-400 border border-white/[0.05]')}`}>
          {doc.ai_analysis.summary}
        </p>
      )}

      <div className={`flex items-start gap-2 rounded-xl px-3 py-2 ${t('bg-blue-50 border border-blue-100','bg-blue-500/5 border border-blue-500/15')}`}>
        <p className={`text-xs leading-relaxed ${t('text-blue-700','text-blue-300')}`}>
          <span className="font-semibold">PSI suggests: </span>
          Route to <span className="font-semibold">{target?.label || doc.artifact_type}</span>
          {doc.routing_suggestion ? ` — ${doc.routing_suggestion}` : ''}
        </p>
      </div>

      {overriding && (
        <div className="space-y-2">
          <p className={`text-xs font-semibold ${t('text-slate-600','text-slate-400')}`}>Choose the correct document type:</p>
          <div className="grid grid-cols-1 gap-1.5">
            {ARTIFACT_OPTIONS.map(opt => (
              <button key={opt.value} onClick={() => setChosenType(opt.value)}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-left text-xs transition-all ${
                  chosenType === opt.value
                    ? t('bg-blue-50 border-blue-300 text-blue-800 font-semibold','bg-blue-500/15 border-blue-500/30 text-blue-300 font-semibold')
                    : t('bg-white border-slate-200 text-slate-600 hover:border-slate-300','bg-black/20 border-white/[0.06] text-slate-400 hover:border-white/[0.12]')
                }`}>
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={() => handleConfirm(chosenType)} disabled={confirming}
              className="flex-1 py-2 text-xs font-semibold rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50">
              {confirming ? 'Saving…' : 'Confirm this type'}
            </button>
            <button onClick={() => { setOverriding(false); setChosenType(doc.artifact_type); }}
              className={`px-3 py-2 text-xs rounded-xl border ${t('text-slate-500 border-slate-200 hover:bg-slate-50','text-slate-400 border-white/[0.08] hover:border-white/20')}`}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {!overriding && (
        <div className="flex gap-2 pt-0.5">
          <button onClick={() => handleConfirm(doc.artifact_type)} disabled={confirming}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50">
            {confirming ? <><Spinner size={3} className="text-white" /> Confirming…</> : 'Confirm routing'}
          </button>
          <button onClick={() => setOverriding(true)}
            className={`px-3 py-2 text-xs font-medium rounded-xl border ${t('text-slate-600 border-slate-200 hover:bg-slate-50','text-slate-400 border-white/[0.08] hover:border-white/20')}`}>
            Change type
          </button>
          {target && (target.href ? (
            <a href={target.href} target="_blank" rel="noreferrer"
              className="px-3 py-2 text-xs font-medium text-blue-600 border border-blue-200 rounded-xl hover:bg-blue-50 transition-all whitespace-nowrap">
              Open {target.label} →
            </a>
          ) : (
            <button onClick={() => onOpenApp?.(target.appKey)}
              className="px-3 py-2 text-xs font-medium text-blue-600 border border-blue-200 rounded-xl hover:bg-blue-50 transition-all whitespace-nowrap">
              Open {target.label} →
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function IntakeRoutedCard({ doc, t, onReclassify }) {
  const type   = ARTIFACT_FRIENDLY[doc.artifact_type] || { label: doc.artifact_type, icon: '📄' };
  const target = ROUTE_TARGET[doc.artifact_type];

  return (
    <div className={`rounded-xl px-4 py-3 transition-all ${t('bg-white border border-slate-200/80 hover:shadow-sm','bg-[#0d1117] border border-white/[0.06] hover:border-white/[0.10]')}`}>
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium truncate ${t('text-slate-800','text-slate-200')}`}>{doc.file_name}</p>
          <p className={`text-xs mt-0.5 ${t('text-slate-400','text-slate-500')}`}>
            Routed to <span className="font-medium">{target?.label || doc.artifact_type}</span> · {timeAgo(doc.created_at)}
          </p>
        </div>
        <ReclassifyChip
          docId={doc.id}
          currentType={doc.artifact_type}
          options={ARTIFACT_OPTIONS}
          onSaved={() => onReclassify?.()}
        />
        <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium border ${t('bg-emerald-50 text-emerald-600 border-emerald-200','bg-emerald-500/10 text-emerald-400 border-emerald-500/20')}`}>
          {doc.status === 'routed' ? 'Routed' : 'Analyzed'}
        </span>
      </div>
    </div>
  );
}

function IntakeInboxTab({ onOpenApp, t }) {
  const [intake, setIntake]   = useState(null);
  const [loading, setLoading] = useState(true);
  const intervalRef           = useRef(null);

  const loadIntake = useCallback(async () => {
    try {
      const res  = await fetch(`${API}/api/overmind/intake`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setIntake(data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    loadIntake();
    intervalRef.current = setInterval(loadIntake, 30000);
    return () => clearInterval(intervalRef.current);
  }, [loadIntake]);

  const handleConfirm = async (docId, artifactType) => {
    try {
      await fetch(`${API}/api/overmind/intake/${docId}/confirm`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ artifact_type: artifactType }),
      });
      setTimeout(loadIntake, 600);
    } catch {}
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2].map(i => (
          <div key={i} className={`rounded-2xl border p-5 animate-pulse ${t('bg-white/75 backdrop-blur-xl ring-1 ring-slate-200/60 border-transparent','bg-white/[0.03] backdrop-blur-xl ring-1 ring-white/[0.06] border-transparent')}`}>
            <div className="flex gap-3">
              <div className={`w-8 h-8 rounded-lg flex-shrink-0 ${t('bg-slate-200','bg-white/[0.04]')}`} />
              <div className="flex-1 space-y-2">
                <div className={`h-3 rounded w-1/3 ${t('bg-slate-200','bg-white/[0.04]')}`} />
                <div className={`h-4 rounded w-3/4 ${t('bg-slate-200','bg-white/[0.04]')}`} />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const processing    = intake?.processing     || [];
  const awaitingHuman = intake?.awaiting_human || [];
  const recent        = intake?.recent         || [];
  const totalPending  = processing.length + awaitingHuman.length;

  // Nothing pending and nothing recent: render nothing. The upload screen
  // sits right above this inbox, so an empty-state card only repeats it.
  if (!totalPending && !recent.length) return null;

  return (
    <div className="space-y-8">
      {processing.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Spinner size={4} className="text-blue-400" />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Processing ({processing.length})</span>
          </div>
          <div className="space-y-2">
            {processing.map(doc => (
              <div key={doc.id} className={`flex items-center gap-3 rounded-xl px-4 py-3 ${t('bg-white border border-slate-200/80','bg-[#0d1117] border border-white/[0.06]')}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${t('bg-blue-100','bg-blue-500/10 border border-blue-500/20')}`}>
                  <Spinner size={3} className="text-blue-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${t('text-slate-800','text-slate-200')}`}>{doc.file_name}</p>
                  <p className="text-xs text-blue-500 mt-0.5">PSI is analyzing this file…</p>
                </div>
                <span className={`text-xs flex-shrink-0 ${t('text-slate-400','text-slate-600')}`}>{timeAgo(doc.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {awaitingHuman.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2.5 h-2.5 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Needs Your Input ({awaitingHuman.length})</span>
            <span className="text-xs text-amber-500 ml-1">— PSI isn't certain, please confirm</span>
          </div>
          <div className="space-y-3">
            {awaitingHuman.map(doc => <IntakeDocCard key={doc.id} doc={doc} onConfirm={handleConfirm} onOpenApp={onOpenApp} t={t} />)}
          </div>
        </div>
      )}
      {recent.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 flex-shrink-0" />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Recently Routed ({recent.length})</span>
          </div>
          <div className="space-y-2">{recent.map(doc => <IntakeRoutedCard key={doc.id} doc={doc} t={t} onReclassify={loadIntake} />)}</div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PomCommandFeed — Mission control. PSI pushes work, not conversation.
// ---------------------------------------------------------------------------
function PomCommandFeed({ user, t }) {
  const [dispatches, setDispatches] = useState([]);
  const [actions, setActions] = useState([]);
  const [stats, setStats] = useState({});
  const [runs, setRuns] = useState([]);
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('dispatches'); // dispatches | actions | runs
  const [triggering, setTriggering] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, aRes, rRes, bRes] = await Promise.all([
        fetch(`${API}/api/overmind/dispatches`, { credentials: 'include' }),
        fetch(`${API}/api/overmind/actions?limit=20`, { credentials: 'include' }),
        fetch(`${API}/api/overmind/runs`, { credentials: 'include' }),
        fetch(`${API}/api/overmind/intelligence`, { credentials: 'include' }),
      ]);
      const [dData, aData, rData, bData] = await Promise.all([dRes.json(), aRes.json(), rRes.json(), bRes.json()]);
      if (dData.success) { setDispatches(dData.dispatches || []); setStats(dData.stats || {}); }
      if (aData.success) setActions(aData.actions || []);
      if (rData.success) setRuns(rData.runs || []);
      if (bData.success && bData.briefing) setBriefing(bData.briefing);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); const id = setInterval(load, 60000); return () => clearInterval(id); }, [load]);

  const handleDispatchAction = async (id, action) => {
    try {
      await fetch(`${API}/api/overmind/dispatches/${id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      load();
    } catch {}
  };

  const handleTriggerRun = async () => {
    setTriggering(true);
    try {
      await fetch(`${API}/api/overmind/dispatch/run`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ run_type: 'manual' }),
      });
      setTimeout(load, 5000);
    } catch {}
    setTriggering(false);
  };

  const priorityColors = {
    critical: t('bg-red-50 border-red-200 text-red-800', 'bg-red-500/10 border-red-500/20 text-red-400'),
    high: t('bg-amber-50 border-amber-200 text-amber-800', 'bg-amber-500/10 border-amber-500/20 text-amber-400'),
    normal: t('bg-blue-50 border-blue-200 text-blue-800', 'bg-blue-500/10 border-blue-500/20 text-blue-400'),
    low: t('bg-slate-50 border-slate-200 text-slate-600', 'bg-slate-500/10 border-slate-500/20 text-slate-400'),
  };

  const categoryIcons = {
    overdue: '!!', escalation: '^', needs_input: '?', anomaly: '~',
    fdr_alert: 'F', screening_result: 'S', new_assignment: '+', due_today: '!',
    due_this_week: '.', system_alert: '*',
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const diffH = Math.round((now - d) / 3600000);
    if (diffH < 1) return 'just now';
    if (diffH < 24) return `${diffH}h ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* AI Briefing */}
      {briefing && (
        <div className={`mb-4 rounded-xl p-5 border ${t('bg-gradient-to-br from-slate-50 to-orange-50/50 border-orange-100', 'bg-gradient-to-br from-orange-900/10 to-amber-900/10 border-orange-500/20')}`}>
          <div className="flex items-start gap-3 mb-3">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-orange-600 to-amber-500 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-bold">P</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className={`font-bold text-sm ${t('text-slate-900','text-white')}`}>{briefing.headline}</div>
            </div>
          </div>
          {briefing.priority_items?.length > 0 && (
            <div className="space-y-2 ml-10 mb-3">
              {briefing.priority_items.map((item, i) => (
                <div key={i} className={`text-sm ${t('text-slate-700','text-slate-300')}`}>
                  <span className="font-semibold">{item.title}</span>
                  <span className={`mx-1 ${t('text-slate-400','text-slate-500')}`}>—</span>
                  <span className={t('text-slate-500','text-slate-400')}>{item.reason}</span>
                  {item.action && <div className={`text-xs mt-0.5 font-medium ${t('text-blue-600','text-blue-400')}`}>{item.action}</div>}
                </div>
              ))}
            </div>
          )}
          {(briefing.pattern_insight || briefing.proactive_recommendation) && (
            <div className={`ml-10 pt-2 border-t text-xs space-y-1 ${t('border-blue-100 text-slate-500','border-blue-500/20 text-slate-400')}`}>
              {briefing.pattern_insight && <div>{briefing.pattern_insight}</div>}
              {briefing.proactive_recommendation && <div className={`font-medium ${t('text-blue-600','text-blue-400')}`}>{briefing.proactive_recommendation}</div>}
            </div>
          )}
        </div>
      )}

      {/* Stats bar */}
      <div className={`flex-shrink-0 flex items-center gap-3 px-4 py-3 mb-4 rounded-xl ${t('bg-slate-50', 'bg-white/[0.03]')}`}>
        <div className="flex items-center gap-4 flex-1 flex-wrap">
          {[
            { label: 'Active', value: stats.active || 0, color: t('text-slate-900','text-white') },
            { label: 'Critical', value: stats.critical || 0, color: 'text-red-500' },
            { label: 'Overdue', value: stats.overdue || 0, color: 'text-amber-500' },
            { label: 'Needs Input', value: stats.needs_input || 0, color: 'text-blue-500' },
            { label: 'Escalated', value: stats.escalations || 0, color: 'text-amber-500' },
            { label: 'Done Today', value: stats.completed_today || 0, color: 'text-emerald-500' },
          ].map((s, i) => (
            <div key={i} className="text-center">
              <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
              <div className={`text-[10px] font-medium uppercase tracking-wider ${t('text-slate-400','text-slate-500')}`}>{s.label}</div>
            </div>
          ))}
        </div>
        <button onClick={handleTriggerRun} disabled={triggering}
          className={`px-4 py-2 text-xs font-semibold rounded-lg transition-all ${t('bg-slate-900 text-white hover:bg-slate-800','bg-white/10 text-white hover:bg-white/20')} disabled:opacity-50`}>
          {triggering ? 'Running...' : 'Run Now'}
        </button>
      </div>

      {/* Sub-tabs */}
      <div className="flex-shrink-0 flex gap-1 mb-4">
        {[
          { key: 'dispatches', label: 'Your Items' },
          { key: 'actions', label: 'PSI Actions' },
          { key: 'runs', label: 'Run History' },
        ].map(tab => (
          <button key={tab.key} onClick={() => setView(tab.key)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
              view === tab.key
                ? t('bg-slate-900 text-white', 'bg-white/10 text-white')
                : t('text-slate-500 hover:text-slate-700', 'text-slate-500 hover:text-slate-300')
            }`}>{tab.label}</button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto space-y-2 px-1 pb-4">
        {loading && dispatches.length === 0 && (
          <div className={`text-center py-16 ${t('text-slate-400','text-slate-500')}`}>
            <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-gradient-to-br from-orange-500/20 to-amber-500/20 flex items-center justify-center animate-pulse">
              <span className="text-lg">P</span>
            </div>
            Loading...
          </div>
        )}

        {/* Dispatches view */}
        {view === 'dispatches' && dispatches.length === 0 && !loading && (
          <div className={`text-center py-16 ${t('text-slate-400','text-slate-500')}`}>
            <div className={`w-12 h-12 mx-auto mb-3 rounded-xl ${t('bg-emerald-50','bg-emerald-500/10')} flex items-center justify-center`}>
              <svg className="w-6 h-6 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
            </div>
            <div className="font-semibold">All clear</div>
            <div className="text-sm mt-1">No active items. PSI will notify you when something needs attention.</div>
          </div>
        )}

        {view === 'dispatches' && dispatches.map(d => (
          <div key={d.id} className={`border rounded-xl p-4 transition-all hover:shadow-sm ${priorityColors[d.priority] || priorityColors.normal}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold ${
                    d.priority === 'critical' ? 'bg-red-500 text-white' :
                    d.priority === 'high' ? 'bg-amber-500 text-white' :
                    t('bg-slate-200 text-slate-600','bg-white/10 text-slate-400')
                  }`}>{categoryIcons[d.category] || '.'}</span>
                  <span className="text-xs font-medium uppercase tracking-wider opacity-60">{d.source_module} / {d.category?.replace(/_/g, ' ')}</span>
                  <span className="text-xs opacity-40">{formatTime(d.created_at)}</span>
                </div>
                <div className="font-semibold text-sm leading-snug">{d.title}</div>
                {d.escalated_to_name && (
                  <div className="text-xs mt-1 opacity-60">Escalated to {d.escalated_to_name}</div>
                )}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => handleDispatchAction(d.id, 'completed')}
                  className={`p-1.5 rounded-lg transition-all ${t('hover:bg-emerald-100','hover:bg-emerald-500/20')}`} title="Complete">
                  <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                </button>
                <button onClick={() => handleDispatchAction(d.id, 'dismissed')}
                  className={`p-1.5 rounded-lg transition-all ${t('hover:bg-slate-100','hover:bg-white/10')}`} title="Dismiss">
                  <svg className="w-4 h-4 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* Actions view */}
        {view === 'actions' && actions.map(a => (
          <div key={a.id} className={`border rounded-xl p-3 ${t('bg-white border-slate-100','bg-white/[0.02] border-white/[0.06]')}`}>
            <div className="flex items-start gap-3">
              <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                a.action_type === 'deadline_alert' ? 'bg-red-500' :
                a.action_type === 'work_escalated' ? 'bg-amber-500' :
                a.action_type === 'cross_module_link' ? 'bg-blue-500' :
                a.action_type === 'anomaly_detected' ? 'bg-amber-500' :
                a.action_type === 'screening_completed' ? 'bg-emerald-500' :
                'bg-slate-400'
              }`} />
              <div className="flex-1 min-w-0">
                <div className={`text-sm leading-snug ${t('text-slate-700','text-slate-300')}`}>{a.action_summary}</div>
                <div className="flex items-center gap-3 mt-1">
                  <span className={`text-xs ${t('text-slate-400','text-slate-500')}`}>{a.target_module}</span>
                  {a.target_user_name && <span className={`text-xs ${t('text-slate-400','text-slate-500')}`}>{a.target_user_name}</span>}
                  <span className={`text-xs ${t('text-slate-400','text-slate-500')}`}>{formatTime(a.created_at)}</span>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Runs view */}
        {view === 'runs' && runs.map(r => (
          <div key={r.id} className={`border rounded-xl p-4 ${t('bg-white border-slate-100','bg-white/[0.02] border-white/[0.06]')}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${
                  r.status === 'completed' ? t('bg-emerald-50 text-emerald-700','bg-emerald-500/10 text-emerald-400') :
                  r.status === 'failed' ? t('bg-red-50 text-red-700','bg-red-500/10 text-red-400') :
                  t('bg-blue-50 text-blue-700','bg-blue-500/10 text-blue-400')
                }`}>{r.status}</span>
                <span className={`text-sm font-semibold ${t('text-slate-700','text-slate-300')}`}>{r.run_type} run</span>
              </div>
              <span className={`text-xs ${t('text-slate-400','text-slate-500')}`}>{formatTime(r.started_at)}</span>
            </div>
            <div className="flex items-center gap-4 text-xs">
              {[
                { label: 'Scanned', value: r.items_scanned },
                { label: 'Actions', value: r.actions_taken },
                { label: 'Dispatches', value: r.dispatches_sent },
                { label: 'Escalations', value: r.escalations },
              ].map((s, i) => (
                <div key={i} className={t('text-slate-500','text-slate-400')}>
                  <span className={`font-bold ${t('text-slate-700','text-slate-300')}`}>{s.value || 0}</span> {s.label}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// IngestTab
// ---------------------------------------------------------------------------
function IngestTab({ user, t }) {
  // Default to 'memo' but auto-correct below to the first option the
  // user can actually submit if they don't have the memo role.
  const [artifactType, setArtifactType] = useState('memo');
  const [uploadFile, setUploadFile]     = useState(null);
  // A regulatory event often arrives as several files that are ONE memo.
  // uploadFile stays as the primary, so every existing reference still works.
  const [uploadFiles, setUploadFiles]   = useState([]);
  const [uploading, setUploading]       = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [documents, setDocuments]       = useState([]);
  const [docsLoading, setDocsLoading]   = useState(true);
  const [dragOver, setDragOver]         = useState(false);
  const fileInputRef                    = useRef(null);

  const loadDocuments = useCallback(async () => {
    try {
      const res  = await fetch(`${API}/api/overmind/documents?limit=10`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setDocuments(data.documents || []);
    } catch {}
    setDocsLoading(false);
  }, []);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  // Re-poll the documents list while the tab is visible so the "Analyzing"
  // badge flips to "Ready" without the user having to refresh. Cheap (one
  // small JSON read per 30s) and paused while the tab is backgrounded.
  useEffect(() => {
    let id = null;
    const start = () => { if (!id) id = setInterval(loadDocuments, 30_000); };
    const stop  = () => { if (id) { clearInterval(id); id = null; } };
    const onVis = () => {
      if (typeof document === 'undefined') return;
      if (document.hidden) stop(); else { loadDocuments(); start(); }
    };
    if (typeof document === 'undefined' || !document.hidden) start();
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVis);
    return () => {
      stop();
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVis);
    };
  }, [loadDocuments]);

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files || []);
    if (dropped.length) { setUploadFiles(dropped); setUploadFile(dropped[0]); }
  };

  // overrideDedup=true tells the server to ingest even though the
  // content hash already matches an existing document (Phase 1).
  //
  // Async memo path (server returns analysis_status === 'analyzing'):
  //   - Clear the file picker immediately so the form is ready for the next
  //     upload while Mandate runs analysis in the background.
  //   - Show a calm one-line acknowledgement in place of the full analysis
  //     panel — the heavy result card will appear later, surfaced via the
  //     in-app notification toast when Mandate finishes.
  //   - The "Recent Uploads" list already shows the doc with status
  //     'analyzing' (server inserts it that way at /ingest); when the
  //     notification arrives the next poll cycle flips it to 'analyzed'.
  const handleUpload = async (overrideDedup = false) => {
    if (!uploadFile) return;
    setUploading(true); setUploadResult(null);

    // Several files that belong to one regulatory event go to the bundle
    // endpoint, which keeps them together as ONE memo. A single file takes
    // the path it has always taken, unchanged.
    const many = uploadFiles.length > 1;
    if (many) {
      const bundle = new FormData();
      uploadFiles.forEach((f) => bundle.append('files', f));
      bundle.append('artifact_type', artifactType);
      try {
        const res  = await fetch(`${API}/api/overmind/ingest-bundle`, {
          method: 'POST', credentials: 'include', body: bundle,
        });
        const data = await res.json();
        setUploadResult(data);
        if (data.success) { setUploadFile(null); setUploadFiles([]); loadDocuments(); }
      } catch {
        setUploadResult({ success: false, message: 'Upload failed. Please try again.' });
      }
      setUploading(false);
      return;
    }

    const form = new FormData();
    form.append('file', uploadFile);
    form.append('artifact_type', artifactType);
    if (overrideDedup) form.append('override_dedup', 'true');
    try {
      const res  = await fetch(`${API}/api/overmind/ingest`, { method: 'POST', credentials: 'include', body: form });
      const data = await res.json();
      setUploadResult(data);
      // Only clear the file picker when we actually ingested. If the
      // server returned a duplicate banner the user may want to keep
      // the file selected so they can override-and-retry. On the async
      // memo path the analysis hasn't completed yet but we still clear
      // the picker — the user can start another upload right away.
      if (data.success && !data.duplicate) { setUploadFile(null); loadDocuments(); }
    } catch { setUploadResult({ success: false, message: 'Upload failed. Please try again.' }); }
    setUploading(false);
  };

  // Artifact-type picker — grouped by *what the user is doing*, not by
  // internal module naming. The `value` field is the backend artifact_type
  // (matches /api/overmind/ingest contract — DO NOT rename: changing it
  // would break routing AND the SOC 2 audit log enum). User-facing
  // `label`, `desc`, and `tip` are display-only.
  //
  // `requires` is a client-side role filter so a user only sees options
  // they can actually submit. The server still enforces the same gates
  // in routes/overmind.js — this is UX, not security.
  // TYPE-FIRST: an explicit compliance_officer type decides, the legacy role
  // column is fallback only. Solis officers carry role 'superuser' from first
  // provisioning and lost their app_roles rows in migration 315; reading the
  // legacy fields left them with one document type. The server gate on
  // /ingest only restricts role 'associate', so this is display, not access.
  const isComplianceOfficer = user?.user_type === 'compliance_officer' || user?.effective_user_type === 'compliance_officer' || user?.can_see_privacy === true;
  const isSuperOrAdmin = isComplianceOfficer || user?.role === 'superadmin' || user?.role === 'admin';
  const hasRole = (key) => {
    if (isSuperOrAdmin) return true;
    if (key === 'memo')     return !!user?.app_roles?.memo;
    if (key === 'cap')      return !!user?.app_roles?.cap;
    if (key === 'scrubber') return !!user?.app_roles?.scrubber;
    if (key === 'privacy')  return user?.can_see_privacy === true;
    return false;
  };

  const artifactGroups = [
    {
      heading: 'Day-to-day compliance documents',
      caption: 'Memos and audit reports your team handles every week.',
      options: [
        {
          value: 'memo', icon: '📄',
          label: 'Regulatory Memo',
          desc: 'CMS guidance, HPMS memos, policy updates',
          tip:  'Goes to Mandate Tracker. PSI extracts requirements, assigns departments, and tracks sign-off.',
          requires: 'memo',
        },
        {
          value: 'audit_report', icon: '🔍',
          label: 'Audit Report / Findings',
          desc: 'Program audits, exam reports, deficiency letters',
          tip:  'Goes to CAP. PSI extracts findings and creates a corrective action plan with milestones.',
          requires: 'cap',
        },
      ],
    },
    {
      heading: 'CMS audit submissions',
      caption: 'Files you prepare when CMS requests a program-audit sample. Skip this group unless you are mid-audit.',
      options: [
        {
          value: 'universe', icon: '📊',
          label: 'CMS Data Submission (Universe)',
          desc: 'Raw data file CMS asked you to produce — claims, members, providers, etc.',
          tip:  'A "universe" is the CMS term for the raw dataset (e.g. all denials in a date range). PSI checks counts, dates, and reconciliation against your protocol.',
          requires: 'scrubber',
        },
        {
          value: 'protocol_file', icon: '📋',
          label: 'CMS Audit Protocol Element',
          desc: 'ODAG, CDAG, FA, SNPCC, CPE element file',
          tip:  'The protocol document that tells Scrubber which elements to validate the universe against. Submit the protocol once per audit, then the universes.',
          requires: 'scrubber',
        },
        {
          value: 'na_file', icon: '🌐',
          label: 'Network Adequacy File',
          desc: 'HSD tables, provider directories, time-distance data',
          tip:  'Goes to the Network Adequacy module. PSI runs CMS time-distance checks and flags coverage gaps.',
          requires: 'privacy',
        },
      ],
    },
  ];

  // Filter each group's options by what the user can submit, then drop empty groups.
  const visibleGroups = artifactGroups
    .map(g => ({ ...g, options: g.options.filter(o => hasRole(o.requires)) }))
    .filter(g => g.options.length > 0);

  // If the default selected value isn't one the user can submit, switch
  // to the first option they CAN submit. Runs once when the visible set
  // changes (e.g. user role flip after a refresh).
  useEffect(() => {
    const flat = visibleGroups.flatMap(g => g.options.map(o => o.value));
    if (flat.length > 0 && !flat.includes(artifactType)) {
      setArtifactType(flat[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role, user?.app_roles?.memo, user?.app_roles?.cap, user?.app_roles?.scrubber, user?.can_see_privacy]);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <PhiFriendlyNote variant="ingest" />
      <div>
        <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${t('text-slate-600','text-slate-500')}`}>What are you uploading?</p>
        {visibleGroups.length === 0 && (
          <div className={`rounded-xl border p-4 text-sm ${t('bg-amber-50 border-amber-200 text-amber-800','bg-amber-500/5 border-amber-500/20 text-amber-300')}`}>
            You don't have access to any document upload types. Ask your admin to grant you a role.
          </div>
        )}
        {visibleGroups.map((group, gi) => (
          <div key={group.heading} className={gi > 0 ? 'mt-5' : ''}>
            <div className="mb-2">
              <p className={`text-[11px] font-bold uppercase tracking-wider ${t('text-slate-500','text-slate-400')}`}>{group.heading}</p>
              <p className={`text-[11px] mt-0.5 ${t('text-slate-400','text-slate-500')}`}>{group.caption}</p>
            </div>
            <div className="grid grid-cols-1 gap-2">
              {group.options.map(opt => (
                <button key={opt.value} onClick={() => setArtifactType(opt.value)} title={opt.tip}
                  className={`flex items-start gap-3 p-3 rounded-xl border text-left transition-all ${
                    artifactType === opt.value
                      ? t('bg-blue-50 border-blue-200 shadow-sm','bg-blue-500/10 border-blue-500/30')
                      : t('bg-white border-slate-200 hover:border-slate-300','bg-white/[0.03] backdrop-blur-xl ring-1 ring-white/[0.06] border-transparent hover:border-white/[0.12]')
                  }`}>
                  <div className={`w-4 h-4 rounded-full border-2 mt-0.5 flex-shrink-0 flex items-center justify-center ${
                    artifactType === opt.value ? 'border-blue-500 bg-blue-500' : t('border-slate-300','border-slate-600')
                  }`}>{artifactType === opt.value && <div className="w-2 h-2 rounded-full bg-white" />}</div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${t('text-slate-800','text-slate-200')}`}>{opt.label}</p>
                    <p className={`text-xs mt-0.5 ${t('text-slate-400','text-slate-500')}`}>{opt.desc}</p>
                    {artifactType === opt.value && (
                      <p className={`text-[11px] mt-2 leading-relaxed ${t('text-blue-700','text-blue-200/80')}`}>
                        <span className="font-semibold">What happens next:</span> {opt.tip}
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div>
        <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${t('text-slate-600','text-slate-500')}`}>Upload File</p>
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !uploadFile && fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all duration-200 ${
            dragOver ? 'border-blue-400 bg-blue-50' :
            uploadFile ? t('border-green-300 bg-green-50 cursor-default','border-emerald-500/40 bg-emerald-500/5 cursor-default') :
            t('border-slate-300 bg-slate-50 hover:border-slate-400 hover:bg-white','border-white/[0.08] bg-[#0d1117] hover:border-white/[0.16]')
          }`}>
          {uploadFile ? (
            <div className="space-y-2">
              <div className="text-3xl">📎</div>
              {uploadFiles.length > 1 ? (
                <>
                  <p className={`font-medium text-sm ${t('text-slate-800','text-slate-200')}`}>
                    {uploadFiles.length} files, one memo
                  </p>
                  {/* Say up front which files will be read and which are only
                      carried, so nobody assumes a spreadsheet was understood. */}
                  <div className="space-y-0.5 text-left inline-block">
                    {uploadFiles.map((f, i) => {
                      const willRead = /\.pdf$/i.test(f.name);
                      return (
                        <p key={i} className={`text-xs ${t('text-slate-600','text-slate-400')}`}>
                          {f.name}
                          <span className={willRead ? 'text-emerald-600 ml-2' : 'text-slate-400 ml-2'}>
                            {willRead ? 'will be read' : 'attached, not analyzed'}
                          </span>
                        </p>
                      );
                    })}
                  </div>
                </>
              ) : (
                <>
                  <p className={`font-medium text-sm ${t('text-slate-800','text-slate-200')}`}>{uploadFile.name}</p>
                  <p className={`text-xs ${t('text-slate-400','text-slate-500')}`}>{formatFileSize(uploadFile.size)}</p>
                </>
              )}
              <button onClick={(e) => { e.stopPropagation(); setUploadFile(null); setUploadFiles([]); setUploadResult(null); }}
                className="text-xs text-red-500 hover:text-red-600 mt-1">Remove</button>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-3xl opacity-60">⬆️</div>
              <p className={`text-sm font-medium ${t('text-slate-600','text-slate-400')}`}>Drag files here, or click to select</p>
              <p className={`text-xs ${t('text-slate-400','text-slate-600')}`}>PDF, Excel, or CSV, max 20 MB each</p>
              <p className={`text-xs ${t('text-slate-400','text-slate-600')}`}>
                Select several files when one regulatory event arrives as more than one document. PDFs are read together as one memo; spreadsheets ride along.
              </p>
            </div>
          )}
        </div>
        <input ref={fileInputRef} type="file" multiple accept=".pdf,.xlsx,.xls,.csv" className="hidden"
          onChange={(e) => {
            const picked = Array.from(e.target.files || []);
            if (picked.length) { setUploadFiles(picked); setUploadFile(picked[0]); }
          }} />
      </div>

      <button onClick={() => handleUpload(false)} disabled={!uploadFile || uploading}
        className="w-full flex items-center justify-center gap-2 py-3 px-6 bg-gradient-to-r from-orange-600 to-amber-500 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-blue-600/20">
        {uploading ? <><Spinner size={4} className="text-white" /> PSI is analyzing…</> : 'Analyze with PSI'}
      </button>

      {/* Phase 1: dedup banner — shown when the server detected a
          content-hash match. Two CTAs: open the existing record, or
          ingest a new copy anyway. The "Override" path is captured in
          metrics_events (DOC_DEDUP_OVERRIDDEN) for SOC 2 evidence. */}
      {uploadResult?.duplicate && uploadResult?.existing_document && (
        <div className={`rounded-2xl border p-5 ${t('bg-amber-50 border-amber-200','bg-amber-500/5 border-amber-500/20')}`}>
          <div className="flex items-start gap-3">
            <span className="text-xl flex-shrink-0">⚠️</span>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold ${t('text-amber-900','text-amber-300')}`}>
                This document has already been ingested
              </p>
              <p className={`text-xs mt-1 ${t('text-amber-800','text-amber-300/80')}`}>
                <span className="font-medium">{uploadResult.existing_document.file_name}</span>
                {uploadResult.existing_document.status && (
                  <span> · status: {uploadResult.existing_document.status}</span>
                )}
                {uploadResult.existing_document.created_at && (
                  <span> · received {new Date(uploadResult.existing_document.created_at).toLocaleDateString()}</span>
                )}
              </p>
              <p className={`text-xs mt-2 ${t('text-amber-800','text-amber-300/80')}`}>
                PSI matched on content fingerprint — the file bytes are identical even though the filename may differ. Open the existing record to continue with that copy, or override to ingest this upload as a separate document anyway.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <button
                  onClick={() => loadDocuments()}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-lg ${t('bg-white border border-amber-300 text-amber-900 hover:bg-amber-100','bg-amber-500/10 border border-amber-500/30 text-amber-200 hover:bg-amber-500/20')}`}
                >
                  View existing record
                </button>
                <button
                  onClick={() => handleUpload(true)}
                  disabled={uploading}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-lg ${t('bg-amber-600 text-white hover:bg-amber-700','bg-amber-500 text-white hover:bg-amber-600')} disabled:opacity-50`}
                >
                  {uploading ? 'Working…' : 'Override and process as new'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Async memo path — Mandate accepted the file and is analyzing in the
          background. Show one calm line, no spinner, no analysis card; the
          full result surfaces later via the in-app notification toast. */}
      {uploadResult && !uploadResult.duplicate && uploadResult.success && uploadResult.analysis_status === 'analyzing' && (
        <div className={`rounded-2xl border p-4 ${t('bg-slate-50 border-slate-200','bg-white/[0.02] border-white/[0.06]')}`}>
          <p className={`text-sm ${t('text-slate-700','text-slate-300')}`}>
            Uploaded. We will let you know when it is ready.
          </p>
        </div>
      )}

      {uploadResult && !uploadResult.duplicate && uploadResult.analysis_status !== 'analyzing' && (
        <div className={`rounded-2xl border p-5 ${uploadResult.success
          ? t('bg-green-50 border-green-200','bg-emerald-500/5 border-emerald-500/20')
          : t('bg-red-50 border-red-200','bg-red-500/5 border-red-500/20')}`}>
          {uploadResult.success ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-lg">✅</span>
                <p className={`font-semibold text-sm ${t('text-slate-800','text-slate-200')}`}>PSI analyzed your document</p>
              </div>
              {uploadResult.analysis?.summary && (
                <p className={`text-sm leading-relaxed ${t('text-slate-700','text-slate-300')}`}>{uploadResult.analysis.summary}</p>
              )}
              {uploadResult.analysis?.key_findings?.length > 0 && (
                <div>
                  <p className={`text-xs font-semibold mb-1.5 uppercase tracking-wide ${t('text-slate-600','text-slate-500')}`}>Key Findings</p>
                  <ul className="space-y-1">
                    {uploadResult.analysis.key_findings.slice(0, 5).map((f, i) => (
                      <li key={i} className={`flex items-start gap-2 text-xs ${t('text-slate-600','text-slate-300')}`}>
                        <span className="text-blue-500 font-bold flex-shrink-0">•</span><span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {uploadResult.analysis?.action_items?.length > 0 && (
                <div>
                  <p className={`text-xs font-semibold mb-1.5 uppercase tracking-wide ${t('text-slate-600','text-slate-500')}`}>Action Items</p>
                  <ul className="space-y-1">
                    {uploadResult.analysis.action_items.slice(0, 4).map((a, i) => (
                      <li key={i} className={`flex items-start gap-2 text-xs ${t('text-slate-600','text-slate-300')}`}>
                        <span className="text-amber-500 font-bold flex-shrink-0 mt-px">→</span>
                        <span>{a.description}{a.due_date ? ` (due: ${a.due_date})` : ''}{a.department ? ` — ${a.department}` : ''}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {uploadResult.routing_suggestion && (
                <div className={`rounded-xl p-3 ${t('bg-blue-50 border border-blue-100','bg-blue-500/5 border border-blue-500/15')}`}>
                  <p className={`text-xs font-semibold mb-0.5 ${t('text-blue-700','text-blue-400')}`}>PSI's routing suggestion</p>
                  <p className={`text-xs ${t('text-blue-600','text-blue-300')}`}>{uploadResult.routing_suggestion}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-start gap-2">
              <span className="text-lg">❌</span>
              <p className={`text-sm ${t('text-red-700','text-red-400')}`}>{uploadResult.message || 'Upload failed.'}</p>
            </div>
          )}
        </div>
      )}

      {!docsLoading && documents.length > 0 && (
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${t('text-slate-600','text-slate-500')}`}>Recent Uploads</p>
          <div className="space-y-2">
            {documents.map(doc => (
              <div key={doc.id} className={`flex items-start gap-3 rounded-xl border px-4 py-3 transition-all ${t('bg-white/75 backdrop-blur-xl ring-1 ring-slate-200/60 border-transparent hover:shadow-sm','bg-white/[0.03] backdrop-blur-xl ring-1 ring-white/[0.06] border-transparent hover:border-white/[0.10]')}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <p className={`text-sm font-medium truncate ${t('text-slate-800','text-slate-200')}`}>{doc.file_name}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${
                      doc.status === 'analyzed' || doc.status === 'routed'
                        ? t('bg-green-100 text-green-700 border-green-200','bg-emerald-500/10 text-emerald-400 border-emerald-500/20')
                        : doc.status === 'failed'
                        ? t('bg-red-100 text-red-700 border-red-200','bg-red-500/10 text-red-400 border-red-500/20')
                        : t('bg-slate-100 text-slate-600 border-slate-200','bg-slate-500/10 text-slate-400 border-slate-500/20')
                    }`}>{
                      // Friendly labels — the raw status field is useful for
                      // engineers but reads cold to the user. 'analyzing'
                      // shows up while Mandate is running async pipeline;
                      // it flips to 'analyzed'/'routed' once finished.
                      doc.status === 'analyzing' ? 'Analyzing'
                        : doc.status === 'analyzed' || doc.status === 'routed' ? 'Ready'
                        : doc.status === 'failed' ? 'Failed'
                        : doc.status
                    }</span>
                  </div>
                  <p className={`text-xs ${t('text-slate-400','text-slate-500')}`}>
                    {ARTIFACT_TYPE_LABELS[doc.artifact_type] || doc.artifact_type}
                    {doc.uploaded_by_name ? ` · ${doc.uploaded_by_name}` : ''} · {timeAgo(doc.created_at)}
                  </p>
                  {doc.routing_suggestion && <p className="text-xs text-blue-500 mt-1 italic">{doc.routing_suggestion}</p>}
                  {/* Agent thinking panel — visible only while the document
                      is still being processed by PSI agents. Polls the
                      agent_progress_events table for ordered checkpoints. */}
                  {doc.status === 'analyzing' && (
                    <DocumentAgentActivity docId={doc.id} />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DocumentAgentActivity — small wrapper that polls /api/agent-progress for an
// in-flight document and renders the AgentThinkingPanel. Used in the Recent
// Uploads list so the user can see what PSI is doing on their behalf.
// ---------------------------------------------------------------------------
function DocumentAgentActivity({ docId }) {
  const { events, status } = useAgentProgress('pom_document', docId);
  if (!events || events.length === 0) return null;
  return <AgentThinkingPanel events={events} status={status} title="Activity" />;
}

// ---------------------------------------------------------------------------
// Open a priority item where the work actually lives. An item carrying an
// internal deep-link ("/app?app=...") navigates straight to it — a mandate
// item lands on ITS memo inside native Precision Mandate, never the archived
// external app (whose subdomain now 404s) and never a bare dashboard. Rows
// written before the native cutover may still say app=memo; that key is
// rewritten to mandate_native so no click can reach the dead subdomain.
// Items without a deep-link fall back to app-level navigation.
// ---------------------------------------------------------------------------
export function openPriorityItem(item, onOpenApp) {
  const url = String(item?.action_url || '').replace(/^\/app\?app=memo(?=&|$)/, '/app?app=mandate_native');
  if (url.startsWith('/app?app=')) {
    try { window.location.assign(url); } catch { /* fall through */ }
    window.location.href = url;
    return;
  }
  onOpenApp?.(item.source_app);
}

// ---------------------------------------------------------------------------
// Plain words for kinds of work. "2 memos, 1 policy, 1 vendor review" reads;
// "memo: 2, ppm: 1, fdr: 1" does not.
// ---------------------------------------------------------------------------
const KIND_NOUNS = {
  memo: ['memo', 'memos'], ppm: ['policy', 'policies'], fdr: ['vendor review', 'vendor reviews'],
  cap: ['CAP milestone', 'CAP milestones'], cil: ['issue', 'issues'], risk: ['risk', 'risks'],
  priq: ['rule change', 'rule changes'], network_adequacy: ['network item', 'network items'],
  na: ['network item', 'network items'], privacy: ['privacy item', 'privacy items'],
  coa: ['COA measure', 'COA measures'], sentinel: ['alert', 'alerts'], calendar: ['obligation', 'obligations'],
  ai_command: ['AI item', 'AI items'],
};
function describeKinds(list, max = 3) {
  const counts = new Map();
  for (const i of list) counts.set(i.source_app, (counts.get(i.source_app) || 0) + 1);
  const parts = [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([app, n]) => {
    const nouns = KIND_NOUNS[app] || [(APP_LABELS[app] || app || 'item').toLowerCase() + ' item', (APP_LABELS[app] || app || 'item').toLowerCase() + ' items'];
    return `${n} ${n === 1 ? nouns[0] : nouns[1]}`;
  });
  if (parts.length <= max) return parts;
  const shown = parts.slice(0, max - 1);
  const restN = [...counts.values()].slice(max - 1).reduce((a, b) => a + b, 0);
  return [...shown, `${restN} more`];
}
function prettyFileName(name) {
  return String(name || 'Document').replace(/\.[A-Za-z0-9]{2,5}$/, '').replace(/[_\-]+/g, ' ').replace(/\s+/g, ' ').trim() || 'Document';
}

// One row of the Today list: severity, title, one plain sentence, tag, due.
// The lead ("Overdue by 9 days.") is bold only on the Start here rows, where
// the sentence carries the decision; in the time bands the band already says it.
function TodayRow({ item, onSelect, t, hero = false }) {
  const dueInfo = formatDueDate(item.due_date);
  const d = item.due_date ? Math.ceil((new Date(item.due_date) - Date.now()) / 86400000) : null;
  const lead = d === null ? '' : d < 0 ? `Overdue by ${-d} day${-d === 1 ? '' : 's'}.` : d === 0 ? 'Due today.' : d <= 14 ? `Due in ${d} day${d === 1 ? '' : 's'}.` : '';
  const why = String(item.why || '').trim();
  const sentence = why || humanizeContext(item.context) || '';
  const sev = item.urgency_level === 'critical' ? 'bg-red-500 shadow-[0_0_0_3px_rgba(220,38,38,0.14)]'
    : item.urgency_level === 'high' ? 'bg-amber-500' : item.urgency_level === 'low' ? t('bg-slate-300','bg-slate-600') : 'bg-blue-500';
  const color = APP_TAG_COLORS[item.source_app] || '#64748b';
  return (
    <div role="button" tabIndex={0}
      onClick={() => onSelect?.(item)}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect?.(item)}
      className={`group grid grid-cols-[14px_minmax(0,1fr)_auto] gap-3 items-center px-4 ${hero ? 'py-3.5' : 'py-3'} cursor-pointer text-left border-t first:border-t-0 transition-colors ${t('border-slate-200/80 hover:bg-slate-50','border-white/[0.06] hover:bg-white/[0.04]')}`}>
      <span className={`w-2 h-2 rounded-full justify-self-center ${sev}`} aria-hidden />
      <span className="min-w-0">
        <span className={`block text-[13.5px] font-semibold truncate ${t('text-slate-900','text-slate-100')}`}>{item.title}</span>
        {(lead || sentence) && (
          <span className={`block text-[12px] truncate mt-0.5 ${t('text-slate-500','text-slate-500')}`}>
            {hero && lead ? <b className={`font-semibold ${t('text-slate-700','text-slate-300')}`}>{lead} </b> : null}
            {sentence || (!hero ? lead : '')}
          </span>
        )}
      </span>
      <span className="flex items-center gap-2.5">
        <span className="text-[11px] font-bold px-2 py-[3px] rounded-full whitespace-nowrap" style={{ backgroundColor: `${color}1a`, color }}>
          {APP_LABELS[item.source_app] || item.source_app}
        </span>
        <span className={`w-[82px] text-right text-[12px] font-semibold whitespace-nowrap ${dueInfo ? dueInfo.color : t('text-slate-300','text-slate-600')}`}>{dueInfo ? dueInfo.text.replace('Overdue by ', 'Overdue ') : ''}</span>
        <span className={`text-[12px] font-semibold opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity ${t('text-[#E5601F]','text-orange-400')}`}>Open</span>
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// OracleHome — main
// ---------------------------------------------------------------------------
export default function OracleHome({ user, onSelectApp }) {
  const { theme, toggle, t } = useTheme();
  // Phase E: the agent beside the user. Off unless the agent is switched on for this person.
  const [agentHome, setAgentHome] = useState(null);
  useEffect(() => {
    let dead = false;
    fetch(`${API}/api/agent/status`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : null)).then((d) => { if (!dead) setAgentHome(d?.success && d.enabled ? d : null); }).catch(() => {});
    return () => { dead = true; };
  }, []);
  const ag = useAgentProposals(!!agentHome);
  const [items, setItems]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [syncing, setSyncing]           = useState(false);
  const [syncResult, setSyncResult]     = useState(null);
  const [filter, setFilter]             = useState('all');
  const [error, setError]               = useState(null);
  const [activeTab, setActiveTab]       = useState('queue');
  const [statFilter, setStatFilter]     = useState(null);
  // Presentational only: whether the filter popover is open.
  const [filterOpen, setFilterOpen]     = useState(false);
  // Free text search over the queue (title, context, why, app). Local only.
  const [query, setQuery]               = useState('');
  // Work due beyond a month is folded away by default. It is never removed,
  // only collapsed behind a count, so the queue opens on what is actually
  // actionable instead of on a year of calendar obligations.
  const [showLater, setShowLater]       = useState(false);
  const [selectedPriority, setSelectedPriority] = useState(null); // the drawer's item
  const [showLaterInternal, setShowLaterInternal] = useState(false); // internal section long tail
  const [lastSync, setLastSync]         = useState(null);
  const [intakeCounts, setIntakeCounts] = useState({ processing: 0, awaiting_human: 0 });
  const [showLauncher, setShowLauncher] = useState(false);
  // Cmd/Ctrl+K opens the app launcher from anywhere on the screen (Spotlight muscle memory).
  useEffect(() => {
    const onHotkey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) { e.preventDefault(); setShowLauncher(s => !s); }
    };
    window.addEventListener('keydown', onHotkey);
    return () => window.removeEventListener('keydown', onHotkey);
  }, []);
  // Selectable quarters. Starts with the current quarter and several prior, and
  // the user can add ANY year/quarter (past or future) to enter data and report.
  // (The Board/Committee deck itself now lives in Deck Studio; these quarters
  // drive the operational-metrics entry, which the Deck Studio composer reads.)
  const mkQ = (y, q, current = false) => ({ year: y, quarter: q, key: `${y}-${q}`, label: `Q${q} ${y}${current ? ' (current)' : ''}` });
  const defaultQuarters = () => {
    const out = []; const n = new Date();
    let y = n.getUTCFullYear(); let q = Math.floor(n.getUTCMonth() / 3) + 1;
    for (let i = 0; i < 8; i++) { out.push(mkQ(y, q, i === 0)); q -= 1; if (q < 1) { q = 4; y -= 1; } }
    return out;
  };
  const [quarters, setQuarters] = useState(defaultQuarters);
  const addQuarter = (y, q) => {
    const yr = Number(y), qq = Number(q), key = `${yr}-${qq}`;
    if (!yr || !(qq >= 1 && qq <= 4)) return null;
    setQuarters(prev => prev.some(x => x.key === key) ? prev : [...prev, mkQ(yr, qq)].sort((a, b) => b.year - a.year || b.quarter - a.quarter));
    return key;
  };
  const [newQ, setNewQ] = useState(() => { const n = new Date(); return { year: n.getUTCFullYear(), quarter: Math.floor(n.getUTCMonth() / 3) + 1 }; });

  // ── Operational metrics (per-quarter manual entry for the committee deck) ──
  // Each periodic metric is three monthly values (one per month of the quarter).
  const blankOp = () => ({
    timeliness: { um: ['', '', ''], partCAppeals: ['', '', ''], partDAppeals: ['', '', ''], partCClaims: ['', '', ''], partCGrievances: ['', '', ''], partDGrievances: ['', '', ''] },
    callCenter: { avgHoldSec: ['', '', ''], answered30Pct: ['', '', ''], disconnectPct: ['', '', ''], asaSec: ['', '', ''], ttyPct: ['', '', ''] },
    ctm: { volume: ['', '', ''], timelyPct: ['', '', ''] },
    caseManagement: { initialHra: ['', '', ''], annualHra: ['', '', ''] },
    icp: { initialIcp: ['', '', ''], annualIcp: ['', '', ''] },
    nonc: [],
    siu: { total: '', rejected: '', opened: '', cases: [] },
    adhoc: { title: '', body: '' },
    adhocSlides: [],
  });
  const arr3 = (a) => { const x = Array.isArray(a) ? a.slice(0, 3) : []; while (x.length < 3) x.push(''); return x; };
  const mapArr3 = (base, src) => Object.fromEntries(Object.keys(base).map(k => [k, arr3((src || {})[k])]));
  const [opModalOpen, setOpModalOpen] = useState(false);
  const [opQuarter, setOpQuarter] = useState(quarters[0].key);
  const [opData, setOpData] = useState(blankOp());
  const [opLoading, setOpLoading] = useState(false);
  const [opSaving, setOpSaving] = useState(false);
  const [opSaved, setOpSaved] = useState(false);
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const ctmMonthLabels = (() => { const q = Number(opQuarter.split('-')[1] || 1); return [0, 1, 2].map(i => MONTHS[(q - 1) * 3 + i]); })();

  const loadOpMetrics = async (key) => {
    const [y, q] = key.split('-').map(Number);
    setOpLoading(true); setOpSaved(false);
    try {
      const res = await fetch(`${API}/api/compliance-findings/operational-metrics?year=${y}&quarter=${q}`, { credentials: 'include' });
      const json = await res.json();
      const d = json?.data && typeof json.data === 'object' ? json.data : null;
      const b = blankOp();
      setOpData(d ? {
        timeliness: mapArr3(b.timeliness, d.timeliness),
        callCenter: mapArr3(b.callCenter, d.callCenter),
        ctm: mapArr3(b.ctm, d.ctm),
        caseManagement: mapArr3(b.caseManagement, d.caseManagement),
        icp: mapArr3(b.icp, d.icp),
        nonc: Array.isArray(d.nonc) ? d.nonc : [],
        siu: { ...b.siu, ...(d.siu || {}), cases: Array.isArray(d.siu?.cases) ? d.siu.cases : [] },
        adhoc: { title: '', body: '' },
        // Fold any legacy single ad-hoc slide into the unified builder so there
        // is one place to author content; clearing adhoc avoids double-render.
        adhocSlides: [
          ...((String(d.adhoc?.title || '').trim() || String(d.adhoc?.body || '').trim())
            ? [{ id: uid(), title: String(d.adhoc?.title || ''), blocks: String(d.adhoc?.body || '').trim() ? [{ id: uid(), type: 'text', text: String(d.adhoc?.body || '') }] : [] }]
            : []),
          ...(Array.isArray(d.adhocSlides) ? d.adhocSlides : []),
        ],
      } : b);
    } catch { setOpData(blankOp()); }
    finally { setOpLoading(false); }
  };
  const openOpModal = () => { setOpModalOpen(true); loadOpMetrics(opQuarter); };
  const saveOpMetrics = async () => {
    const [y, q] = opQuarter.split('-').map(Number);
    setOpSaving(true);
    try {
      const res = await fetch(`${API}/api/compliance-findings/operational-metrics`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ year: y, quarter: q, data: opData }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `Failed (${res.status})`);
      setOpSaved(true);
    } catch (e) { alert(`Could not save: ${e.message}`); }
    finally { setOpSaving(false); }
  };
  const setT = (k, i, v) => setOpData(d => ({ ...d, timeliness: { ...d.timeliness, [k]: d.timeliness[k].map((x, j) => j === i ? v : x) } }));
  const setCC = (k, i, v) => setOpData(d => ({ ...d, callCenter: { ...d.callCenter, [k]: d.callCenter[k].map((x, j) => j === i ? v : x) } }));
  const setCtm = (k, i, v) => setOpData(d => ({ ...d, ctm: { ...d.ctm, [k]: d.ctm[k].map((x, j) => j === i ? v : x) } }));
  const setCM = (k, i, v) => setOpData(d => ({ ...d, caseManagement: { ...d.caseManagement, [k]: d.caseManagement[k].map((x, j) => j === i ? v : x) } }));
  const setICP = (k, i, v) => setOpData(d => ({ ...d, icp: { ...d.icp, [k]: d.icp[k].map((x, j) => j === i ? v : x) } }));
  // ── Custom user-authored slides (content blocks + charts) ──────────────────
  const uid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
  const newBlock = (type) => {
    if (type === 'bullets') return { id: uid(), type, items: [''] };
    if (type === 'chart') return { id: uid(), type, title: '', chartType: 'auto', time: false, labels: ['', '', ''], series: [{ name: '', values: ['', '', ''] }], colors: [], stack: 'none', combo: false, lineSeries: [], targetLine: '', labelFormat: 'auto', legendPos: 'auto', valAxis: false, valMin: '', valMax: '', orientation: 'auto' };
    if (type === 'table') return { id: uid(), type, header: ['Column 1', 'Column 2'], rows: [['', ''], ['', '']] };
    return { id: uid(), type, text: '' };
  };
  const mapSlide = (d, sid, fn) => ({ ...d, adhocSlides: (d.adhocSlides || []).map(s => s.id === sid ? fn(s) : s) });
  const mapBlock = (sid, bid, fn) => setOpData(d => mapSlide(d, sid, s => ({ ...s, blocks: (s.blocks || []).map(b => b.id === bid ? fn(b) : b) })));
  const addUserSlide = () => setOpData(d => ({ ...d, adhocSlides: [...(d.adhocSlides || []), { id: uid(), title: '', blocks: [] }] }));
  const delUserSlide = (sid) => setOpData(d => ({ ...d, adhocSlides: (d.adhocSlides || []).filter(s => s.id !== sid) }));
  const setUserSlideTitle = (sid, v) => setOpData(d => mapSlide(d, sid, s => ({ ...s, title: v })));
  const addBlock = (sid, type) => setOpData(d => mapSlide(d, sid, s => ({ ...s, blocks: [...(s.blocks || []), newBlock(type)] })));
  const delBlock = (sid, bid) => setOpData(d => mapSlide(d, sid, s => ({ ...s, blocks: (s.blocks || []).filter(b => b.id !== bid) })));
  const moveBlock = (sid, bid, dir) => setOpData(d => mapSlide(d, sid, s => {
    const bs = [...(s.blocks || [])]; const i = bs.findIndex(b => b.id === bid); const j = i + dir;
    if (i < 0 || j < 0 || j >= bs.length) return s; [bs[i], bs[j]] = [bs[j], bs[i]]; return { ...s, blocks: bs };
  }));
  const setBlock = (sid, bid, patch) => mapBlock(sid, bid, b => ({ ...b, ...patch }));
  const setBullet = (sid, bid, i, v) => mapBlock(sid, bid, b => ({ ...b, items: b.items.map((x, j) => j === i ? v : x) }));
  const addBullet = (sid, bid) => mapBlock(sid, bid, b => ({ ...b, items: [...(b.items || []), ''] }));
  const delBullet = (sid, bid, i) => mapBlock(sid, bid, b => ({ ...b, items: b.items.filter((_, j) => j !== i) }));
  const setLabel = (sid, bid, i, v) => mapBlock(sid, bid, b => ({ ...b, labels: b.labels.map((x, j) => j === i ? v : x) }));
  const setSeriesName = (sid, bid, si, v) => mapBlock(sid, bid, b => ({ ...b, series: b.series.map((se, j) => j === si ? { ...se, name: v } : se) }));
  const setSeriesVal = (sid, bid, si, i, v) => mapBlock(sid, bid, b => ({ ...b, series: b.series.map((se, j) => j === si ? { ...se, values: se.values.map((x, k) => k === i ? v : x) } : se) }));
  const addCol = (sid, bid) => mapBlock(sid, bid, b => ({ ...b, labels: [...b.labels, ''], series: b.series.map(se => ({ ...se, values: [...se.values, ''] })) }));
  const delCol = (sid, bid) => mapBlock(sid, bid, b => b.labels.length <= 1 ? b : ({ ...b, labels: b.labels.slice(0, -1), series: b.series.map(se => ({ ...se, values: se.values.slice(0, -1) })) }));
  const addSeries = (sid, bid) => mapBlock(sid, bid, b => ({ ...b, series: [...b.series, { name: '', values: b.labels.map(() => '') }] }));
  const delSeries = (sid, bid) => mapBlock(sid, bid, b => b.series.length <= 1 ? b : ({ ...b, series: b.series.slice(0, -1), colors: (b.colors || []).slice(0, -1), lineSeries: (b.lineSeries || []).filter(i => i < b.series.length - 1) }));
  // Pass 1 chart controls. colors is a sparse index→hex array ('' means "use the
  // default palette at this position"); the server validates every value.
  const setChartColor = (sid, bid, i, v) => mapBlock(sid, bid, b => { const c = [...(b.colors || [])]; while (c.length <= i) c.push(''); c[i] = v; return { ...b, colors: c }; });
  const clearChartColor = (sid, bid, i) => mapBlock(sid, bid, b => { const c = [...(b.colors || [])]; if (i < c.length) c[i] = ''; return { ...b, colors: c }; });
  const toggleLineSeries = (sid, bid, i) => mapBlock(sid, bid, b => { const set = new Set(b.lineSeries || []); set.has(i) ? set.delete(i) : set.add(i); return { ...b, lineSeries: [...set].sort((a, z) => a - z) }; });
  // Client mirror of the server's deterministic picker, so the color controls show
  // for the axis (series vs category) that will actually be colored.
  const effectiveChartType = (b) => {
    if (b.chartType && b.chartType !== 'auto') return b.chartType;
    const cats = Array.isArray(b.labels) ? b.labels.length : 0;
    const nSeries = Array.isArray(b.series) ? b.series.length : 0;
    if (b.time) return nSeries > 1 ? 'line' : 'area';
    if (nSeries <= 1 && cats >= 2 && cats <= 6) return 'doughnut';
    return 'bar';
  };
  const setTableHeader = (sid, bid, ci, v) => mapBlock(sid, bid, b => ({ ...b, header: b.header.map((h, j) => j === ci ? v : h) }));
  const setTableCell = (sid, bid, ri, ci, v) => mapBlock(sid, bid, b => ({ ...b, rows: b.rows.map((r, j) => j === ri ? r.map((c, k) => k === ci ? v : c) : r) }));
  const addTableCol = (sid, bid) => mapBlock(sid, bid, b => ({ ...b, header: [...b.header, ''], rows: b.rows.map(r => [...r, '']) }));
  const delTableCol = (sid, bid) => mapBlock(sid, bid, b => b.header.length <= 1 ? b : ({ ...b, header: b.header.slice(0, -1), rows: b.rows.map(r => r.slice(0, -1)) }));
  const addTableRow = (sid, bid) => mapBlock(sid, bid, b => ({ ...b, rows: [...b.rows, b.header.map(() => '')] }));
  const delTableRow = (sid, bid) => mapBlock(sid, bid, b => b.rows.length <= 1 ? b : ({ ...b, rows: b.rows.slice(0, -1) }));
  // Paste a block of cells from Excel/PowerPoint and spread it across fields. Excel
  // copies tab-separated columns and newline-separated rows; we parse that grid and
  // write each value into its own cell, growing the table/chart as needed.
  const parseGrid = (text) => String(text || '').replace(/\r/g, '').replace(/\n+$/, '').split('\n').map(line => line.split('\t'));
  const isMultiCell = (text) => /\t|\n/.test(String(text || ''));
  // ri === -1 targets the header row; rows flow down, columns flow right.
  const pasteTable = (sid, bid, ri, ci, text) => mapBlock(sid, bid, b => {
    const grid = parseGrid(text);
    const maxCols = Math.max(1, ...grid.map(g => g.length));
    const needCols = ci + maxCols;
    let header = [...(b.header || [])];
    let rows = (b.rows || []).map(r => [...r]);
    while (header.length < needCols) header.push('');
    rows = rows.map(r => { while (r.length < needCols) r.push(''); return r; });
    let target = ri;
    for (const line of grid) {
      if (target === -1) { for (let k = 0; k < line.length; k++) header[ci + k] = line[k]; }
      else { while (rows.length <= target) rows.push(Array(needCols).fill('')); for (let k = 0; k < line.length; k++) rows[target][ci + k] = line[k]; }
      target++;
    }
    return { ...b, header, rows };
  });
  // mode: 'values' (start series si, category ci), 'labels' (categories from ci), 'names' (series names down from si).
  const pasteChart = (sid, bid, si, ci, text, mode) => mapBlock(sid, bid, b => {
    const grid = parseGrid(text);
    let labels = [...(b.labels || [])];
    let series = (b.series || []).map(se => ({ ...se, values: [...se.values] }));
    const grow = (need) => { while (labels.length < need) labels.push(''); series = series.map(se => { const v = [...se.values]; while (v.length < need) v.push(''); return { ...se, values: v }; }); };
    if (mode === 'labels') {
      const line = grid[0] || [];
      grow(ci + line.length);
      for (let k = 0; k < line.length; k++) labels[ci + k] = line[k];
    } else if (mode === 'names') {
      for (let g = 0; g < grid.length; g++) { const ts = si + g; while (series.length <= ts) series.push({ name: '', values: labels.map(() => '') }); series[ts] = { ...series[ts], name: grid[g][0] ?? '' }; }
    } else {
      const maxCols = Math.max(1, ...grid.map(g => g.length));
      grow(ci + maxCols);
      for (let g = 0; g < grid.length; g++) {
        const ts = si + g; while (series.length <= ts) series.push({ name: '', values: labels.map(() => '') });
        const vals = [...series[ts].values]; while (vals.length < ci + maxCols) vals.push('');
        for (let k = 0; k < grid[g].length; k++) vals[ci + k] = grid[g][k];
        series[ts] = { ...series[ts], values: vals };
      }
    }
    return { ...b, labels, series };
  });
  const [enhancingId, setEnhancingId] = useState(null);
  const enhanceBlock = async (sid, bid, text, title) => {
    if (!String(text || '').trim()) return;
    setEnhancingId(bid);
    try {
      const res = await fetch(`${API}/api/compliance-findings/enhance-text`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, title: title || '' }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `Failed (${res.status})`);
      if (json.enhanced) setBlock(sid, bid, { text: json.enhanced });
    } catch (e) { alert(`Could not enhance: ${e.message}`); }
    finally { setEnhancingId(null); }
  };
  const BLOCK_LABEL = { heading: 'Heading', text: 'Text', bullets: 'Bullets', table: 'Table', chart: 'Chart' };
  const setSiuField = (k, v) => setOpData(d => ({ ...d, siu: { ...d.siu, [k]: v } }));
  const addNonc = () => setOpData(d => ({ ...d, nonc: [...d.nonc, { notification: '', dateReceived: '', topic: '' }] }));
  const setNonc = (i, k, v) => setOpData(d => ({ ...d, nonc: d.nonc.map((r, j) => j === i ? { ...r, [k]: v } : r) }));
  const delNonc = (i) => setOpData(d => ({ ...d, nonc: d.nonc.filter((_, j) => j !== i) }));
  const addCase = () => setOpData(d => ({ ...d, siu: { ...d.siu, cases: [...d.siu.cases, { label: '', dateReceived: '', description: '', disposition: '', summary: '', status: '', imedic: false }] } }));
  const setCase = (i, k, v) => setOpData(d => ({ ...d, siu: { ...d.siu, cases: d.siu.cases.map((r, j) => j === i ? { ...r, [k]: v } : r) } }));
  const delCase = (i) => setOpData(d => ({ ...d, siu: { ...d.siu, cases: d.siu.cases.filter((_, j) => j !== i) } }));

  const hour      = new Date().getHours();
  const greeting  = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = user?.name?.split(' ')[0] || 'there';

  // Data loading
  const loadPriorities = useCallback(async () => {
    try {
      const res  = await fetch(`${API}/api/overmind/oracle?limit=500`, { credentials: 'include' });
      const data = await res.json();
      // Calendar obligations are shown on the two calendars beside this list, so they
      // leave the list and every count derived from it.
      if (data.success) { setItems((data.items || []).filter(i => i.source_app !== 'calendar')); setLastSync(new Date()); }
      else setError('Could not load priorities.');
    } catch { setError('Connection error. Please refresh.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadPriorities(); }, [loadPriorities]);

  useEffect(() => {
    async function fetchIntakeCounts() {
      try {
        const res  = await fetch(`${API}/api/overmind/intake`, { credentials: 'include' });
        const data = await res.json();
        if (data.success) setIntakeCounts({ processing: data.counts?.processing || 0, awaiting_human: data.counts?.awaiting_human || 0 });
      } catch {}
    }
    fetchIntakeCounts();
    const id = setInterval(fetchIntakeCounts, 30000);
    return () => clearInterval(id);
  }, []);

  // Computed
  const now           = new Date();
  // Dedup for stats too
  // Same dedup as the visible list (id first, then title+app fallback) —
  // the counts and the rows must never disagree.
  const uniqueItems   = items.filter((item, idx, arr) => {
    if (item.source_entity_id) return arr.findIndex(i => i.source_entity_id === item.source_entity_id) === idx;
    return arr.findIndex(i => i.title === item.title && i.source_app === item.source_app) === idx;
  });
  const criticalCount = uniqueItems.filter(i => i.urgency_level === 'critical').length;
  const overdueCount  = uniqueItems.filter(i => i.due_date && new Date(i.due_date) < now).length;
  const dueWeekCount  = uniqueItems.filter(i => { if (!i.due_date) return false; return (new Date(i.due_date) - now) / 86400000 >= 0 && (new Date(i.due_date) - now) / 86400000 <= 7; }).length;
  const totalActive   = uniqueItems.length;
  // What needs action now: overdue, due today, or due within the week. The
  // Today badge shows this, never the whole open list, so "Today 39" cannot
  // read as thirty nine things due today.
  const actionNowCount = uniqueItems.filter(i => i.due_date && (new Date(i.due_date) - now) / 86400000 <= 7).length;

  // Handlers
  const handleSync = async () => {
    setSyncing(true); setSyncResult(null);
    try { const res = await fetch(`${API}/api/overmind/oracle/sync`, { method: 'POST', credentials: 'include' }); const data = await res.json(); if (data.success) { setSyncResult(data.sync); await loadPriorities(); } } catch {}
    setSyncing(false);
  };

  const handleAction = async (itemId, action) => {
    try { await fetch(`${API}/api/overmind/priority/${itemId}`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action }) }); setItems(prev => prev.filter(i => i.id !== itemId)); } catch {}
  };
  const handleApproveAndAct = async (itemId) => {
    try { await fetch(`${API}/api/overmind/priority/${itemId}`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'actioned', execute_action: true }) }); setItems(prev => prev.filter(i => i.id !== itemId)); } catch {}
  };
  const handleDraft = async (itemId) => {
    try { const res = await fetch(`${API}/api/overmind/priority/${itemId}/draft`, { method: 'POST', credentials: 'include' }); const data = await res.json(); if (data.success) { setItems(prev => prev.map(i => i.id === itemId ? { ...i, pom_draft: data.draft } : i)); return data.draft; } } catch {}
    return null;
  };

  // Filters
  const filterBar = [
    { key: 'all', label: 'All', dot: null },
    { key: 'critical', label: 'Critical', dot: '#ef4444' },
    { key: 'high', label: 'High', dot: '#f97316' },
    { key: 'network_adequacy', label: 'Network', dot: '#3b82f6' },
    { key: 'privacy', label: 'Privacy', dot: '#06b6d4' },
    { key: 'cil', label: 'Issues', dot: '#64748b' },
    { key: 'coa', label: 'COA', dot: '#10b981' },
    { key: 'risk', label: 'Risk', dot: '#eab308' },
    { key: 'sentinel', label: 'Sentinel', dot: '#ef4444' },
    { key: 'fdr', label: 'FDR', dot: '#14b8a6' },
    { key: 'cap', label: 'CAP', dot: '#0ea5e9' },
    { key: 'ppm', label: 'Policies', dot: '#a855f7' },
    { key: 'memo', label: 'Mandate', dot: '#22c55e' },
    { key: 'priq', label: 'RuleIQ', dot: '#f59e0b' },
  ];
  // Dedup items — by source_entity_id first, then by title as fallback
  const dedupedItems = items.filter((item, idx, arr) => {
    if (item.source_entity_id) {
      return arr.findIndex(i => i.source_entity_id === item.source_entity_id) === idx;
    }
    return arr.findIndex(i => i.title === item.title && i.source_app === item.source_app) === idx;
  });
  let filteredItems = filter === 'all' ? dedupedItems : dedupedItems.filter(i => i.urgency_level === filter || i.source_app === filter);
  if (statFilter === 'critical')  filteredItems = filteredItems.filter(i => i.urgency_level === 'critical');
  if (statFilter === 'overdue')   filteredItems = filteredItems.filter(i => i.due_date && new Date(i.due_date) < now);
  if (statFilter === 'week')      filteredItems = filteredItems.filter(i => { if (!i.due_date) return false; const dl = (new Date(i.due_date) - now) / 86400000; return dl >= 0 && dl <= 7; });
  if (query.trim()) { const q = query.trim().toLowerCase(); filteredItems = filteredItems.filter(i => [i.title, i.context, i.why, i.source_app].some(v => (v || '').toLowerCase().includes(q))); }

  // -------------------------------------------------------------------------
  return (
    <div className={`relative flex-1 min-h-0 overflow-auto ${t('bg-[#f8fafc]', 'bg-[#08090f]')}`}>
      {/* Ambient depth wash — wallpaper light bleeding through the glass layers above it. */}
      <div aria-hidden className="pointer-events-none fixed inset-0 z-0" style={{
        background: t(
          'radial-gradient(900px 480px at 18% -8%, rgba(251,146,60,0.07), transparent 60%), radial-gradient(1100px 560px at 88% -12%, rgba(59,130,246,0.06), transparent 60%)',
          'radial-gradient(900px 480px at 18% -8%, rgba(251,146,60,0.06), transparent 60%), radial-gradient(1100px 560px at 88% -12%, rgba(59,130,246,0.07), transparent 60%)'
        )
      }} />
      <style>{`
        @keyframes psiFadeUp { from { opacity:0; transform: translateY(8px) } to { opacity:1; transform:none } }
        @media (prefers-reduced-motion: reduce) { .psi-fade-up { animation: none !important } }
        .psi-fade-up { animation: psiFadeUp .35s cubic-bezier(.2,.9,.3,1) both }
      `}</style>

      {/* ── Top Bar — modern, minimal (Linear/Vercel-inspired) ── */}
      <div className={`sticky top-0 z-40 flex items-center justify-between h-14 px-5 backdrop-blur-xl ${t('bg-white/70 border-b border-slate-200/50','bg-[#0a0c10]/70 border-b border-white/[0.04]')}`}>
        {/* Left: breadcrumb-style identity */}
        <div className="flex items-center gap-2.5">
          <div className="relative">
            {/* The PCG lighthouse mark. The structure's fill follows the
                theme (navy on light, white on dark) so the mark never sinks
                into the background; the signal stays brand orange in both. */}
            <svg className="w-8 h-8" viewBox="0 0 512 512" role="img" aria-label="Precision Compliance Group">
              <defs>
                <linearGradient id="psiPcgLight" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0" stopColor="#F79A3E" />
                  <stop offset="1" stopColor="#E5601F" />
                </linearGradient>
              </defs>
              <g fill="none" stroke="url(#psiPcgLight)" strokeWidth="26" strokeLinecap="round">
                <path d="M148 88 A 150 150 0 0 0 148 216" />
                <path d="M364 88 A 150 150 0 0 1 364 216" />
              </g>
              <circle cx="256" cy="152" r="46" fill="url(#psiPcgLight)" />
              <path d="M212 232 h88 l40 214 a16 16 0 0 1 -16 20 H188 a16 16 0 0 1 -16 -20 Z" fill={t('#0E2841', '#FFFFFF')} />
            </svg>
            <div className={`absolute -bottom-px -right-px w-2.5 h-2.5 rounded-full border-2 bg-emerald-400 ${t('border-white','border-[#0a0c10]')}`} />
          </div>
          <div className="flex items-center gap-1.5">
            <span className={`text-[13px] font-semibold ${t('text-slate-800','text-white')}`}>Precision Secured Intelligence</span>
          </div>
        </div>

        {/* Right: clean icon row */}
        <div className="flex items-center gap-0.5">
          {/* Sync */}
          <button onClick={handleSync} disabled={syncing}
            className={`h-8 px-2.5 flex items-center gap-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-40 ${t('text-slate-500 hover:bg-slate-100','text-slate-400 hover:bg-white/[0.06]')}`}>
            {syncing ? <Spinner size={3} className={t('text-slate-400','text-slate-500')} /> : <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>}
            {syncing ? 'Syncing' : 'Sync'}
          </button>

          <div className={`w-px h-4 mx-1 ${t('bg-slate-200','bg-white/[0.06]')}`} />

          {/* Theme */}
          <button onClick={toggle}
            className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all ${t('text-slate-400 hover:text-slate-600 hover:bg-slate-100','text-slate-500 hover:text-slate-300 hover:bg-white/[0.06]')}`}
            title={`${theme === 'dark' ? 'Light' : 'Dark'} mode`}>
            {theme === 'dark' ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><circle cx="12" cy="12" r="5"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>
            )}
          </button>

          {/* Apps — Cmd/Ctrl+K also opens this */}
          <button onClick={() => setShowLauncher(true)}
            className={`h-8 px-2 flex items-center gap-1.5 rounded-lg transition-all ${t('text-slate-400 hover:text-slate-600 hover:bg-slate-100','text-slate-500 hover:text-slate-300 hover:bg-white/[0.06]')}`}
            title="Apps (⌘K)">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
              <rect x="1" y="1" width="6" height="6" rx="1.5"/><rect x="9" y="1" width="6" height="6" rx="1.5"/>
              <rect x="1" y="9" width="6" height="6" rx="1.5"/><rect x="9" y="9" width="6" height="6" rx="1.5"/>
            </svg>
            <span className={`hidden md:inline text-[10px] font-semibold px-1 py-px rounded border ${t('border-slate-200 text-slate-400','border-white/[0.08] text-slate-500')}`}>⌘K</span>
          </button>

          {/* Team & Access — visible only to compliance officer + platform admin */}
          {(() => {
            const ut = user?.user_type || user?.effective_user_type || null;
            return (
              ut === 'platform_superuser' ||
              ut === 'org_superadmin' ||
              ut === 'compliance_officer' ||
              user?.role === 'superadmin' ||
              user?.role === 'superuser' ||
              (!ut && user?.role === 'admin' && !user?.department_id)
            );
          })() && (
            <a href="/team-access"
              className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all ${t('text-slate-400 hover:text-slate-600 hover:bg-slate-100','text-slate-500 hover:text-slate-300 hover:bg-white/[0.06]')}`}
              title="Team & Access — invite teammates, grant per-app access, observers">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.7} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"/>
              </svg>
            </a>
          )}

          {/* User */}
          <button className={`w-8 h-8 rounded-lg flex items-center justify-center text-[11px] font-bold tracking-wide transition-all ${t('bg-slate-100 text-slate-500 hover:bg-slate-200','bg-white/[0.06] text-slate-400 hover:bg-white/10')}`}>
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </button>
        </div>
      </div>

      {/* App launcher modal */}
      <AppLauncher open={showLauncher} onClose={() => setShowLauncher(false)} onSelectApp={onSelectApp} user={user} t={t} />

      <div className="max-w-[1400px] mx-auto px-6 py-8">
        {/* ── Greeting ── */}
        <div className="psi-fade-up flex items-end justify-between gap-4 mb-[18px] flex-wrap">
          <div>
            <p className={`text-[12px] font-medium mb-1 ${t('text-slate-400','text-slate-500')}`}>
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
            <h1 className={`text-[26px] leading-tight font-bold tracking-[-0.015em] ${t('text-slate-900','text-white')}`}>
              {greeting}, {firstName}
            </h1>
            <p className={`text-[13px] mt-1 ${t('text-slate-500','text-slate-400')}`}>
              {loading ? 'Checking your systems…' : totalActive === 0 && intakeCounts.awaiting_human === 0
                ? 'All clear. Nothing is overdue and nothing is waiting on you.'
                : <>
                    <b className={`font-semibold ${t('text-slate-900','text-slate-100')}`}>{overdueCount} overdue</b>, <b className={`font-semibold ${t('text-slate-900','text-slate-100')}`}>{dueWeekCount} due this week</b>
                    {intakeCounts.awaiting_human > 0 && <>, and <b className={`font-semibold ${t('text-slate-900','text-slate-100')}`}>{intakeCounts.awaiting_human} document{intakeCounts.awaiting_human !== 1 ? 's' : ''}</b> waiting for your call</>}.
                  </>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {(intakeCounts.awaiting_human > 0 || intakeCounts.processing > 0) && (
              <button onClick={() => setActiveTab('intake')}
                className={`inline-flex items-center gap-2 text-[13px] px-3.5 py-2 rounded-[10px] font-semibold border transition-colors ${t('bg-white border-slate-200 text-slate-800 hover:bg-slate-50','bg-white/[0.04] border-white/10 text-slate-200 hover:bg-white/[0.08]')}`}>
                <span className="w-2 h-2 rounded-full bg-amber-400" />
                {intakeCounts.awaiting_human > 0 ? `${intakeCounts.awaiting_human} to review` : `${intakeCounts.processing} processing`}
              </button>
            )}
            <button onClick={() => setActiveTab('ingest')}
              className="inline-flex items-center text-[13px] px-3.5 py-2 rounded-[10px] font-semibold text-white bg-[#E5601F] hover:bg-[#cf5217] transition-colors">
              Add a document
            </button>
          </div>
        </div>


        {/* ── Views ── */}
        <div role="tablist" className={`psi-fade-up inline-flex mb-[18px] p-[3px] rounded-xl gap-0.5 border ${t('bg-slate-100/80 border-slate-200/80','bg-white/[0.045] border-white/[0.06]')}`}>
          {[
            { key: 'queue',  label: 'Today',               on: activeTab === 'queue', badge: actionNowCount, warn: false },
            { key: 'ingest', label: 'Documents',           on: activeTab === 'ingest' || activeTab === 'intake', badge: intakeCounts.awaiting_human || intakeCounts.processing, warn: intakeCounts.awaiting_human > 0 },
            { key: 'agents', label: 'What it has learned', on: activeTab === 'agents', badge: 0 },
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} aria-selected={tab.on} role="tab"
              className={`inline-flex items-center gap-[7px] px-3.5 py-[7px] text-[13px] font-semibold rounded-[9px] whitespace-nowrap transition-all duration-150 ${
                tab.on ? t('bg-white text-slate-900 shadow-[0_1px_3px_rgba(15,23,42,0.12)]','bg-white/[0.1] text-white')
                       : t('text-slate-500 hover:text-slate-800','text-slate-500 hover:text-slate-300')
              }`}>
              {tab.label}
              {tab.badge > 0 && (
                <span className={`text-[11px] tabular-nums px-1.5 py-px rounded-full font-semibold ${
                  tab.warn ? t('bg-amber-100 text-amber-700','bg-amber-400/20 text-amber-400') : t('bg-slate-200/90 text-slate-600','bg-white/[0.1] text-slate-300')
                }`}>{tab.badge}</span>
              )}
            </button>
          ))}
        </div>

        <div className="xl:grid xl:grid-cols-[minmax(0,1fr)_440px] xl:gap-8 xl:items-stretch">
        <div className="min-w-0">

        {/* ── Tiles: three numbers you can press ── */}
        {activeTab === 'queue' && !loading && (() => {
          const overdueList = uniqueItems.filter(i => i.due_date && new Date(i.due_date) < now);
          const weekList = uniqueItems.filter(i => { if (!i.due_date) return false; const dl = (new Date(i.due_date) - now) / 86400000; return dl >= 0 && dl <= 7; });
          const criticalList = uniqueItems.filter(i => i.urgency_level === 'critical');
          const docs = intakeCounts.awaiting_human;
          const decideSub = [docs > 0 ? `${docs} document${docs === 1 ? '' : 's'}` : null, ...describeKinds(criticalList, docs > 0 ? 2 : 3)].filter(Boolean).join(', ');
          const tiles = [
            { key: 'overdue',  label: 'Overdue',          value: overdueList.length,          dot: '#DC2626', sub: overdueList.length ? describeKinds(overdueList).join(', ') : 'nothing past due' },
            { key: 'week',     label: 'Due this week',    value: weekList.length,             dot: '#D97706', sub: weekList.length ? describeKinds(weekList).join(', ') : 'nothing due in the next seven days' },
            { key: 'critical', label: 'Needs a decision', value: criticalList.length + docs + ag.live,  dot: '#2563EB', sub: decideSub || 'nothing waiting on you' },
          ];
          return (
            <div className="psi-fade-up grid grid-cols-3 gap-3 mb-5">
              {tiles.map(w => {
                const isActive = statFilter === w.key;
                return (
                  <button key={w.key} aria-pressed={isActive}
                    onClick={() => setStatFilter(isActive ? null : w.key)}
                    title={isActive ? 'Show every open item' : `Show only ${w.label.toLowerCase()}`}
                    className={`text-left rounded-[14px] px-4 py-3.5 border transition-all ${
                      isActive
                        ? t('bg-white border-slate-900 shadow-[inset_0_0_0_1px_#0f172a]','bg-white/[0.06] border-white/80 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.8)]')
                        : t('bg-white border-slate-200/90 hover:border-slate-300','bg-white/[0.035] border-white/[0.07] hover:border-white/[0.14]')
                    }`}>
                    <span className={`flex items-center gap-[7px] text-[12px] font-semibold ${t('text-slate-500','text-slate-400')}`}><i className="inline-block w-2 h-2 rounded-full" style={{ background: w.dot }} aria-hidden />{w.label}</span>
                    <span className={`block text-[26px] font-bold leading-[1.1] mt-0.5 tabular-nums tracking-[-0.02em] ${t('text-slate-900','text-white')}`}>{w.value}</span>
                    <span className={`block text-[12px] mt-0.5 truncate ${t('text-slate-500','text-slate-500')}`}>{w.sub}</span>
                  </button>
                );
              })}
            </div>
          );
        })()}

        {/* ── Search and chips ── */}
        {activeTab === 'queue' && items.length > 0 && (
          <div className="flex items-center gap-2.5 flex-wrap mb-4">
            <label className={`flex-1 min-w-[240px] flex items-center gap-2 px-3 py-[7px] rounded-[10px] border text-[13px] ${t('bg-white border-slate-200/90 text-slate-400 focus-within:border-slate-400','bg-white/[0.04] border-white/[0.08] text-slate-500 focus-within:border-white/30')}`}>
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden><circle cx="11" cy="11" r="7" /><path strokeLinecap="round" d="M20 20l-3.5-3.5" /></svg>
              <input id="psi-search" type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search memos, findings, policies, vendors"
                className={`flex-1 min-w-0 bg-transparent outline-none ${t('text-slate-900 placeholder:text-slate-400','text-white placeholder:text-slate-500')}`} />
            </label>
            {(() => {
              const hasUrg = (k) => dedupedItems.some(i => i.urgency_level === k);
              const present = new Set(dedupedItems.map(i => i.source_app));
              const shown = filterBar.filter(f => f.key !== 'all' && f.key !== 'high' && (f.key === 'critical' ? hasUrg('critical') : present.has(f.key))).slice(0, 5);
              const hidden = filterBar.filter(f => f.key !== 'all' && !shown.includes(f));
              const chip = (f) => {
                const on = filter === f.key && !statFilter;
                return (
                  <button key={f.key} onClick={() => { setFilter(on ? 'all' : f.key); setStatFilter(null); setFilterOpen(false); }} aria-pressed={on}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-[5px] rounded-full text-[12px] font-semibold border transition-colors ${
                      on ? t('bg-slate-900 text-white border-slate-900','bg-white text-slate-900 border-white')
                         : t('bg-white text-slate-700 border-slate-300/80 hover:bg-slate-50','bg-white/[0.04] text-slate-300 border-white/[0.1] hover:bg-white/[0.08]')
                    }`}>
                    {f.dot && <i className="inline-block w-[7px] h-[7px] rounded-full" style={{ background: f.dot }} aria-hidden />}
                    {f.label}
                  </button>
                );
              };
              return (
                <div className="relative flex items-center gap-1.5 flex-wrap">
                  {shown.map(chip)}
                  {hidden.length > 0 && (
                    <button type="button" onClick={() => setFilterOpen(o => !o)} aria-expanded={filterOpen}
                      className={`inline-flex items-center px-2.5 py-[5px] rounded-full text-[12px] font-semibold border transition-colors ${t('bg-white text-slate-700 border-slate-300/80 hover:bg-slate-50','bg-white/[0.04] text-slate-300 border-white/[0.1] hover:bg-white/[0.08]')}`}>
                      More…
                    </button>
                  )}
                  {filterOpen && <div className="fixed inset-0 z-20" onClick={() => setFilterOpen(false)} aria-hidden />}
                  {filterOpen && (
                    <div className={`absolute right-0 top-full mt-2 z-30 w-56 rounded-2xl p-2 border shadow-xl ${t('bg-white border-slate-200/80','bg-[#10121a] border-white/[0.08]')}`}>
                      {[{ key: 'all', label: 'Everything', dot: null }, ...hidden].map(f => (
                        <button key={f.key} onClick={() => { setFilter(f.key); setStatFilter(null); setFilterOpen(false); }}
                          className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-[12px] font-medium rounded-lg text-left transition-colors ${
                            filter === f.key && !statFilter ? t('bg-slate-100 text-slate-900','bg-white/[0.1] text-white') : t('text-slate-600 hover:bg-slate-50','text-slate-300 hover:bg-white/[0.05]')
                          }`}>
                          {f.dot && <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: f.dot }} />}
                          {f.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* ── Today: the work, opened on a decision, then by time ── */}
        {activeTab === 'queue' && (
          <>
            {(statFilter || (filter !== 'all') || query) && !loading && (
              <div className={`flex items-center gap-2 flex-wrap mb-3 text-[12px] ${t('text-slate-500','text-slate-400')}`}>
                <span>Showing {filteredItems.length} of {totalActive}</span>
                {statFilter && (
                  <button onClick={() => setStatFilter(null)} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-semibold ${t('bg-slate-900 text-white','bg-white/[0.12] text-white')}`} title="Clear this filter">
                    {statFilter === 'week' ? 'Due this week' : statFilter === 'critical' ? 'Needs a decision' : 'Overdue'}<span aria-hidden>×</span>
                  </button>
                )}
                {filter !== 'all' && !statFilter && (
                  <button onClick={() => { setFilter('all'); setFilterOpen(false); }} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-semibold ${t('bg-slate-900 text-white','bg-white/[0.12] text-white')}`} title="Clear this filter">
                    {(filterBar.find(f => f.key === filter) || {}).label || filter}<span aria-hidden>×</span>
                  </button>
                )}
                {query && (
                  <button onClick={() => setQuery('')} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-semibold ${t('bg-slate-900 text-white','bg-white/[0.12] text-white')}`} title="Clear the search">
                    “{query}”<span aria-hidden>×</span>
                  </button>
                )}
              </div>
            )}
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className={`rounded-[14px] p-5 animate-pulse border ${t('bg-white border-slate-200/70','bg-white/[0.03] border-white/[0.06]')}`}>
                    <div className="flex gap-3">
                      <div className={`w-2 h-2 mt-2 rounded-full flex-shrink-0 ${t('bg-slate-200','bg-white/[0.06]')}`} />
                      <div className="flex-1 space-y-2">
                        <div className={`h-4 rounded w-3/4 ${t('bg-slate-200','bg-white/[0.04]')}`} />
                        <div className={`h-3 rounded w-1/2 ${t('bg-slate-200','bg-white/[0.04]')}`} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className={`rounded-[14px] border p-5 text-sm ${t('bg-red-50 border-red-200 text-red-700','bg-red-500/5 border-red-500/20 text-red-400')}`}>{error}</div>
            ) : filteredItems.length === 0 ? (
              <div className={`flex flex-col items-center justify-center py-16 text-center rounded-[14px] border ${t('bg-white border-slate-200/90','bg-white/[0.03] border-white/[0.06]')}`}>
                <h3 className={`text-lg font-semibold mb-2 ${t('text-slate-800','text-slate-200')}`}>
                  {totalActive === 0 ? `All clear, ${firstName}.` : 'Nothing matches.'}
                </h3>
                <p className={`text-sm max-w-xs mb-6 leading-relaxed ${t('text-slate-500','text-slate-500')}`}>
                  {totalActive === 0 ? 'Every connected system is quiet. The moment something needs you, it appears here.' : 'Try another filter or clear the search.'}
                </p>
                {totalActive === 0 ? (
                  <button onClick={handleSync}
                    className={`px-4 py-2 text-sm font-medium rounded-xl border ${t('text-slate-600 border-slate-200 hover:bg-slate-50','text-slate-400 border-white/[0.08] hover:border-white/20')}`}>
                    {syncing ? 'Checking…' : 'Check again'}
                  </button>
                ) : (
                  <button onClick={() => { setFilter('all'); setStatFilter(null); setQuery(''); }}
                    className={`px-4 py-2 text-sm font-medium rounded-xl border ${t('text-slate-600 border-slate-200 hover:bg-slate-50','text-slate-400 border-white/[0.08] hover:border-white/20')}`}>
                    Show everything
                  </button>
                )}
              </div>
            ) : (() => {
              // "Start here" — the three highest-priority items get their own
              // band so the screen opens with a decision, not a scroll. Only
              // on the unfiltered view; any filter means the user is already
              // navigating with intent.
              const unfiltered = filter === 'all' && !statFilter && !query;
              const ranked = [...filteredItems].sort((a, b) => (b.priority_score || 0) - (a.priority_score || 0));
              const startHere = unfiltered && filteredItems.length > 4 ? ranked.slice(0, 3) : [];
              const startIds = new Set(startHere.map(i => i.id));
              const rest = startHere.length ? filteredItems.filter(i => !startIds.has(i.id)) : filteredItems;

              // Then by time. Items with no due date sit by urgency, never hidden.
              const dayOf = (i) => i.due_date ? Math.ceil((new Date(i.due_date) - now) / 86400000) : null;
              const isUrgent = (i) => i.urgency_level === 'critical' || i.urgency_level === 'high';
              const bands = { overdue: [], today: [], week: [], fortnight: [], later: [] };
              for (const i of rest) {
                const d = dayOf(i);
                if (d === null) (isUrgent(i) ? bands.week : bands.fortnight).push(i);
                else if (d < 0) bands.overdue.push(i);
                else if (d === 0) bands.today.push(i);
                else if (d <= 7) bands.week.push(i);
                else if (d <= 14) bands.fortnight.push(i);
                else bands.later.push(i);
              }
              const listBox = t('bg-white border-slate-200/90','bg-white/[0.035] border-white/[0.07]');
              // Phase E: the agent's proposals sit under the item they are about; the rest get their own band.
              const stripsOf = (item) => { const k = objectKeyOfItem(item); const ps = k ? (ag.byObject.get(k) || []) : []; return ps.map(pp => <ProposalStrip key={pp.id} p={pp} t={t} brand={agentHome?.brand || null} onChanged={ag.refresh} onAddDocument={() => setActiveTab('ingest')} />); };
              const rowsOf = (list, hero = false) => list.map(item => <React.Fragment key={item.id}><TodayRow item={item} onSelect={setSelectedPriority} t={t} hero={hero} />{stripsOf(item)}</React.Fragment>);
              const listedKeys = new Set(filteredItems.map(objectKeyOfItem).filter(Boolean));
              const alsoNoticed = ag.proposals.filter(pp => !listedKeys.has(pp.object_key) && pp.status === 'proposed');
              const Band = ({ label, tone, note, list }) => list.length === 0 ? null : (
                <div className="mb-[22px]">
                  <div className="flex items-baseline gap-2.5 mb-2">
                    <h2 className={`text-[13px] font-bold tracking-[0.01em] ${tone || t('text-slate-800','text-slate-200')}`}>{label}</h2>
                    <span className={`text-[12px] ${t('text-slate-500','text-slate-500')}`}>{note || `${list.length} item${list.length === 1 ? '' : 's'}`}</span>
                  </div>
                  <div className={`rounded-[14px] overflow-hidden border ${listBox}`}>
                    {rowsOf(list)}
                  </div>
                </div>
              );
              return (
                <>
                  {startHere.length > 0 && (
                    <div className="mb-[22px]">
                      <div className="flex items-baseline gap-2.5 mb-2">
                        <h2 className={`text-[13px] font-bold tracking-[0.01em] ${t('text-slate-800','text-slate-200')}`}>Start here</h2>
                        <span className={`text-[12px] ${t('text-slate-500','text-slate-500')}`}>the three that matter most right now</span>
                      </div>
                      <div className={`rounded-[14px] overflow-hidden border ${listBox}`}>
                        {rowsOf(startHere, true)}
                      </div>
                    </div>
                  )}
                  <Band label="Overdue" tone={t('text-red-600','text-red-400')} list={bands.overdue} note={bands.overdue.length === 1 ? 'past its due date' : 'past their due date'} />
                  <Band label="Today" tone={t('text-amber-600','text-amber-400')} list={bands.today} />
                  <Band label="This week" list={bands.week} />
                  <Band label="Next two weeks" list={bands.fortnight} />
                  {agentHome && <AlsoNoticed proposals={alsoNoticed} t={t} brand={agentHome.brand || null} onChanged={ag.refresh} onAddDocument={() => setActiveTab('ingest')} />}
                  {bands.later.length > 0 && (
                    <div className="mb-[22px]">
                      <button onClick={() => setShowLater(v => !v)}
                        title={showLater ? 'Fold away work due later' : 'Show work due later'}
                        className={`w-full flex items-center justify-between rounded-[14px] border px-4 py-3 text-left transition ${t('bg-white border-slate-200/90 hover:border-slate-300','bg-white/[0.03] border-white/[0.06] hover:bg-white/[0.05]')}`}>
                        <span className="flex items-baseline gap-2.5">
                          <span className={`text-[13px] font-bold ${t('text-slate-800','text-slate-200')}`}>Later</span>
                          <span className={`text-[12px] ${t('text-slate-500','text-slate-500')}`}>{bands.later.length} item{bands.later.length === 1 ? '' : 's'} due after the next two weeks</span>
                        </span>
                        <span className={`text-[12px] font-semibold ${t('text-slate-500','text-slate-400')}`}>{showLater ? 'Hide' : 'Show'}</span>
                      </button>
                      {showLater && (
                        <div className={`rounded-[14px] overflow-hidden mt-3 border ${listBox}`}>
                          {rowsOf(bands.later)}
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
            })()}
          </>
        )}

        {selectedPriority && (() => {
          // The drawer shows the LIVE item; when an action removes it from
          // the queue, the drawer closes itself rather than showing stale work.
          const live = items.find(i => i.id === selectedPriority.id);
          if (!live) { setTimeout(() => setSelectedPriority(null), 0); return null; }
          return (
            <PriorityDrawer item={live} onClose={() => setSelectedPriority(null)}
              onAction={handleAction} onOpenApp={onSelectApp} onReclassified={loadPriorities} t={t} />
          );
        })()}

        {(activeTab === 'intake' || activeTab === 'ingest') && (
          // Stacked, never side by side: next to the calendar rail the two
          // halves were too narrow and the upload form ran under the routed cards.
          <div className="space-y-8">
            <IngestTab user={user} t={t} />
            <div className="max-w-2xl mx-auto">
              <IntakeInboxTab onOpenApp={onSelectApp} t={t} />
            </div>
          </div>
        )}
        {activeTab === 'agents' && <AgentDashboard t={t} />}
        </div>

        {/* ── Rail ── */}
        {/* The calendar zone: its own ground and a hairline, so it reads apart from the work list without leaving the page. */}
        <aside className={`mt-10 xl:mt-0 xl:self-stretch grid gap-3.5 content-start xl:pl-6 xl:border-l ${t('xl:border-slate-200', 'xl:border-white/[0.08]')}`} aria-label="Calendars and more">
          <HubCalendars onOpenApp={onSelectApp} />

          <div className={`relative rounded-[14px] border px-4 py-3.5 ${t('bg-white border-slate-200/90','bg-white/[0.035] border-white/[0.07]')}`}>
            <h3 className={`text-[12px] font-bold uppercase tracking-[0.02em] mb-1.5 ${t('text-slate-500','text-slate-400')}`}>Also here</h3>
            <div className="grid">
            <button onClick={openOpModal}
                title="Enter operational metrics (timeliness, call center, CTMs, SIU, notices) for a quarter"
                className={`flex items-center justify-between py-2 text-[13px] font-semibold text-left ${t('text-slate-900 hover:text-[#E5601F]','text-slate-100 hover:text-orange-400')}`}>
                Operational metrics<span className={`text-[12px] font-medium ${t('text-slate-400','text-slate-500')}`}>{(quarters.find(q => q.key === opQuarter) || quarters[0]).label.replace(' (current)', '')}</span>
              </button>
            {opModalOpen && createPortal((
              <div className={`fixed inset-0 z-[60] flex items-center justify-center p-4 ${t('bg-slate-900/30','bg-black/60')} backdrop-blur-[6px]`} onClick={() => setOpModalOpen(false)} role="dialog" aria-modal="true" aria-label="Operational metrics">
                <div onClick={e => e.stopPropagation()}
                  className={`w-full max-w-4xl h-[86vh] rounded-[22px] ring-1 flex flex-col overflow-hidden
                    ${t('bg-white/90 ring-black/[0.06] shadow-[0_32px_80px_-16px_rgba(15,23,42,0.4)]','bg-[#0d0f16]/95 ring-white/[0.08] shadow-[0_32px_80px_-16px_rgba(0,0,0,0.85)]')}`}
                  style={{ backdropFilter: 'blur(28px) saturate(160%)', WebkitBackdropFilter: 'blur(28px) saturate(160%)', animation: 'launchIn .22s cubic-bezier(.2,1.1,.3,1)' }}>
                  <div className={`flex items-center justify-between gap-3 px-6 py-4 border-b flex-shrink-0 ${t('border-slate-200/60', 'border-white/[0.06]')}`}>
                    <div>
                      <div className={`text-[15px] font-bold tracking-[-0.01em] ${t('text-slate-900', 'text-white')}`}>Operational metrics</div>
                      <div className={`text-[11px] mt-0.5 ${t('text-slate-400', 'text-slate-500')}`}>Timeliness, call center, CTMs, SIU and notices — feeds the committee deck</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <select value={opQuarter} onChange={e => { setOpQuarter(e.target.value); loadOpMetrics(e.target.value); }}
                        className={`text-xs font-semibold rounded-full border px-3 py-1.5 outline-none focus:ring-2 focus:ring-orange-500/40 ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`}>
                        {quarters.map(q => <option key={q.key} value={q.key}>{q.label}</option>)}
                      </select>
                      <button onClick={() => setOpModalOpen(false)} aria-label="Close"
                        className={`w-7 h-7 flex items-center justify-center rounded-full text-sm transition-colors ${t('bg-slate-100 hover:bg-slate-200 text-slate-500', 'bg-white/[0.06] hover:bg-white/[0.12] text-slate-300')}`}>✕</button>
                    </div>
                  </div>
                  <div className="flex-1 flex min-h-0">
                    {/* Section rail — iOS Settings navigation */}
                    <nav className={`w-[168px] flex-shrink-0 overflow-y-auto py-3 px-2 border-r ${t('bg-slate-50/60 border-slate-200/60', 'bg-white/[0.015] border-white/[0.05]')}`}>
                      {[['op-timeliness', 'Timeliness'], ['op-callcenter', 'Call center'], ['op-ctm', 'Complaints (CTM)'], ['op-hra', 'SNP HRAs'], ['op-icp', 'SNP Care Plans'], ['op-nonc', 'Notices'], ['op-siu', 'SIU'], ['op-slides', 'Custom slides']].map(([id, label]) => (
                        <button key={id} onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                          className={`w-full text-left px-3 py-[7px] rounded-[9px] text-[12px] font-medium transition-colors ${t('text-slate-500 hover:text-slate-900 hover:bg-white', 'text-slate-500 hover:text-slate-200 hover:bg-white/[0.05]')}`}>
                          {label}
                        </button>
                      ))}
                      <div className={`mx-2 my-2.5 h-px ${t('bg-slate-200','bg-white/[0.05]')}`} />
                      <div className={`px-3 space-y-1.5 text-[11px] ${t('text-slate-500','text-slate-400')}`}>
                        <div className="font-semibold">Add a quarter</div>
                        <input type="number" value={newQ.year} onChange={e => setNewQ(s => ({ ...s, year: e.target.value }))} placeholder="Year"
                          className={`w-full px-2 py-1 rounded-lg border text-xs outline-none focus:ring-2 focus:ring-orange-500/40 ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                        <select value={newQ.quarter} onChange={e => setNewQ(s => ({ ...s, quarter: Number(e.target.value) }))}
                          className={`w-full px-2 py-1 rounded-lg border text-xs outline-none ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`}>
                          {[1, 2, 3, 4].map(q => <option key={q} value={q}>Q{q}</option>)}
                        </select>
                        <button onClick={() => { const k = addQuarter(newQ.year, newQ.quarter); if (k) { setOpQuarter(k); loadOpMetrics(k); } }}
                          className="w-full px-2 py-1 rounded-lg font-semibold bg-orange-600 text-white hover:bg-orange-500 transition-colors text-xs">+ Add</button>
                      </div>
                    </nav>
                  <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {opLoading ? (
                      <div className="space-y-4">
                        {[1, 2, 3].map(i => (
                          <div key={i} className="animate-pulse space-y-2">
                            <div className={`h-3.5 rounded w-40 ${t('bg-slate-200','bg-white/[0.05]')}`} />
                            <div className={`h-24 rounded-xl ${t('bg-slate-100','bg-white/[0.03]')}`} />
                          </div>
                        ))}
                      </div>
                    ) : (<>
                      {/* Timeliness */}
                      <section id="op-timeliness" className={`scroll-mt-2 rounded-2xl p-4 ring-1 ${t('bg-white/70 ring-slate-200/60','bg-white/[0.02] ring-white/[0.05]')}`}>
                        <div className="flex items-baseline justify-between mb-2">
                          <h4 className={`text-xs font-semibold uppercase tracking-wide ${t('text-slate-700', 'text-slate-200')}`}>Timeliness (%) — by month</h4>
                          <span className={`text-[10px] ${t('text-slate-400', 'text-slate-500')}`}>compliant at 95% or above</span>
                        </div>
                        <div className={`grid grid-cols-[1.6fr_1fr_1fr_1fr] gap-2 mb-1 text-[10px] ${t('text-slate-400', 'text-slate-500')}`}>
                          <span></span>{ctmMonthLabels.map(m => <span key={m} className="text-center">{m}</span>)}
                        </div>
                        <div className="space-y-1">
                          {[['um', 'UM'], ['partCAppeals', 'Part C appeals'], ['partDAppeals', 'Part D appeals'], ['partCClaims', 'Part C claims'], ['partCGrievances', 'Part C grievances'], ['partDGrievances', 'Part D grievances']].map(([k, label]) => (
                            <div key={k} className="grid grid-cols-[1.6fr_1fr_1fr_1fr] gap-2 items-center">
                              <span className={`text-[10px] ${t('text-slate-500', 'text-slate-400')}`}>{label}</span>
                              {[0, 1, 2].map(i => (
                                <input key={i} type="number" min="0" max="100" value={opData.timeliness[k][i] ?? ''} onChange={e => setT(k, i, e.target.value)}
                                  className={`w-full px-2 py-1.5 rounded-lg border text-xs text-center outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-400 transition-shadow ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                              ))}
                            </div>
                          ))}
                        </div>
                      </section>
                      {/* Call center */}
                      <section id="op-callcenter" className={`scroll-mt-2 rounded-2xl p-4 ring-1 ${t('bg-white/70 ring-slate-200/60','bg-white/[0.02] ring-white/[0.05]')}`}>
                        <h4 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${t('text-slate-700', 'text-slate-200')}`}>Call center — by month</h4>
                        <div className={`grid grid-cols-[1.6fr_1fr_1fr_1fr] gap-2 mb-1 text-[10px] ${t('text-slate-400', 'text-slate-500')}`}>
                          <span></span>{ctmMonthLabels.map(m => <span key={m} className="text-center">{m}</span>)}
                        </div>
                        <div className="space-y-1">
                          {[['avgHoldSec', 'Avg hold time (sec) ≤120'], ['answered30Pct', '% answered in 30s ≥80'], ['disconnectPct', 'Disconnect rate % <5'], ['asaSec', 'Avg speed of answer (sec)'], ['ttyPct', 'TTY availability %']].map(([k, label]) => (
                            <div key={k} className="grid grid-cols-[1.6fr_1fr_1fr_1fr] gap-2 items-center">
                              <span className={`text-[10px] ${t('text-slate-500', 'text-slate-400')}`}>{label}</span>
                              {[0, 1, 2].map(i => (
                                <input key={i} type="number" min="0" step="0.1" value={opData.callCenter[k][i] ?? ''} onChange={e => setCC(k, i, e.target.value)}
                                  className={`w-full px-2 py-1.5 rounded-lg border text-xs text-center outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-400 transition-shadow ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                              ))}
                            </div>
                          ))}
                        </div>
                      </section>
                      {/* CTM */}
                      <section id="op-ctm" className={`scroll-mt-2 rounded-2xl p-4 ring-1 ${t('bg-white/70 ring-slate-200/60','bg-white/[0.02] ring-white/[0.05]')}`}>
                        <div className="flex items-baseline justify-between mb-2">
                          <h4 className={`text-xs font-semibold uppercase tracking-wide ${t('text-slate-700', 'text-slate-200')}`}>Complaints (CTM) — by month</h4>
                          <span className={`text-[10px] ${t('text-slate-400', 'text-slate-500')}`}>volume + % timely</span>
                        </div>
                        <div className={`grid grid-cols-[1.6fr_1fr_1fr_1fr] gap-2 mb-1 text-[10px] ${t('text-slate-400', 'text-slate-500')}`}>
                          <span></span>{ctmMonthLabels.map(m => <span key={m} className="text-center">{m}</span>)}
                        </div>
                        <div className="space-y-1">
                          {[['volume', 'CTM volume'], ['timelyPct', '% processed timely']].map(([k, label]) => (
                            <div key={k} className="grid grid-cols-[1.6fr_1fr_1fr_1fr] gap-2 items-center">
                              <span className={`text-[10px] ${t('text-slate-500', 'text-slate-400')}`}>{label}</span>
                              {[0, 1, 2].map(i => (
                                <input key={i} type="number" min="0" value={opData.ctm[k][i] ?? ''} onChange={e => setCtm(k, i, e.target.value)}
                                  className={`w-full px-2 py-1.5 rounded-lg border text-xs text-center outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-400 transition-shadow ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                              ))}
                            </div>
                          ))}
                        </div>
                      </section>
                      {/* Case management — SNP HRAs */}
                      <section id="op-hra" className={`scroll-mt-2 rounded-2xl p-4 ring-1 ${t('bg-white/70 ring-slate-200/60','bg-white/[0.02] ring-white/[0.05]')}`}>
                        <div className="flex items-baseline justify-between mb-2">
                          <h4 className={`text-xs font-semibold uppercase tracking-wide ${t('text-slate-700', 'text-slate-200')}`}>Case management — SNP HRAs (by month)</h4>
                          <span className={`text-[10px] ${t('text-slate-400', 'text-slate-500')}`}>timeliness %, internal goal 95%</span>
                        </div>
                        <div className={`grid grid-cols-[1.6fr_1fr_1fr_1fr] gap-2 mb-1 text-[10px] ${t('text-slate-400', 'text-slate-500')}`}>
                          <span></span>{ctmMonthLabels.map(m => <span key={m} className="text-center">{m}</span>)}
                        </div>
                        <div className="space-y-1">
                          {[['initialHra', 'Initial HRA ≤90d of enrollment'], ['annualHra', 'Annual HRA ≤365d of prior HRA']].map(([k, label]) => (
                            <div key={k} className="grid grid-cols-[1.6fr_1fr_1fr_1fr] gap-2 items-center">
                              <span className={`text-[10px] ${t('text-slate-500', 'text-slate-400')}`}>{label}</span>
                              {[0, 1, 2].map(i => (
                                <input key={i} type="number" min="0" max="100" value={opData.caseManagement[k][i] ?? ''} onChange={e => setCM(k, i, e.target.value)}
                                  className={`w-full px-2 py-1.5 rounded-lg border text-xs text-center outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-400 transition-shadow ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                              ))}
                            </div>
                          ))}
                        </div>
                      </section>
                      {/* Case management — SNP Individualized Care Plans (ICPs) */}
                      <section id="op-icp" className={`scroll-mt-2 rounded-2xl p-4 ring-1 ${t('bg-white/70 ring-slate-200/60','bg-white/[0.02] ring-white/[0.05]')}`}>
                        <div className="flex items-baseline justify-between mb-2">
                          <h4 className={`text-xs font-semibold uppercase tracking-wide ${t('text-slate-700', 'text-slate-200')}`}>Case management — SNP Individualized Care Plans (by month)</h4>
                          <span className={`text-[10px] ${t('text-slate-400', 'text-slate-500')}`}>compliance %, internal goal 95%</span>
                        </div>
                        <div className={`grid grid-cols-[1.6fr_1fr_1fr_1fr] gap-2 mb-1 text-[10px] ${t('text-slate-400', 'text-slate-500')}`}>
                          <span></span>{ctmMonthLabels.map(m => <span key={m} className="text-center">{m}</span>)}
                        </div>
                        <div className="space-y-1">
                          {[['initialIcp', 'Initial ICP ≤90d of HRA'], ['annualIcp', 'Annual ICP update ≤365d of prior ICP']].map(([k, label]) => (
                            <div key={k} className="grid grid-cols-[1.6fr_1fr_1fr_1fr] gap-2 items-center">
                              <span className={`text-[10px] ${t('text-slate-500', 'text-slate-400')}`}>{label}</span>
                              {[0, 1, 2].map(i => (
                                <input key={i} type="number" min="0" max="100" value={(opData.icp?.[k] || ['', '', ''])[i] ?? ''} onChange={e => setICP(k, i, e.target.value)}
                                  className={`w-full px-2 py-1.5 rounded-lg border text-xs text-center outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-400 transition-shadow ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                              ))}
                            </div>
                          ))}
                        </div>
                      </section>
                      {/* Notices of non-compliance */}
                      <section id="op-nonc" className={`scroll-mt-2 rounded-2xl p-4 ring-1 ${t('bg-white/70 ring-slate-200/60','bg-white/[0.02] ring-white/[0.05]')}`}>
                        <div className="flex items-baseline justify-between mb-2">
                          <h4 className={`text-xs font-semibold uppercase tracking-wide ${t('text-slate-700', 'text-slate-200')}`}>Notices of non-compliance</h4>
                          <button onClick={addNonc} className={`text-[11px] px-2 py-0.5 rounded ${t('bg-slate-100 text-slate-600 hover:bg-slate-200', 'bg-white/10 text-slate-200 hover:bg-white/20')}`}>+ Add</button>
                        </div>
                        {opData.nonc.length === 0 && <div className={`text-[11px] italic ${t('text-slate-400', 'text-slate-500')}`}>None entered.</div>}
                        <div className="space-y-1.5">
                          {opData.nonc.map((n, i) => (
                            <div key={i} className="flex items-center gap-1.5">
                              <input placeholder="Notification" value={n.notification} onChange={e => setNonc(i, 'notification', e.target.value)}
                                className={`flex-1 px-2 py-1 rounded border text-xs ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                              <input type="date" value={n.dateReceived} onChange={e => setNonc(i, 'dateReceived', e.target.value)}
                                className={`px-2 py-1 rounded border text-xs ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                              <input placeholder="Topic" value={n.topic} onChange={e => setNonc(i, 'topic', e.target.value)}
                                className={`flex-1 px-2 py-1 rounded border text-xs ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                              <button onClick={() => delNonc(i)} className={`text-xs px-1.5 py-1 rounded ${t('text-slate-400 hover:bg-slate-100', 'text-slate-400 hover:bg-white/10')}`}>✕</button>
                            </div>
                          ))}
                        </div>
                      </section>
                      {/* SIU */}
                      <section id="op-siu" className={`scroll-mt-2 rounded-2xl p-4 ring-1 ${t('bg-white/70 ring-slate-200/60','bg-white/[0.02] ring-white/[0.05]')}`}>
                        <h4 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${t('text-slate-700', 'text-slate-200')}`}>SIU</h4>
                        <div className="grid grid-cols-3 gap-2 mb-2">
                          {[['total', 'Total cases'], ['opened', 'Opened'], ['rejected', 'Rejected']].map(([k, label]) => (
                            <label key={k} className="block">
                              <span className={`block text-[10px] ${t('text-slate-500', 'text-slate-400')}`}>{label}</span>
                              <input type="number" min="0" value={opData.siu[k]} onChange={e => setSiuField(k, e.target.value)}
                                className={`w-full mt-0.5 px-2 py-1 rounded border text-xs ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                            </label>
                          ))}
                        </div>
                        <div className="flex items-baseline justify-between mb-1.5">
                          <span className={`text-[11px] font-medium ${t('text-slate-600', 'text-slate-300')}`}>Cases</span>
                          <button onClick={addCase} className={`text-[11px] px-2 py-0.5 rounded ${t('bg-slate-100 text-slate-600 hover:bg-slate-200', 'bg-white/10 text-slate-200 hover:bg-white/20')}`}>+ Add case</button>
                        </div>
                        {opData.siu.cases.length === 0 && <div className={`text-[11px] italic ${t('text-slate-400', 'text-slate-500')}`}>No cases entered.</div>}
                        <div className="space-y-2">
                          {opData.siu.cases.map((c, i) => (
                            <div key={i} className={`rounded-lg border p-2 space-y-1.5 ${t('border-slate-200 bg-slate-50/50', 'border-white/10 bg-white/[0.03]')}`}>
                              <div className="flex items-center gap-1.5">
                                <input placeholder="Case" value={c.label} onChange={e => setCase(i, 'label', e.target.value)}
                                  className={`flex-1 px-2 py-1 rounded border text-xs ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                                <input type="date" value={c.dateReceived || ''} onChange={e => setCase(i, 'dateReceived', e.target.value)} title="Date received"
                                  className={`px-2 py-1 rounded border text-xs ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                                <input placeholder="Status" value={c.status} onChange={e => setCase(i, 'status', e.target.value)}
                                  className={`w-28 px-2 py-1 rounded border text-xs ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                                <label className={`flex items-center gap-1 text-[10px] whitespace-nowrap ${t('text-slate-600', 'text-slate-300')}`}>
                                  <input type="checkbox" checked={!!c.imedic} onChange={e => setCase(i, 'imedic', e.target.checked)} className="accent-orange-600" />
                                  I-MEDIC
                                </label>
                                <button onClick={() => delCase(i)} className={`text-xs px-1.5 py-1 rounded ${t('text-slate-400 hover:bg-slate-100', 'text-slate-400 hover:bg-white/10')}`}>✕</button>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <input placeholder="Description" value={c.description || ''} onChange={e => setCase(i, 'description', e.target.value)}
                                  className={`flex-1 px-2 py-1 rounded border text-xs ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                                <input placeholder="Disposition" value={c.disposition || ''} onChange={e => setCase(i, 'disposition', e.target.value)}
                                  className={`flex-1 px-2 py-1 rounded border text-xs ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                              </div>
                              <input placeholder="Brief summary" value={c.summary || ''} onChange={e => setCase(i, 'summary', e.target.value)}
                                className={`w-full px-2 py-1 rounded border text-xs ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                            </div>
                          ))}
                        </div>
                      </section>
                      {/* Custom user-authored slides: content blocks + charts */}
                      <section id="op-slides" className={`scroll-mt-2 rounded-2xl p-4 ring-1 ${t('bg-white/70 ring-slate-200/60','bg-white/[0.02] ring-white/[0.05]')}`}>
                        <div className="flex items-baseline justify-between mb-2">
                          <h4 className={`text-xs font-semibold uppercase tracking-wide ${t('text-slate-700', 'text-slate-200')}`}>Custom slides — content &amp; charts (optional)</h4>
                          <button onClick={addUserSlide} className="text-[11px] px-2 py-0.5 rounded font-semibold bg-orange-600 text-white hover:bg-orange-700">+ Add slide</button>
                        </div>
                        {(opData.adhocSlides || []).length === 0 && (
                          <div className={`text-[10px] mb-2 ${t('text-slate-400', 'text-slate-500')}`}>Build your own slides with headings, text, bullet lists, and charts. Charts auto-pick the best type from your data; you can override per chart.</div>
                        )}
                        <div className="space-y-3">
                          {(opData.adhocSlides || []).map((sl) => (
                            <div key={sl.id} className={`rounded-lg border p-2.5 ${t('bg-slate-50 border-slate-200', 'bg-white/[0.03] border-white/10')}`}>
                              <div className="flex gap-2 mb-2">
                                <input placeholder="Slide title" value={sl.title} onChange={e => setUserSlideTitle(sl.id, e.target.value)}
                                  className={`flex-1 px-2 py-1 rounded border text-xs font-semibold ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                                <button onClick={() => delUserSlide(sl.id)} className="text-[11px] px-2 py-0.5 rounded text-orange-600 hover:bg-orange-50">Remove slide</button>
                              </div>
                              <div className="space-y-2">
                                {(sl.blocks || []).map((b) => (
                                  <div key={b.id} className={`rounded border p-2 ${t('bg-white border-slate-200', 'bg-white/[0.04] border-white/10')}`}>
                                    <div className="flex items-center justify-between mb-1.5">
                                      <span className={`text-[10px] font-bold uppercase tracking-wide ${t('text-slate-500', 'text-slate-400')}`}>{BLOCK_LABEL[b.type] || b.type}</span>
                                      <div className="flex items-center gap-1 text-[11px]">
                                        <button onClick={() => moveBlock(sl.id, b.id, -1)} title="Move up" className={`px-1 rounded ${t('hover:bg-slate-100', 'hover:bg-white/10')}`}>↑</button>
                                        <button onClick={() => moveBlock(sl.id, b.id, 1)} title="Move down" className={`px-1 rounded ${t('hover:bg-slate-100', 'hover:bg-white/10')}`}>↓</button>
                                        <button onClick={() => delBlock(sl.id, b.id)} title="Delete block" className="px-1 rounded text-orange-600 hover:bg-orange-50">✕</button>
                                      </div>
                                    </div>
                                    {b.type === 'heading' && (
                                      <input placeholder="Heading text" value={b.text} onChange={e => setBlock(sl.id, b.id, { text: e.target.value })}
                                        className={`w-full px-2 py-1 rounded border text-xs ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                                    )}
                                    {b.type === 'text' && (
                                      <div className="space-y-1">
                                        <textarea rows={3} placeholder="Paragraph text. One paragraph per line." value={b.text} onChange={e => setBlock(sl.id, b.id, { text: e.target.value })}
                                          className={`w-full px-2 py-1 rounded border text-xs ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                                        <div className="flex items-center justify-between">
                                          <span className={`text-[10px] ${t('text-slate-400', 'text-slate-500')}`}>AI refines wording only; it will not invent facts.</span>
                                          <button onClick={() => enhanceBlock(sl.id, b.id, b.text, sl.title)} disabled={enhancingId === b.id || !(b.text || '').trim()}
                                            className="text-[11px] px-2 py-0.5 rounded font-semibold bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50">
                                            {enhancingId === b.id ? 'Enhancing…' : '✨ Enhance with AI'}
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                    {b.type === 'bullets' && (
                                      <div className="space-y-1">
                                        {(b.items || []).map((it, ii) => (
                                          <div key={ii} className="flex gap-1">
                                            <input value={it} placeholder={`Bullet ${ii + 1}`} onChange={e => setBullet(sl.id, b.id, ii, e.target.value)}
                                              className={`flex-1 px-2 py-1 rounded border text-xs ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                                            <button onClick={() => delBullet(sl.id, b.id, ii)} className="text-[11px] px-1.5 rounded text-orange-600 hover:bg-orange-50">✕</button>
                                          </div>
                                        ))}
                                        <button onClick={() => addBullet(sl.id, b.id)} className={`text-[11px] px-2 py-0.5 rounded border ${t('border-slate-200 text-slate-600 hover:bg-slate-50', 'border-white/10 text-slate-300 hover:bg-white/10')}`}>+ bullet</button>
                                      </div>
                                    )}
                                    {b.type === 'table' && (
                                      <div className="space-y-1.5">
                                        <div className="overflow-x-auto">
                                          <table className="text-[11px] border-collapse">
                                            <thead>
                                              <tr>
                                                {(b.header || []).map((h, ci) => (
                                                  <th key={ci} className="p-0.5">
                                                    <input value={h} placeholder={`Column ${ci + 1}`} onChange={e => setTableHeader(sl.id, b.id, ci, e.target.value)}
                                                      onPaste={e => { const txt = e.clipboardData.getData('text'); if (isMultiCell(txt)) { e.preventDefault(); pasteTable(sl.id, b.id, -1, ci, txt); } }}
                                                      className={`w-28 px-1 py-0.5 rounded border font-semibold ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                                                  </th>
                                                ))}
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {(b.rows || []).map((r, ri) => (
                                                <tr key={ri}>
                                                  {r.map((c, ci) => (
                                                    <td key={ci} className="p-0.5">
                                                      <input value={c} onChange={e => setTableCell(sl.id, b.id, ri, ci, e.target.value)}
                                                        onPaste={e => { const txt = e.clipboardData.getData('text'); if (isMultiCell(txt)) { e.preventDefault(); pasteTable(sl.id, b.id, ri, ci, txt); } }}
                                                        className={`w-28 px-1 py-0.5 rounded border ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                                                    </td>
                                                  ))}
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                        <div className="flex flex-wrap gap-1.5 text-[11px]">
                                          <button onClick={() => addTableCol(sl.id, b.id)} className={`px-2 py-0.5 rounded border ${t('border-slate-200 text-slate-600 hover:bg-slate-50', 'border-white/10 text-slate-300 hover:bg-white/10')}`}>+ column</button>
                                          <button onClick={() => delTableCol(sl.id, b.id)} className={`px-2 py-0.5 rounded border ${t('border-slate-200 text-slate-600 hover:bg-slate-50', 'border-white/10 text-slate-300 hover:bg-white/10')}`}>− column</button>
                                          <button onClick={() => addTableRow(sl.id, b.id)} className={`px-2 py-0.5 rounded border ${t('border-slate-200 text-slate-600 hover:bg-slate-50', 'border-white/10 text-slate-300 hover:bg-white/10')}`}>+ row</button>
                                          <button onClick={() => delTableRow(sl.id, b.id)} className={`px-2 py-0.5 rounded border ${t('border-slate-200 text-slate-600 hover:bg-slate-50', 'border-white/10 text-slate-300 hover:bg-white/10')}`}>− row</button>
                                          <span className={`px-1 py-0.5 italic ${t('text-slate-400', 'text-slate-500')}`}>Tip: copy a block of cells from Excel and paste into any cell — it fills across.</span>
                                        </div>
                                      </div>
                                    )}
                                    {b.type === 'chart' && (
                                      <div className="space-y-1.5">
                                        <input placeholder="Chart title" value={b.title} onChange={e => setBlock(sl.id, b.id, { title: e.target.value })}
                                          className={`w-full px-2 py-1 rounded border text-xs ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                                        <div className="flex items-center gap-3 text-[11px] flex-wrap">
                                          <label className={`flex items-center gap-1 ${t('text-slate-600', 'text-slate-300')}`}>Type
                                            <select value={b.chartType} onChange={e => setBlock(sl.id, b.id, { chartType: e.target.value })}
                                              className={`px-1.5 py-0.5 rounded border ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`}>
                                              <option value="auto">Auto (recommended)</option>
                                              <option value="bar">Column / Bar</option>
                                              <option value="line">Line</option>
                                              <option value="area">Area</option>
                                              <option value="pie">Pie</option>
                                              <option value="doughnut">Doughnut</option>
                                            </select>
                                          </label>
                                          <label className={`flex items-center gap-1 ${t('text-slate-600', 'text-slate-300')}`}>
                                            <input type="checkbox" checked={!!b.time} onChange={e => setBlock(sl.id, b.id, { time: e.target.checked })} /> Time / ordered axis
                                          </label>
                                        </div>
                                        {/* Pass 1 controls: stacking, combo, target line */}
                                        {(() => {
                                          const eff = effectiveChartType(b);
                                          const isBar = eff === 'bar';
                                          const isPieish = eff === 'pie' || eff === 'doughnut';
                                          const canCombo = (b.series || []).length > 1 && !isPieish;
                                          return (
                                            <div className="flex items-center gap-3 text-[11px] flex-wrap">
                                              {isBar && (
                                                <label className={`flex items-center gap-1 ${t('text-slate-600', 'text-slate-300')}`}>Stack
                                                  <select value={b.stack || 'none'} onChange={e => setBlock(sl.id, b.id, { stack: e.target.value })}
                                                    className={`px-1.5 py-0.5 rounded border ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`}>
                                                    <option value="none">None</option>
                                                    <option value="stacked">Stacked</option>
                                                    <option value="percent">100% stacked</option>
                                                  </select>
                                                </label>
                                              )}
                                              {canCombo && (
                                                <label className={`flex items-center gap-1 ${t('text-slate-600', 'text-slate-300')}`}>
                                                  <input type="checkbox" checked={!!b.combo} onChange={e => setBlock(sl.id, b.id, { combo: e.target.checked })} /> Combo (bars + line, 2nd axis)
                                                </label>
                                              )}
                                              {!isPieish && (
                                                <label className={`flex items-center gap-1 ${t('text-slate-600', 'text-slate-300')}`}>Target line
                                                  <input type="number" inputMode="decimal" value={b.targetLine ?? ''} onChange={e => setBlock(sl.id, b.id, { targetLine: e.target.value })} placeholder="e.g. 95"
                                                    className={`w-16 px-1.5 py-0.5 rounded border ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                                                </label>
                                              )}
                                            </div>
                                          );
                                        })()}
                                        {/* Combo: choose which series render as the line */}
                                        {b.combo && (b.series || []).length > 1 && !['pie', 'doughnut'].includes(effectiveChartType(b)) && (
                                          <div className={`flex items-center gap-2 flex-wrap text-[11px] ${t('text-slate-500', 'text-slate-400')}`}>
                                            <span>Line series:</span>
                                            {b.series.map((se, si) => (
                                              <label key={si} className={`flex items-center gap-1 px-1.5 py-0.5 rounded border cursor-pointer ${(b.lineSeries || []).includes(si) ? t('bg-orange-50 border-orange-300 text-orange-700', 'bg-orange-500/10 border-orange-500/30 text-orange-300') : t('border-slate-200 text-slate-600', 'border-white/10 text-slate-300')}`}>
                                                <input type="checkbox" className="sr-only" checked={(b.lineSeries || []).includes(si)} onChange={() => toggleLineSeries(sl.id, b.id, si)} />
                                                {se.name || `Series ${si + 1}`}
                                              </label>
                                            ))}
                                          </div>
                                        )}
                                        {/* Colors — per series for bar/line/area/combo, per category for pie/doughnut */}
                                        {(() => {
                                          const eff = effectiveChartType(b);
                                          const isPieish = eff === 'pie' || eff === 'doughnut';
                                          const entries = isPieish
                                            ? (b.labels || []).map((l, i) => ({ i, name: l || `Category ${i + 1}` }))
                                            : (b.series || []).map((se, i) => ({ i, name: se.name || `Series ${i + 1}` }));
                                          if (!entries.length) return null;
                                          const swatch = (i) => { const v = (b.colors || [])[i]; return (typeof v === 'string' && /^#?[0-9A-Fa-f]{6}$/.test(v.trim())) ? (v.trim().startsWith('#') ? v.trim() : `#${v.trim()}`) : ''; };
                                          return (
                                            <div className="flex items-center gap-2 flex-wrap">
                                              <span className={`text-[11px] ${t('text-slate-500', 'text-slate-400')}`}>Colors ({isPieish ? 'by category' : 'by series'}):</span>
                                              {entries.map(({ i, name }) => (
                                                <span key={i} className="inline-flex items-center gap-1" title={name}>
                                                  <input type="color" value={swatch(i) || '#F58220'} onChange={e => setChartColor(sl.id, b.id, i, e.target.value.replace(/^#/, '').toUpperCase())}
                                                    className="w-6 h-6 rounded cursor-pointer border-0 bg-transparent p-0" aria-label={`Color for ${name}`} />
                                                  {swatch(i) && <button onClick={() => clearChartColor(sl.id, b.id, i)} title="Reset to default palette" className={`text-[10px] ${t('text-slate-400 hover:text-slate-600', 'text-slate-500 hover:text-slate-300')}`}>✕</button>}
                                                </span>
                                              ))}
                                              <span className={`text-[10px] italic ${t('text-slate-400', 'text-slate-500')}`}>blank = palette default</span>
                                            </div>
                                          );
                                        })()}
                                        <div className="overflow-x-auto">
                                          <table className="text-[11px] border-collapse">
                                            <thead>
                                              <tr>
                                                <th className={`p-0.5 text-left font-normal ${t('text-slate-400', 'text-slate-500')}`}>series \ category</th>
                                                {b.labels.map((l, i) => (
                                                  <th key={i} className="p-0.5">
                                                    <input value={l} placeholder={`Cat ${i + 1}`} onChange={e => setLabel(sl.id, b.id, i, e.target.value)}
                                                      onPaste={e => { const txt = e.clipboardData.getData('text'); if (isMultiCell(txt)) { e.preventDefault(); pasteChart(sl.id, b.id, 0, i, txt, 'labels'); } }}
                                                      className={`w-16 px-1 py-0.5 rounded border ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                                                  </th>
                                                ))}
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {b.series.map((se, si) => (
                                                <tr key={si}>
                                                  <td className="p-0.5">
                                                    <input value={se.name} placeholder={`Series ${si + 1}`} onChange={e => setSeriesName(sl.id, b.id, si, e.target.value)}
                                                      onPaste={e => { const txt = e.clipboardData.getData('text'); if (isMultiCell(txt)) { e.preventDefault(); pasteChart(sl.id, b.id, si, 0, txt, 'names'); } }}
                                                      className={`w-24 px-1 py-0.5 rounded border ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                                                  </td>
                                                  {se.values.map((v, i) => (
                                                    <td key={i} className="p-0.5">
                                                      <input value={v} inputMode="decimal" onChange={e => setSeriesVal(sl.id, b.id, si, i, e.target.value)}
                                                        onPaste={e => { const txt = e.clipboardData.getData('text'); if (isMultiCell(txt)) { e.preventDefault(); pasteChart(sl.id, b.id, si, i, txt, 'values'); } }}
                                                        className={`w-16 px-1 py-0.5 rounded border ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                                                    </td>
                                                  ))}
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                        <div className="flex flex-wrap gap-1.5 text-[11px]">
                                          <button onClick={() => addCol(sl.id, b.id)} className={`px-2 py-0.5 rounded border ${t('border-slate-200 text-slate-600 hover:bg-slate-50', 'border-white/10 text-slate-300 hover:bg-white/10')}`}>+ category</button>
                                          <button onClick={() => delCol(sl.id, b.id)} className={`px-2 py-0.5 rounded border ${t('border-slate-200 text-slate-600 hover:bg-slate-50', 'border-white/10 text-slate-300 hover:bg-white/10')}`}>− category</button>
                                          <button onClick={() => addSeries(sl.id, b.id)} className={`px-2 py-0.5 rounded border ${t('border-slate-200 text-slate-600 hover:bg-slate-50', 'border-white/10 text-slate-300 hover:bg-white/10')}`}>+ series</button>
                                          <button onClick={() => delSeries(sl.id, b.id)} className={`px-2 py-0.5 rounded border ${t('border-slate-200 text-slate-600 hover:bg-slate-50', 'border-white/10 text-slate-300 hover:bg-white/10')}`}>− series</button>
                                          <span className={`px-1 py-0.5 italic ${t('text-slate-400', 'text-slate-500')}`}>Tip: paste values, categories, or series from Excel — they spread across the grid.</span>
                                        </div>
                                        {/* Pass 2 formatting */}
                                        {(() => {
                                          const eff = effectiveChartType(b);
                                          const isPieish = eff === 'pie' || eff === 'doughnut';
                                          return (
                                            <div className="flex items-center gap-3 text-[11px] flex-wrap">
                                              <label className={`flex items-center gap-1 ${t('text-slate-600', 'text-slate-300')}`}>Labels
                                                <select value={b.labelFormat || 'auto'} onChange={e => setBlock(sl.id, b.id, { labelFormat: e.target.value })}
                                                  className={`px-1.5 py-0.5 rounded border ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`}>
                                                  <option value="auto">Auto</option><option value="none">Hide</option><option value="number">Number</option>
                                                  <option value="percent">Percent</option><option value="currency">Currency</option><option value="thousands">Thousands</option>
                                                </select>
                                              </label>
                                              <label className={`flex items-center gap-1 ${t('text-slate-600', 'text-slate-300')}`}>Legend
                                                <select value={b.legendPos || 'auto'} onChange={e => setBlock(sl.id, b.id, { legendPos: e.target.value })}
                                                  className={`px-1.5 py-0.5 rounded border ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`}>
                                                  <option value="auto">Auto</option><option value="b">Bottom</option><option value="r">Right</option><option value="none">Hide</option>
                                                </select>
                                              </label>
                                              {b.chartType === 'bar' && (
                                                <label className={`flex items-center gap-1 ${t('text-slate-600', 'text-slate-300')}`}>Orient
                                                  <select value={b.orientation || 'auto'} onChange={e => setBlock(sl.id, b.id, { orientation: e.target.value })}
                                                    className={`px-1.5 py-0.5 rounded border ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`}>
                                                    <option value="auto">Auto</option><option value="col">Vertical</option><option value="bar">Horizontal</option>
                                                  </select>
                                                </label>
                                              )}
                                              {!isPieish && (
                                                <label className={`flex items-center gap-1 ${t('text-slate-600', 'text-slate-300')}`}>
                                                  <input type="checkbox" checked={!!b.valAxis} onChange={e => setBlock(sl.id, b.id, { valAxis: e.target.checked })} /> Show axis
                                                </label>
                                              )}
                                              {!isPieish && b.valAxis && (
                                                <span className="inline-flex items-center gap-1">
                                                  <input type="number" inputMode="decimal" value={b.valMin ?? ''} onChange={e => setBlock(sl.id, b.id, { valMin: e.target.value })} placeholder="min"
                                                    className={`w-12 px-1.5 py-0.5 rounded border ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                                                  <input type="number" inputMode="decimal" value={b.valMax ?? ''} onChange={e => setBlock(sl.id, b.id, { valMax: e.target.value })} placeholder="max"
                                                    className={`w-12 px-1.5 py-0.5 rounded border ${t('bg-white border-slate-200 text-slate-700', 'bg-white/[0.06] border-white/10 text-slate-200')}`} />
                                                </span>
                                              )}
                                            </div>
                                          );
                                        })()}
                                        {/* Live preview — mirrors what the PowerPoint will render */}
                                        <div className={`rounded-xl p-2 mt-1 ${t('bg-slate-50 ring-1 ring-slate-200/70', 'bg-white/[0.03] ring-1 ring-white/[0.06]')}`}>
                                          <div className={`text-[9px] font-bold uppercase tracking-wide mb-1 px-1 ${t('text-slate-400', 'text-slate-500')}`}>Live preview</div>
                                          <ChartPreview block={b} effType={effectiveChartType(b)} dark={theme === 'dark'} />
                                        </div>
                                        <div className={`text-[10px] ${t('text-slate-400', 'text-slate-500')}`}>Leave a cell blank for a gap. Auto picks the best chart from your data; override with Type.</div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                                <span className={`text-[10px] ${t('text-slate-400', 'text-slate-500')}`}>Add block:</span>
                                {['heading', 'text', 'bullets', 'table', 'chart'].map(tp => (
                                  <button key={tp} onClick={() => addBlock(sl.id, tp)} className={`text-[11px] px-2 py-0.5 rounded border ${t('border-slate-200 text-slate-600 hover:bg-slate-50', 'border-white/10 text-slate-300 hover:bg-white/10')}`}>{BLOCK_LABEL[tp]}</button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </section>
                    </>)}
                  </div>
                  </div>
                  <div className={`flex items-center justify-between gap-3 px-6 py-3.5 border-t flex-shrink-0 ${t('border-slate-200/60', 'border-white/[0.06]')}`}>
                    <div className={`text-[11px] ${opSaved ? 'text-emerald-500 font-semibold' : t('text-slate-400', 'text-slate-500')}`}>{opSaved ? 'Saved for this quarter' : 'Saved per quarter, then included when you build the deck'}</div>
                    <button onClick={saveOpMetrics} disabled={opSaving}
                      className="text-xs px-5 py-2 rounded-full font-semibold bg-orange-600 text-white hover:bg-orange-500 active:scale-[0.98] transition-all disabled:opacity-40 shadow-[0_4px_14px_-4px_rgba(234,88,12,0.5)]">
                      {opSaving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            ), document.body)}
              <button onClick={() => setActiveTab('intake')}
                className={`flex items-center justify-between py-2 text-[13px] font-semibold text-left border-t ${t('text-slate-900 border-slate-200/80 hover:text-[#E5601F]','text-slate-100 border-white/[0.06] hover:text-orange-400')}`}>
                All documents<span className={`text-[12px] font-medium ${t('text-slate-400','text-slate-500')}`}>Upload and review</span>
              </button>
            </div>
          </div>
        </aside>
        </div>
      </div>
    </div>
  );
}
